import React, { useEffect, useMemo, useRef, useState } from 'react';
import ApplicationDetailsModal from '../components/JobApplication/ApplicationDetailsModal';
import ApplicationViewerModal from '../components/JobApplication/ApplicationViewerModal';
import AcceptedApplicationsList from '../components/JobApplication/AcceptedApplicationsList';
import ApplicationStatusActions from '../components/JobApplication/ApplicationStatusActions';
import CompanyAcceptedApplications from '../components/JobApplication/CompanyAcceptedApplications';
import ApplicationFilters from '../components/JobApplication/ApplicationFilters';
import ApplicationPagination from '../components/JobApplication/ApplicationPagination';
import {
  Box,
  Container,
  Typography,
  Tabs,
  Tab,
  Card,
  CardContent,
  Avatar,
  Button,
  Chip,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  useTheme,
  useMediaQuery,
  CircularProgress,
  Alert,
  Skeleton,
  Switch,
  FormControlLabel,
  Grid,
  InputAdornment,
  LinearProgress,
} from '@mui/material';
import { Rating, TextField } from '@mui/material';
import {
  ShoppingBag as OrdersIcon,
  Favorite as FavoritesIcon,
  Add as UploadIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  Store as MyProductsIcon,
  MoreVert as MoreIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Message as MessageIcon,
  Person as PersonIcon,
  Settings as SettingsIcon,
  SupportAgent as SupportAgentIcon,
  AccessTime as AccessTimeIcon,
  ShoppingCart as ShoppingCartIcon,
  CheckCircle as CheckIcon,
  Close as CloseIcon,
  FilterList as FilterListIcon,
  Storefront as StorefrontIcon,
  Inbox as InboxIcon,
  Business as BusinessIcon,
  Description as DocumentsIcon,
  People as PeopleIcon,
  LocationOn as LocationIcon,
  Schedule as ScheduleIcon,
  Event as EventIcon,
  Work as WorkIcon,
  Gavel as GavelIcon,
  Send as SendIcon,
  HourglassEmpty as PendingIcon,
  Cancel as CancelIcon,
  Chat as ChatIcon,
  ArrowDownward as ArrowDownwardIcon,
  ArrowUpward as ArrowUpwardIcon,
} from '@mui/icons-material';
import VerifiedIcon from '@mui/icons-material/Verified';
import { Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import { motion } from 'framer-motion';
import { useAuthStore } from '../store/authStore';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAppMode } from '../contexts/AppModeContext';
import { useTranslation } from '../contexts/TranslationContext';
import { useUIStore } from '../store/uiStore';
import { UserProductService, UserProduct } from '../services/userProductService';
import { OffersService, Offer } from '../services/offersService';
import { ApplicationService } from '../services/applicationService';
import { PurchasesService, Purchase, PaidStatus } from '../services/purchasesService';
import OfferPopup from '../components/ChatWindow/OfferPopup';
import { supabase } from '../lib/supabase';
import { UserService } from '../services/userService';
import { CompanyService } from '../services/companyService';
import { FavoritesService } from '../services/favoritesService';
import { ProductService } from '../services/productService';
import { Product } from '../data/mockProducts';
import { Company } from '../types';
import { ProfileImageService } from '../services/profileImageService';
import { InquiryService, Inquiry } from '../services/inquiryService';
import { AuctionService, AuctionWithProduct } from '../services/auctionService';
import { PlatformSettingsService, PlatformFeeSettings } from '../services/platformSettingsService';
import { calculatePriceWithFeesSync } from '../utils/priceUtils';
import UserAvatar from '../components/Profile/UserAvatar';
import ProfileImageModal from '../components/Profile/ProfileImageModal';
import UserInfoModal from '../components/Profile/UserInfoModal';
import CompleteTransactionPopup from '../components/Settings/tabs/CompleteTransactionPopup';
import TransactionDetailsPopup from '../components/Payment/TransactionDetailsPopup';
import TransactionStatusLabel from '../components/Orders/TransactionStatusLabel';
import OrderTimeline from '../components/Orders/OrderTimeline';
import DeliveryManagerPopup from '../components/Orders/DeliveryManagerPopup';
import ThemeSwitcher from '../components/Theme/ThemeSwitcher';
import SettingsModal from '../components/Settings/SettingsModal';
import VerificationModal from '../components/Verification/VerificationModal';
import VerificationInfoModal from '../components/Verification/VerificationInfoModal';
import MyCompaniesModal from '../components/Companies/MyCompaniesModal';
import JobShuffleModal from '../components/Jobs/JobShuffleModal';
import { useTheme as useCustomTheme } from '../hooks/useTheme';
import ReviewModal from '../components/Reviews/ReviewModal';
import UserDocumentsModal from '../components/Documents/UserDocumentsModal';
import ProfilePreviewModal from '../components/Profile/ProfilePreviewModal';
import TalentPoolSection from '../components/TalentPool/TalentPoolSection';
import ProductPagination from '../components/Pagination/ProductPagination';
import toast from 'react-hot-toast';
import { translateText } from '../services/translationService';
import { getCachedAlias, cacheAlias, getCachedAvatar, cacheAvatar } from '../utils/profileCache';
import { ensureSupabaseConfigured, initSupabase } from '../lib/supabase';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`dashboard-tabpanel-${index}`}
      aria-labelledby={`dashboard-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

// Mock data
const mockOrders = [
  {
    id: 'ORD-001',
    date: '2024-01-15',
    status: 'delivered',
    total: 89.99,
    items: 2,
    products: [
      { name: 'Premium Cotton T-Shirt', image: 'https://images.unsplash.com/photo-1521572163474-6864f9cf17ab?w=100&h=100&fit=crop' },
      { name: 'Designer Jeans', image: 'https://images.unsplash.com/photo-1542272604-787c3835535d?w=100&h=100&fit=crop' },
    ],
  },
  {
    id: 'ORD-002',
    date: '2024-01-10',
    status: 'shipped',
    total: 65.99,
    items: 1,
    products: [
      { name: 'Elegant Summer Dress', image: 'https://images.unsplash.com/photo-1515372039744-b8f02a3ae446?w=100&h=100&fit=crop' },
    ],
  },
];

// Removed Bought Products mock list

const mockFavorites = [
  {
    id: '4',
    name: 'Classic Blazer',
    price: 149.99,
    originalPrice: 199.99,
    image: 'https://images.unsplash.com/photo-1594938298605-cd64d190e6bc?w=200&h=200&fit=crop',
    addedDate: '2024-01-12',
    rating: 4.7,
  },
  {
    id: '5',
    name: 'Leather Handbag',
    price: 79.99,
    originalPrice: 99.99,
    image: 'https://images.unsplash.com/photo-1553062407-98eeb64c6a62?w=200&h=200&fit=crop',
    addedDate: '2024-01-08',
    rating: 4.9,
  },
];

const mockMyProducts = [
  {
    id: '6',
    name: 'Premium Cotton T-Shirt',
    price: 29.99,
    originalPrice: 39.99,
    image: 'https://images.unsplash.com/photo-1521572163474-6864f9cf17ab?w=200&h=200&fit=crop',
    category: 'Clothing',
    subcategory: 'Tops',
    brand: 'EcoWear',
    stock: 45,
    sold: 23,
    views: 156,
    rating: 4.5,
    reviews: 12,
    status: 'active',
    uploadDate: '2024-01-20',
    lastUpdated: '2024-01-25',
    tags: ['casual', 'cotton', 'comfortable'],
    colors: ['Black', 'White', 'Navy'],
    sizes: ['S', 'M', 'L', 'XL'],
  },
  {
    id: '7',
    name: 'Designer Denim Jeans',
    price: 89.99,
    originalPrice: 119.99,
    image: 'https://images.unsplash.com/photo-1542272604-787c3835535d?w=200&h=200&fit=crop',
    category: 'Clothing',
    subcategory: 'Bottoms',
    brand: 'DenimCo',
    stock: 28,
    sold: 15,
    views: 234,
    rating: 4.8,
    reviews: 8,
    status: 'active',
    uploadDate: '2024-01-18',
    lastUpdated: '2024-01-22',
    tags: ['denim', 'casual', 'premium'],
    colors: ['Blue', 'Black'],
    sizes: ['28', '30', '32', '34', '36'],
  },
  {
    id: '8',
    name: 'Elegant Summer Dress',
    price: 65.99,
    originalPrice: 85.99,
    image: 'https://images.unsplash.com/photo-1515372039744-b8f02a3ae446?w=200&h=200&fit=crop',
    category: 'Clothing',
    subcategory: 'Dresses',
    brand: 'SummerStyle',
    stock: 12,
    sold: 8,
    views: 189,
    rating: 4.6,
    reviews: 5,
    status: 'active',
    uploadDate: '2024-01-15',
    lastUpdated: '2024-01-20',
    tags: ['summer', 'elegant', 'floral'],
    colors: ['Pink', 'Blue', 'White'],
    sizes: ['XS', 'S', 'M', 'L'],
  },
  {
    id: '9',
    name: 'Leather Crossbody Bag',
    price: 79.99,
    originalPrice: 99.99,
    image: 'https://images.unsplash.com/photo-1553062407-98eeb64c6a62?w=200&h=200&fit=crop',
    category: 'Accessories',
    subcategory: 'Bags',
    brand: 'LeatherLux',
    stock: 8,
    sold: 12,
    views: 298,
    rating: 4.9,
    reviews: 18,
    status: 'active',
    uploadDate: '2024-01-10',
    lastUpdated: '2024-01-18',
    tags: ['leather', 'luxury', 'handbag'],
    colors: ['Brown', 'Black', 'Tan'],
    sizes: ['One Size'],
  },
  {
    id: '10',
    name: 'Vintage Style Watch',
    price: 149.99,
    originalPrice: 199.99,
    image: 'https://images.unsplash.com/photo-1523275335684-37898b6baf30?w=200&h=200&fit=crop',
    category: 'Accessories',
    subcategory: 'Watches',
    brand: 'TimeClassic',
    stock: 5,
    sold: 3,
    views: 67,
    rating: 4.3,
    reviews: 2,
    status: 'draft',
    uploadDate: '2024-01-28',
    lastUpdated: '2024-01-28',
    tags: ['vintage', 'watch', 'classic'],
    colors: ['Gold', 'Silver'],
    sizes: ['One Size'],
  },
];

const UserDashboard: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const { currentTheme, changeTheme } = useCustomTheme();
  const [searchParams] = useSearchParams();
  const { is_job } = useAppMode();
  const { t, language, autoTranslate } = useTranslation();
  const { setMessagesSidebarOpen } = useUIStore();
  
  // Company dashboard state
  const [companyId, setCompanyId] = useState<string | null>(null);
  const [companyName, setCompanyName] = useState<string | null>(null);
  const isCompanyDashboard = companyId && companyName;
  
  // Tab persistence key
  const DASHBOARD_TAB_STORAGE_KEY = 'dashboard_active_tab';
  
  // Initialize tab value from URL params, localStorage, or default to 0
  const [tabValue, setTabValue] = useState(() => {
    // Check URL parameters first
    const urlTab = searchParams.get('tab');
    if (urlTab === 'favorites') {
      // Find the favorites tab index
      const showInquiries = is_job && !isCompanyDashboard;
      const showAuctions = !is_job && !isCompanyDashboard;
      let favoritesIndex = 2; // base index after Interviews(0) and Applications(1)
      if (showInquiries) favoritesIndex++;
      if (showAuctions) favoritesIndex++;
      return favoritesIndex;
    }
    
    // Fallback to localStorage
    const savedTab = localStorage.getItem(DASHBOARD_TAB_STORAGE_KEY);
    return savedTab ? parseInt(savedTab, 10) : 0;
  });
  const [ordersTab, setOrdersTab] = useState<'made' | 'received'>('made');
  const [ordersView, setOrdersView] = useState<'sold' | 'bought'>('sold');
  // Dynamic translations for product names/categories
  const [translatedProducts, setTranslatedProducts] = useState<Record<string, { name: string; category?: string; subcategory?: string }>>({});
  const [purchases, setPurchases] = useState<Purchase[]>([]);
  const [loadingPurchases, setLoadingPurchases] = useState(false);
  const [purchasesError, setPurchasesError] = useState<string | null>(null);
  // Offers/Applications data
  const [offersMade, setOffersMade] = useState<Offer[]>([]);
  const [offersReceived, setOffersReceived] = useState<Offer[]>([]);
  const [applicationsSubmitted, setApplicationsSubmitted] = useState<any[]>([]);
  const [applicationsReceived, setApplicationsReceived] = useState<any[]>([]);
  const [loadingOffers, setLoadingOffers] = useState(false);
  const [editingOffer, setEditingOffer] = useState<Offer | null>(null);
  const [savingEdit, setSavingEdit] = useState(false);
  const [offersError, setOffersError] = useState<string | null>(null);
  const [offersFilterOpen, setOffersFilterOpen] = useState(true);
  // Orders filters
  const [ordersFilterOpen, setOrdersFilterOpen] = useState(true);
  const [ordersStatusFilter, setOrdersStatusFilter] = useState<'all' | 'pending' | 'requires_action' | 'succeeded' | 'failed' | 'refunded' | 'cancelled'>('all');
  const [ordersMinPrice, setOrdersMinPrice] = useState('');
  const [ordersMaxPrice, setOrdersMaxPrice] = useState('');
  const [productInfoById, setProductInfoById] = useState<Record<string, { id: string; name: string; price: number; image?: string; description?: string }>>({});
  const [translatedOfferProducts, setTranslatedOfferProducts] = useState<Record<string, { name?: string; description?: string }>>({});
  const [translatedOfferMessages, setTranslatedOfferMessages] = useState<Record<string, string>>({});
  // Inquiries data
  const [inquiries, setInquiries] = useState<Inquiry[]>([]);
  const [loadingInquiries, setLoadingInquiries] = useState(false);
  const [inquiriesError, setInquiriesError] = useState<string | null>(null);
  const [inquiriesView, setInquiriesView] = useState<'received' | 'sent'>('received');
  const [inquiryProfilePictures, setInquiryProfilePictures] = useState<Record<string, string>>({});
  const [profilePreviewOpen, setProfilePreviewOpen] = useState(false);
  const [previewUserData, setPreviewUserData] = useState<any>(null);
  // Auctions data
  const [auctions, setAuctions] = useState<AuctionWithProduct[]>([]);
  const [loadingAuctions, setLoadingAuctions] = useState(false);
  const [auctionsError, setAuctionsError] = useState<string | null>(null);
  const [auctionFilter, setAuctionFilter] = useState<'all' | 'active' | 'ended' | 'cancelled'>('all');
  const [filteredAuctions, setFilteredAuctions] = useState<AuctionWithProduct[]>([]);
  const [translatedAuctionNames, setTranslatedAuctionNames] = useState<Record<string, string>>({});
  const [translatedAuctionDescriptions, setTranslatedAuctionDescriptions] = useState<Record<string, string>>({});
  // Offer filters
  const [statusFilter, setStatusFilter] = useState<'all' | 'pending' | 'accepted' | 'declined' | 'countered' | 'expired'>('all');
  const [minPrice, setMinPrice] = useState<string>('');
  const [maxPrice, setMaxPrice] = useState<string>('');
  // Application filters
  const [showAIGeneratedOnly, setShowAIGeneratedOnly] = useState(false);
  const [applicationStatusFilter, setApplicationStatusFilter] = useState('all');
  // Application pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalApplications, setTotalApplications] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [applicationsPerPage] = useState(5);
  // Platform fee settings state
  const [platformFeeSettings, setPlatformFeeSettings] = useState<PlatformFeeSettings | null>(null);
  // Toggle for showing prices with/without fees in My Products tab
  const [showPricesWithFees, setShowPricesWithFees] = useState(true);
  // Translated fields for orders (product name/description)
  const [translatedOrders, setTranslatedOrders] = useState<Record<string, { name?: string; description?: string }>>({});

  // Handle page change
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  

  // Cache management functions
  const getCacheKey = (page: number, limit: number) => `page_${page}_limit_${limit}`;
  
  const isCacheValid = (timestamp: number) => {
    return Date.now() - timestamp < CACHE_DURATION;
  };
  
  const getCachedData = (page: number, limit: number) => {
    const cacheKey = getCacheKey(page, limit);
    const cached = productsCache.get(cacheKey);
    
    if (cached && isCacheValid(cached.timestamp)) {
      console.log(`📦 Cache HIT for page ${page} (limit: ${limit})`);
      return cached;
    }
    
    if (cached) {
      console.log(`⏰ Cache EXPIRED for page ${page} (limit: ${limit})`);
    } else {
      console.log(`❌ Cache MISS for page ${page} (limit: ${limit})`);
    }
    
    return null;
  };
  
  const setCachedData = (page: number, limit: number, data: {
    products: UserProduct[];
    totalCount: number;
    totalPages: number;
  }) => {
    const cacheKey = getCacheKey(page, limit);
    
    setProductsCache(prev => {
      const newCache = new Map(prev);
      
      // Remove oldest entries if cache is full
      if (newCache.size >= MAX_CACHE_SIZE) {
        const oldestKey = Array.from(newCache.keys())[0];
        newCache.delete(oldestKey);
      }
      
      newCache.set(cacheKey, {
        ...data,
        timestamp: Date.now()
      });
      
      console.log(`💾 Cached page ${page} (limit: ${limit}). Cache size: ${newCache.size}`);
      return newCache;
    });
  };
  
  const clearCache = () => {
    setProductsCache(new Map());
    console.log('🗑️ Cache cleared');
  };

  const loadProductsRequestRef = useRef(0);
  
  const invalidateCache = () => {
    setProductsCache(new Map());
    console.log('🔄 Cache invalidated due to data modification');
  };

  // Remove a product from all cached pages
  const removeFromCache = (productId: string) => {
    setProductsCache(prev => {
      const next = new Map(prev);
      next.forEach((entry: any, key: string) => {
        const filtered = (entry.products || []).filter((p: any) => p.id !== productId);
        if (filtered.length !== entry.products.length) {
          next.set(key, {
            ...entry,
            products: filtered,
            totalCount: Math.max(0, (entry.totalCount || 0) - 1),
            totalPages: Math.max(1, Math.ceil(Math.max(0, (entry.totalCount || 0) - 1) / productsPerPage))
          });
        }
      });
      return next;
    });
  };

  // Load products with pagination and caching
  const loadProducts = async (page: number = productsCurrentPage, limit: number = productsPerPage) => {
    const requestId = ++loadProductsRequestRef.current;
    const modeSnapshot = !!isCompanyDashboard;
    const companySnapshot = companyId;
    try {
      setLoading(true);
      setError(null);

      if (isCompanyDashboard) {
        // For company dashboard, load only jobs belonging to the selected company
        const jobs = await UserProductService.getCompanyJobs(companyId!);

        // Ignore stale responses
        if (requestId !== loadProductsRequestRef.current) return;
        if (!isCompanyDashboard) return;
        if (companySnapshot !== companyId) return;

        setUserProducts(jobs);
        setProductsTotalCount(jobs.length);
        setProductsTotalPages(1);
      } else {
        // Check cache first
        const cachedData = getCachedData(page, limit);
        
        if (cachedData) {
          // Use cached data

          // Ignore stale responses
          if (requestId !== loadProductsRequestRef.current) return;
          if (modeSnapshot !== !!isCompanyDashboard) return;
          if (modeSnapshot && companySnapshot !== companyId) return;

          setUserProducts(cachedData.products);
          setProductsTotalCount(cachedData.totalCount);
          setProductsTotalPages(cachedData.totalPages);
          setLoading(false);
          return;
        }
        
        // Fetch from Supabase if not in cache
        console.log(`🌐 Fetching page ${page} from Supabase (limit: ${limit})`);
        const result = await UserProductService.getUserProductsPaginated(page, limit);

        // Ignore stale responses
        if (requestId !== loadProductsRequestRef.current) return;
        if (modeSnapshot !== !!isCompanyDashboard) return;
        if (modeSnapshot && companySnapshot !== companyId) return;
        
        // Cache the result
        setCachedData(page, limit, {
          products: result.products,
          totalCount: result.totalCount,
          totalPages: result.totalPages
        });
        
        setUserProducts(result.products);
        setProductsTotalCount(result.totalCount);
        setProductsTotalPages(result.totalPages);
      }
    } catch (err) {
      console.error('Error loading products:', err);
      setError(err instanceof Error ? err.message : 'Failed to load products');
    } finally {
      setLoading(false);
    }
  };

  // Preload adjacent pages for better performance
  const preloadAdjacentPages = (currentPage: number, totalPages: number, limit: number) => {
    const pagesToPreload = [];
    
    // Preload next page if it exists
    if (currentPage < totalPages) {
      pagesToPreload.push(currentPage + 1);
    }
    
    // Preload previous page if it exists
    if (currentPage > 1) {
      pagesToPreload.push(currentPage - 1);
    }
    
    // Preload pages in background
    pagesToPreload.forEach(page => {
      const cacheKey = getCacheKey(page, limit);
      if (!productsCache.has(cacheKey)) {
        console.log(`🔄 Preloading page ${page} in background`);
        UserProductService.getUserProductsPaginated(page, limit)
          .then(result => {
            setCachedData(page, limit, {
              products: result.products,
              totalCount: result.totalCount,
              totalPages: result.totalPages
            });
          })
          .catch(err => console.warn(`Failed to preload page ${page}:`, err));
      }
    });
  };

  // Products pagination handlers
  const handleProductsPageChange = (page: number) => {
    setProductsCurrentPage(page);
    loadProducts(page, productsPerPage);
    
    // Preload adjacent pages for better performance
    if (productsTotalPages > 1) {
      preloadAdjacentPages(page, productsTotalPages, productsPerPage);
    }
  };

  const handleProductsPerPageChange = (itemsPerPage: number) => {
    setProductsPerPage(itemsPerPage);
    setProductsCurrentPage(1); // Reset to first page when changing items per page
    // Clear cache when changing items per page since cache keys include limit
    clearCache();
    loadProducts(1, itemsPerPage);
  };
  const [actingOfferId, setActingOfferId] = useState<string | null>(null);
  const [confirmOffer, setConfirmOffer] = useState<Offer | null>(null);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [deleteOffer, setDeleteOffer] = useState<Offer | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [debugError, setDebugError] = useState<string | null>(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [myCompaniesOpen, setMyCompaniesOpen] = useState(false);
  const [userInfoOpen, setUserInfoOpen] = useState(false);
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [jobShuffleOpen, setJobShuffleOpen] = useState(false);
  const { 
    jobApplicationWriterOpen, 
    setJobApplicationWriterOpen,
    jobApplicationWriterMinimized,
    setJobApplicationWriterMinimized 
  } = useUIStore();
  const [selectedOrderForPayment, setSelectedOrderForPayment] = useState<any>(null);
  const [transactionDetailsOpen, setTransactionDetailsOpen] = useState(false);
  const [selectedOrderForDetails, setSelectedOrderForDetails] = useState<string | null>(null);
  const [deliveryManagerOpen, setDeliveryManagerOpen] = useState(false);
  const [deliveryManagerTarget, setDeliveryManagerTarget] = useState<Purchase | null>(null);
  // Documents modal state
  const [documentsModalOpen, setDocumentsModalOpen] = useState(false);
  // Review modal state
  const [reviewOpen, setReviewOpen] = useState(false);
  const [reviewTarget, setReviewTarget] = useState<Purchase | null>(null);
  const [reviewRating, setReviewRating] = useState<number | null>(5);
  const [reviewTitle, setReviewTitle] = useState('');
  const [reviewComment, setReviewComment] = useState('');
  const mainTabsRef = useRef<HTMLDivElement | null>(null);
  const [reviewSubmitting, setReviewSubmitting] = useState(false);
  const [reviewUserShowAlias, setReviewUserShowAlias] = useState(false);
  // category ratings
  const [reviewPriceRating, setReviewPriceRating] = useState(0);
  const [reviewAccuracyRating, setReviewAccuracyRating] = useState(0);
  const [reviewDeliveryRating, setReviewDeliveryRating] = useState(0);
  const [reviewPackagingRating, setReviewPackagingRating] = useState(0);
  const [reviewCommunicationRating, setReviewCommunicationRating] = useState(0);
  const [reviewQualityRating, setReviewQualityRating] = useState(0);
  const [verificationOpen, setVerificationOpen] = useState(false);
  const [verificationInfoOpen, setVerificationInfoOpen] = useState(false);
  const [isVerified, setIsVerified] = useState<boolean>(false);
  const [showScrollTop, setShowScrollTop] = useState(false);

  const [dashboardCompanies, setDashboardCompanies] = useState<Company[]>([]);
  const [dashboardCompaniesLoading, setDashboardCompaniesLoading] = useState(false);
  const [dashboardCompaniesError, setDashboardCompaniesError] = useState<string | null>(null);
  const [companiesSubTab, setCompaniesSubTab] = useState(0);
  const [companyMemberMetaById, setCompanyMemberMetaById] = useState<Record<string, { role?: string; job_title?: string | null; department?: string | null }>>({});
  
  // Smooth scroll to main tabs, with desktop offset for sticky top bars
  const handleScrollToMainTabs = React.useCallback(() => {
    const anchor = mainTabsRef.current;
    if (!anchor) return;
    const rect = anchor.getBoundingClientRect();
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const desktopOffset = 80; // approximate sticky top height
    const top = rect.top + scrollTop - (isMobile ? 0 : desktopOffset);
    window.scrollTo({ top, behavior: 'smooth' });
  }, [isMobile]);

  // Show scroll-to-top button when header is out of view
  useEffect(() => {
    // Load the current user's verified flag
    const loadVerified = async () => {
      try {
        const { data: { user: authUser } } = await supabase.auth.getUser();
        const uid = (authUser as any)?.id || (user as any)?.id;
        if (!uid) return;
        const { data } = await supabase
          .from('users')
          .select('is_verified')
          .eq('id', uid)
          .maybeSingle();
        setIsVerified(!!data?.is_verified);
      } catch {}
    };
    loadVerified();

    const onScroll = () => {
      const anchor = mainTabsRef.current;
      if (!anchor) return setShowScrollTop(window.scrollY > 300);
      const rect = anchor.getBoundingClientRect();
      const pageTop = window.pageYOffset || document.documentElement.scrollTop;
      const anchorTop = rect.top + pageTop;
      setShowScrollTop(pageTop > anchorTop - 60);
    };
    window.addEventListener('scroll', onScroll, { passive: true } as any);
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // After verification modal closes, refresh status
  useEffect(() => {
    const refresh = async () => {
      if (verificationOpen) return;
      try {
        const { data: { user: authUser } } = await supabase.auth.getUser();
        const uid = (authUser as any)?.id || (user as any)?.id;
        if (!uid) return;
        const { data } = await supabase
          .from('users')
          .select('is_verified')
          .eq('id', uid)
          .maybeSingle();
        setIsVerified(!!data?.is_verified);
      } catch {}
    };
    refresh();
  }, [verificationOpen]);

  // Product management popup state
  const [productMenuAnchor, setProductMenuAnchor] = useState<HTMLElement | null>(null);
  const [selectedProduct, setSelectedProduct] = useState<UserProduct | null>(null);
  const [updatingProduct, setUpdatingProduct] = useState(false);

  const buildChatState = (offer: Offer, type: 'made' | 'received') => {
    const product = productInfoById[offer.product_id] || { name: 'Product', price: 0 } as any;
    const recipientId = type === 'made' ? offer.seller_id : offer.buyer_id;
    return {
      recipientId,
      recipientName: 'User',
      productId: offer.product_id,
      productTitle: product.name,
      productPrice: product.price,
      subject: `Offer for ${product.name}`,
      isOffer: true,
    };
  };
  
  // State for real products data
  const [userProducts, setUserProducts] = useState<UserProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Products pagination
  const [productsCurrentPage, setProductsCurrentPage] = useState(1);
  const [productsPerPage, setProductsPerPage] = useState(6); // Default to 6 entries per page
  const [productsTotalCount, setProductsTotalCount] = useState(0);
  const [productsTotalPages, setProductsTotalPages] = useState(0);
  
  // Products cache - stores previously fetched pages
  const [productsCache, setProductsCache] = useState<Map<string, {
    products: UserProduct[];
    totalCount: number;
    totalPages: number;
    timestamp: number;
  }>>(new Map());
  
  // Cache configuration
  const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes in milliseconds
  const MAX_CACHE_SIZE = 10; // Maximum number of pages to cache

  // Translate product names/categories dynamically using the same API as AllProductsPage
  useEffect(() => {
    const run = async () => {
      try {
        if (!autoTranslate || language === 'en') {
          setTranslatedProducts({});
          return;
        }
        const texts = new Set<string>();
        userProducts.forEach((p) => {
          if (p.name) texts.add(p.name);
          if (p.category_name) texts.add(p.category_name);
          if (p.subcategory_name) texts.add(p.subcategory_name);
        });
        const mapText: Record<string, string> = {};
        await Promise.all(
          Array.from(texts).map(async (txt) => {
            const tr = await translateText(txt, language as any, 'en');
            mapText[txt] = tr;
          })
        );
        const out: Record<string, { name: string; category?: string; subcategory?: string }> = {};
        userProducts.forEach((p) => {
          out[p.id] = {
            name: p.name ? (mapText[p.name] || p.name) : '',
            category: p.category_name ? (mapText[p.category_name] || p.category_name) : undefined,
            subcategory: p.subcategory_name ? (mapText[p.subcategory_name] || p.subcategory_name) : undefined,
          };
        });
        setTranslatedProducts(out);
      } catch (e) {
        // Fallback silently
      }
    };
    run();
  }, [userProducts, language, autoTranslate]);

  // Translate product names and descriptions for Offers tab
  useEffect(() => {
    const run = async () => {
      try {
        if (!autoTranslate || language === 'en') {
          setTranslatedOfferProducts({});
          return;
        }
        const entries = Object.entries(productInfoById);
        if (entries.length === 0) {
          setTranslatedOfferProducts({});
          return;
        }
        const texts = new Set<string>();
        entries.forEach(([_, p]) => {
          if (p.name) texts.add(p.name);
          if (p.description) texts.add(p.description);
        });
        const mapText: Record<string, string> = {};
        await Promise.all(
          Array.from(texts).map(async (txt) => {
            const tr = await translateText(txt, language as any, 'en');
            mapText[txt] = tr;
          })
        );
        const out: Record<string, { name?: string; description?: string }> = {};
        entries.forEach(([id, p]) => {
          out[id] = {
            name: p.name ? (mapText[p.name] || p.name) : undefined,
            description: p.description ? (mapText[p.description] || p.description) : undefined,
          };
        });
        setTranslatedOfferProducts(out);
      } catch {
        // silent fallback
      }
    };
    run();
  }, [productInfoById, language, autoTranslate]);

  // Translate order product names/descriptions in Orders tab
  useEffect(() => {
    const run = async () => {
      try {
        if (!autoTranslate || language === 'en') {
          setTranslatedOrders({});
          return;
        }
        if (!purchases || purchases.length === 0) {
          setTranslatedOrders({});
          return;
        }
        const texts = new Set<string>();
        purchases.forEach((o) => {
          if (o.product_name) texts.add(o.product_name);
          if (o.product_description) texts.add(o.product_description);
        });
        const mapText: Record<string, string> = {};
        await Promise.all(
          Array.from(texts).map(async (txt) => {
            const tr = await translateText(txt, language as any, 'en');
            mapText[txt] = tr;
          })
        );
        const out: Record<string, { name?: string; description?: string }> = {};
        purchases.forEach((o) => {
          out[o.id] = {
            name: o.product_name ? (mapText[o.product_name] || o.product_name) : undefined,
            description: o.product_description ? (mapText[o.product_description] || o.product_description) : undefined,
          };
        });
        setTranslatedOrders(out);
      } catch {
        // silent fallback
      }
    };
    run();
  }, [purchases, language, autoTranslate]);

  // Translate offer messages for both made and received offers
  useEffect(() => {
    const run = async () => {
      try {
        if (!autoTranslate || language === 'en') {
          setTranslatedOfferMessages({});
          return;
        }
        const allOffers = [...offersMade, ...offersReceived];
        if (allOffers.length === 0) {
          setTranslatedOfferMessages({});
          return;
        }
        const uniqueMessages = new Set<string>();
        allOffers.forEach(o => {
          if (o.offer_message) uniqueMessages.add(o.offer_message);
        });
        const mapText: Record<string, string> = {};
        await Promise.all(
          Array.from(uniqueMessages).map(async (msg) => {
            const tr = await translateText(msg, language as any, 'en');
            mapText[msg] = tr;
          })
        );
        const out: Record<string, string> = {};
        allOffers.forEach(o => {
          if (o.offer_message) out[o.id] = mapText[o.offer_message] || o.offer_message;
        });
        setTranslatedOfferMessages(out);
      } catch {
        // silent fallback
      }
    };
    run();
  }, [offersMade, offersReceived, language, autoTranslate]);

  // Utility function to calculate auction time remaining
  const getAuctionTimeRemaining = (endTime: string) => {
    const now = new Date();
    const end = new Date(endTime);
    const diffMs = end.getTime() - now.getTime();
    
    if (diffMs <= 0) {
      return { expired: true, text: 'Auction Ended' };
    }
    
    const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
    
    if (days > 0) {
      return { expired: false, text: `${days} day${days !== 1 ? 's' : ''} left` };
    } else if (hours > 0) {
      return { expired: false, text: `${hours} hour${hours !== 1 ? 's' : ''} left` };
    } else {
      return { expired: false, text: `${minutes} minute${minutes !== 1 ? 's' : ''} left` };
    }
  };
  
  // State for user alias (hydrate from cache synchronously to prevent flicker)
  const [userAlias, setUserAlias] = useState<string | null>(() => {
    try {
      const uid = useAuthStore.getState().user?.id as string | undefined;
      return uid ? getCachedAlias(uid) : null;
    } catch { return null; }
  });
  const [loadingAlias, setLoadingAlias] = useState<boolean>(() => {
    try {
      const uid = useAuthStore.getState().user?.id as string | undefined;
      const cached = uid ? getCachedAlias(uid) : null;
      return !cached;
    } catch { return true; }
  });
  
  // State for profile images
  const [profileImages, setProfileImages] = useState<{
    profile_picture_url?: string;
    banner_image_url?: string;
  } | null>(null);
  // Backblaze mode and resolved avatar URL
  const [b2Enabled, setB2Enabled] = useState<boolean>(false);
  const [avatarUrl, setAvatarUrl] = useState<string | undefined>(undefined);
  const [loadingProfileImages, setLoadingProfileImages] = useState(true);
  const [profileImageOpen, setProfileImageOpen] = useState(false);
 
  useEffect(() => {
    const uid = user?.id;
    if (!uid) return;
    const cachedAlias = getCachedAlias(uid);
    if (cachedAlias) {
      setUserAlias(cachedAlias);
      setLoadingAlias(false);
    }
    const cachedAvatar = getCachedAvatar(uid);
    if (cachedAvatar) {
      setAvatarUrl(cachedAvatar);
    }
  }, [user?.id]);
  
  // State for favorites
  const [favoriteProducts, setFavoriteProducts] = useState<Product[]>([]);
  const [favoriteJobs, setFavoriteJobs] = useState<any[]>([]);
  const [loadingFavorites, setLoadingFavorites] = useState(true);
  const [favoritesError, setFavoritesError] = useState<string | null>(null);
  const [translatedFavoriteNames, setTranslatedFavoriteNames] = useState<Record<string, string>>({});

  // Application details modal
  const [applicationModalOpen, setApplicationModalOpen] = useState(false);
  const [selectedApplication, setSelectedApplication] = useState<any | null>(null);

  // Handle application status update
  const handleApplicationStatusUpdate = async (applicationId: string, newStatus: 'accepted' | 'rejected' | 'submitted' | 'review') => {
    try {
      await ApplicationService.updateApplicationStatus(applicationId, newStatus);
      
      // Update the local state to reflect the status change
      if (selectedApplication && selectedApplication.id === applicationId) {
        setSelectedApplication((prev: any) => prev ? { ...prev, status: newStatus } : null);
      }
      
      // Update applications received list immediately
      setApplicationsReceived(prev => 
        prev.map(app => 
          app.id === applicationId 
            ? { ...app, status: newStatus, updated_at: new Date().toISOString() }
            : app
        )
      );
      
      // Refresh the applications list
      if (is_job && !isCompanyDashboard) {
        // If the current filter would hide the updated application, adjust it
        let filterToUse = applicationStatusFilter;
        if (applicationStatusFilter !== 'all' && applicationStatusFilter !== newStatus) {
          // Show a toast to inform the user about the filter change
          toast(`Filter adjusted to show ${newStatus} applications`, { 
            icon: 'ℹ️',
            duration: 3000,
            id: 'filter-adjusted'
          });
          filterToUse = 'all'; // Show all applications so the user can see the updated status
        }
        
        // Refresh user's submitted applications
        const submittedResult = await ApplicationService.getSubmittedByUser(
          user!.id, 
          currentPage, 
          applicationsPerPage, 
          showAIGeneratedOnly,
          filterToUse
        );
        setApplicationsSubmitted(submittedResult.data);
        setTotalApplications(submittedResult.total);
        setTotalPages(submittedResult.totalPages);
        
        // Update the filter state if we changed it
        if (filterToUse !== applicationStatusFilter) {
          setApplicationStatusFilter(filterToUse);
        }
      } else if (isCompanyDashboard) {
        // Refresh company's received applications with a small delay to ensure DB is updated
        setTimeout(async () => {
          try {
            const receivedApps = await ApplicationService.getReceivedByCompany(user!.id);
            if (receivedApps && receivedApps.length > 0) {
              setApplicationsReceived(receivedApps);
              console.log('Refreshed company applications:', receivedApps.length);
            } else {
              console.log('API returned empty applications, keeping local state');
              // Keep the current local state if API returns empty
            }
          } catch (error) {
            console.error('Error refreshing company applications:', error);
            // Keep the current local state if API fails
          }
        }, 500); // 500ms delay
      }
      
      toast.success(`Application ${newStatus} successfully! 🎉`);
    } catch (error) {
      console.error('Error updating application status:', error);
      toast.error('Failed to update application status. Please try again.');
    }
  };

  // Wrapper function for ApplicationStatusActions component (it handles API calls internally)
  const handleApplicationStatusUpdateForActions = (applicationId: string, newStatus: string) => {
    // Update the local state to reflect the status change
    if (selectedApplication && selectedApplication.id === applicationId) {
      setSelectedApplication((prev: any) => prev ? { ...prev, status: newStatus } : null);
    }
    
    // Update applications received list immediately
    setApplicationsReceived(prev => 
      prev.map(app => 
        app.id === applicationId 
          ? { ...app, status: newStatus, updated_at: new Date().toISOString() }
          : app
      )
    );
    
    // Refresh the applications list
    if (is_job && !isCompanyDashboard) {
      // If the current filter would hide the updated application, adjust it
      let filterToUse = applicationStatusFilter;
      if (applicationStatusFilter !== 'all' && applicationStatusFilter !== newStatus) {
        // Show a toast to inform the user about the filter change
        toast(`Filter adjusted to show ${newStatus} applications`, { 
          icon: 'ℹ️',
          duration: 3000,
          id: 'filter-adjusted'
        });
        filterToUse = 'all'; // Show all applications so the user can see the updated status
      }
      
      // Refresh user's submitted applications
      ApplicationService.getSubmittedByUser(
        user!.id, 
        currentPage, 
        applicationsPerPage, 
        showAIGeneratedOnly,
        filterToUse
      ).then(submittedResult => {
        setApplicationsSubmitted(submittedResult.data);
        setTotalApplications(submittedResult.total);
        setTotalPages(submittedResult.totalPages);
        
        // Update the filter state if we changed it
        if (filterToUse !== applicationStatusFilter) {
          setApplicationStatusFilter(filterToUse);
        }
      });
    } else if (isCompanyDashboard) {
      // Refresh company's received applications with a small delay to ensure DB is updated
      setTimeout(() => {
        ApplicationService.getReceivedByCompany(user!.id).then(receivedApps => {
          if (receivedApps && receivedApps.length > 0) {
            setApplicationsReceived(receivedApps);
            console.log('Refreshed company applications (wrapper):', receivedApps.length);
          } else {
            console.log('API returned empty applications (wrapper), keeping local state');
            // Keep the current local state if API returns empty
          }
        }).catch(error => {
          console.error('Error refreshing company applications (wrapper):', error);
          // Keep the current local state if API fails
        });
      }, 500); // 500ms delay
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    // Save the selected tab to localStorage for persistence
    localStorage.setItem(DASHBOARD_TAB_STORAGE_KEY, newValue.toString());
  };

  // Fetch profile images
  const fetchProfileImages = async () => {
    if (!user?.id) return;
    
    setLoadingProfileImages(true);
    try {
      const images = await ProfileImageService.getUserProfileImages(user.id);
      setProfileImages(images);
    } catch (error) {
      console.error('Error fetching profile images:', error);
      // Don't show error to user as this is not critical
    } finally {
      setLoadingProfileImages(false);
    }
  };

  // Fetch favorites depending on mode
  const fetchFavoriteProducts = async () => {
    if (!user?.id) return;
    
    setLoadingFavorites(true);
    setFavoritesError(null);
    
    try {
      if (is_job) {
        // Favorite jobs
        const favorites = await FavoritesService.getUserJobFavorites(user.id);
        const jobIds = favorites.map(f => f.job_id);
        const { products: allProducts } = await ProductService.getProductsWithSellers({ limit: 1000 });
        const onlyJobs = allProducts.filter(p => p.isJob);
        const jobs = onlyJobs.filter(j => jobIds.includes(j.id));
        setFavoriteJobs(jobs);
        setFavoriteProducts([]);
      } else {
        // Favorite products
        const favorites = await FavoritesService.getUserFavorites(user.id);
        if (favorites.length === 0) {
          setFavoriteProducts([]);
          setFavoriteJobs([]);
          return;
        }
        const productIds = favorites.map(fav => fav.product_id);
        const { products: allProducts } = await ProductService.getProductsWithSellers({ limit: 1000 });
        const products = allProducts.filter(product => productIds.includes(product.id) && !product.isJob);
        setFavoriteProducts(products);
        setFavoriteJobs([]);
      }
    } catch (error) {
      console.error('Error fetching favorite products:', error);
      setFavoritesError(t('dashboard.favorites.error.load'));
    } finally {
      setLoadingFavorites(false);
    }
  };

  // Remove product from favorites
  const handleRemoveFavorite = async (productId: string) => {
    if (!user?.id) return;
    
    try {
      if (is_job) {
        await FavoritesService.removeJobFromFavorites(productId, user.id);
        setFavoriteJobs(prev => prev.filter(job => job.id !== productId));
      } else {
        await FavoritesService.removeFromFavorites(productId, user.id);
        setFavoriteProducts(prev => prev.filter(product => product.id !== productId));
      }
      
      toast.success(t('dashboard.favorites.toast.removed'));
    } catch (error) {
      console.error('Error removing favorite:', error);
      toast.error('Failed to remove from favorites');
    }
  };

  // Dynamic translation for favorite names
  useEffect(() => {
    const run = async () => {
      if (!autoTranslate || language === 'en') {
        setTranslatedFavoriteNames({});
        return;
      }
      const list = is_job ? favoriteJobs : favoriteProducts;
      if (!list || list.length === 0) {
        setTranslatedFavoriteNames({});
        return;
      }
      try {
        const entries = await Promise.all(
          list.map(async (p: any) => {
            const tr = await translateText(p.name || 'Untitled', language as any, 'en');
            return [p.id, tr] as const;
          })
        );
        const map: Record<string, string> = {};
        entries.forEach(([id, name]) => (map[id] = name));
        setTranslatedFavoriteNames(map);
      } catch {
        setTranslatedFavoriteNames({});
      }
    };
    run();
  }, [favoriteProducts, favoriteJobs, language, autoTranslate, is_job]);

  const handleRespondToInquiry = async (inquiryId: string, status: 'approved' | 'rejected' | 'pending') => {
    try {
      const result = await InquiryService.respondToInquiry(inquiryId, status);
      
      if (result.success) {
        // Update the inquiry in the local state
        setInquiries(prev => 
          prev.map(inquiry => 
            inquiry.id === inquiryId 
              ? { ...inquiry, status, responded_at: new Date().toISOString() }
              : inquiry
          )
        );
        
        // Update localStorage if needed
        const inquiry = inquiries.find(i => i.id === inquiryId);
        if (inquiry) {
          const localStorageKey = `inquiry_${inquiry.requester_user_id}_${inquiry.profile_user_id}`;
          const inquiryData = {
            status: status,
            date: new Date().toISOString()
          };
          localStorage.setItem(localStorageKey, JSON.stringify(inquiryData));
        }
        
        const statusMessages = {
          approved: 'approved',
          rejected: 'rejected', 
          pending: 'kept pending'
        };
        
        toast.success(`Inquiry ${statusMessages[status]} successfully`);
      } else {
        toast.error(result.error || 'Failed to respond to inquiry');
      }
    } catch (error) {
      console.error('Error responding to inquiry:', error);
      toast.error('Failed to respond to inquiry');
    }
  };

  const handleProfilePreview = async (userId: string) => {
    try {
      // Fetch user data for preview
      const userData = await UserService.getUserProfile(userId);
      setPreviewUserData(userData);
      setProfilePreviewOpen(true);
    } catch (error) {
      console.error('Error loading user data for preview:', error);
      toast.error('Failed to load user profile');
    }
  };

  // Payment handlers
  const handlePayNow = (order: any) => {
    setSelectedOrderForPayment(order);
    setPaymentModalOpen(true);
  };

  const handlePaymentSuccess = async () => {
    // Refresh purchases data after successful payment
    if (!user?.id) return;
    setLoadingPurchases(true);
    try {
      const data = await PurchasesService.getMyPurchases();
      setPurchases(data);
      toast.success(t('dashboard.orders.toast.paymentCompleted'));
    } catch (e: any) {
      console.error('Error refreshing purchases:', e);
      toast.error(t('dashboard.orders.toast.refreshFailed'));
    } finally {
      setLoadingPurchases(false);
    }
  };

  const handleSubmitReview = async () => {
    if (!reviewTarget || !user?.id || !reviewRating) return;
    try {
      setReviewSubmitting(true);
      const { data: userRow } = await supabase
        .from('users')
        .select('alias')
        .eq('id', user.id)
        .single();

      const { error } = await supabase
        .from('product_reviews')
        .insert({
          product_id: reviewTarget.product_id,
          buyer_id: user.id,
          purchase_id: reviewTarget.id,
          rating: reviewRating,
          title: reviewTitle || null,
          comment: reviewComment || null,
          user_showalias: reviewUserShowAlias,
          user_alias: reviewUserShowAlias ? (userRow?.alias || null) : null,
          price_rating: reviewPriceRating || null,
          accuracy_rating: reviewAccuracyRating || null,
          delivery_rating: reviewDeliveryRating || null,
          packaging_rating: reviewPackagingRating || null,
          communication_rating: reviewCommunicationRating || null,
          product_quality_rating: reviewQualityRating || null,
        });
      if (error) throw error;
      toast.success('Thanks for your review!');
      setReviewOpen(false);
      setReviewTarget(null);
      setReviewTitle('');
      setReviewComment('');
      setReviewRating(5);
      setReviewPriceRating(0);
      setReviewAccuracyRating(0);
      setReviewDeliveryRating(0);
      setReviewPackagingRating(0);
      setReviewCommunicationRating(0);
      setReviewQualityRating(0);
      setReviewUserShowAlias(false);
    } catch (e: any) {
      console.error('Submit review failed:', e);
      toast.error(e?.message || 'Failed to submit review');
    } finally {
      setReviewSubmitting(false);
    }
  };

  // Compute overall rating from category ratings (ignore zeros)
  useEffect(() => {
    const parts = [
      reviewPriceRating,
      reviewAccuracyRating,
      reviewDeliveryRating,
      reviewPackagingRating,
      reviewCommunicationRating,
      reviewQualityRating,
    ].filter(v => v > 0);
    if (parts.length > 0) {
      const avg = parts.reduce((a, b) => a + b, 0) / parts.length;
      const rounded = Math.round(avg * 10) / 10; // nearest 0.1
      setReviewRating(rounded);
    }
  }, [
    reviewPriceRating,
    reviewAccuracyRating,
    reviewDeliveryRating,
    reviewPackagingRating,
    reviewCommunicationRating,
    reviewQualityRating,
  ]);


  // Check if a product is the user's own product
  const isOwnProduct = (product: any) => {
    if (!user?.id) return false;
    // Support multiple shapes from different loaders
    const sellerIdCandidates = [
      product.sellerId,
      product.seller_id,
      product.seller?.id,
      product.sellerID,
    ].filter(Boolean);
    return sellerIdCandidates.includes(user.id);
  };

  // Fetch favorites when user is available
  useEffect(() => {
    if (user?.id) {
      fetchFavoriteProducts();
    }
  }, [user?.id, is_job]);

  // Load platform fee settings on mount
  useEffect(() => {
    const loadFeeSettings = async () => {
      try {
        const settings = await PlatformSettingsService.getPlatformFeeSettings();
        setPlatformFeeSettings(settings);
      } catch (error) {
        console.error('Error loading platform fee settings:', error);
        // Set default fee settings if loading fails
        setPlatformFeeSettings({
          fee_type: 'fixed',
          fee_amount: 0.50,
          fee_percentage: null,
        });
      }
    };
    loadFeeSettings();
  }, []);

  // Refresh purchases function (can be called after accepting offers)
  const refreshPurchases = React.useCallback(async () => {
    if (!user?.id) return;
    setLoadingPurchases(true);
    setPurchasesError(null);
    try {
      const data = await PurchasesService.getMyPurchases();
      setPurchases(data);
    } catch (e: any) {
      console.error('Error fetching purchases:', e);
      setPurchasesError(e?.message || t('dashboard.orders.toast.refreshFailed'));
    } finally {
      setLoadingPurchases(false);
    }
  }, [user?.id]);

  // Initial load: fetch purchases so Orders tab shows what's in Supabase
  useEffect(() => {
    if (!user?.id) return;
    refreshPurchases();
  }, [user?.id, refreshPurchases]);

  // Realtime: keep purchases in sync so newly accepted offers instantly appear in Orders
  useEffect(() => {
    if (!user?.id) return;

    const safeRefresh = () => {
      try {
        const p = refreshPurchases();
        if (p && typeof (p as any).catch === 'function') {
          (p as any).catch((e: any) => console.warn('Realtime purchases refresh failed:', e));
        }
      } catch (e) {
        console.warn('Realtime purchases refresh threw:', e);
      }
    };

    let channelBuyer: any = null;
    let channelSeller: any = null;

    try {
      channelBuyer = supabase
        .channel(`purchases-buyer-${user.id}`)
        .on(
          'postgres_changes',
          { event: '*', schema: 'public', table: 'purchases', filter: `buyer_id=eq.${user.id}` },
          safeRefresh
        )
        .subscribe();
    } catch (e) {
      console.warn('Failed to subscribe to purchases buyer channel:', e);
    }

    try {
      channelSeller = supabase
        .channel(`purchases-seller-${user.id}`)
        .on(
          'postgres_changes',
          { event: '*', schema: 'public', table: 'purchases', filter: `seller_id=eq.${user.id}` },
          safeRefresh
        )
        .subscribe();
    } catch (e) {
      console.warn('Failed to subscribe to purchases seller channel:', e);
    }

    return () => {
      try { if (channelBuyer) supabase.removeChannel(channelBuyer); } catch {}
      try { if (channelSeller) supabase.removeChannel(channelSeller); } catch {}
    };
  }, [user?.id, refreshPurchases]);

  // Fetch offers/applications and minimal product info
  useEffect(() => {
    if (!user) return;

    const fetchOffers = async () => {
      try {
        await initSupabase();
        ensureSupabaseConfigured();

        setLoadingOffers(true);
        setOffersError(null);

        // Local holders to avoid using stale state when building productIds
        let localProductIds: string[] = [];
        if (is_job && !isCompanyDashboard) {
          // Job mode (user dashboard): show only applications submitted by the user
          const submittedResult = await ApplicationService.getSubmittedByUser(
            user.id, 
            currentPage, 
            applicationsPerPage, 
            showAIGeneratedOnly,
            applicationStatusFilter
          );
          setApplicationsSubmitted(submittedResult.data);
          setTotalApplications(submittedResult.total);
          setTotalPages(submittedResult.totalPages);
          localProductIds = Array.from(new Set([
            ...submittedResult.data.map((a: any) => a.product_id).filter(Boolean),
          ]));
        } else if (isCompanyDashboard) {
          // Company dashboard: submitted by user and received by company
          const [submittedResult, receivedApps] = await Promise.all([
            ApplicationService.getSubmittedByUser(user.id, 1, 100), // Get all for company dashboard
            companyId ? ApplicationService.getReceivedByCompany(companyId) : Promise.resolve([]),
          ]);
          setApplicationsSubmitted(submittedResult.data);
          setApplicationsReceived(receivedApps as any);

          localProductIds = Array.from(new Set([
            ...submittedResult.data.map((a: any) => a.product_id).filter(Boolean),
            ...(receivedApps as any[]).map((a: any) => a.product_id).filter(Boolean),
          ]));
        } else {
          // Shop mode: offers
          const [made, received] = await Promise.all([
            OffersService.getMyOffers(),
            OffersService.getOffersReceived(),
          ]);
          setOffersMade(made);
          setOffersReceived(received);

          localProductIds = Array.from(new Set([
            ...made.map(o => o.product_id).filter(Boolean),
            ...received.map(o => o.product_id).filter(Boolean),
          ]));
        }

        // Build product info map from fresh IDs
        if (localProductIds.length > 0) {
          const { data, error } = await supabase
            .from('products')
            .select('id, name, price, images, description')
            .in('id', localProductIds);
          if (error) throw error;
          const info: Record<string, { id: string; name: string; price: number; image?: string; description?: string }> = {};
          // Robustly parse first image URL from images column (text[] or JSON string)
          const parseFirstImage = (images: any): string | undefined => {
            if (Array.isArray(images)) return images[0];
            if (typeof images === 'string') {
              // Try JSON array string first
              try {
                const parsed = JSON.parse(images);
                if (Array.isArray(parsed)) return parsed[0];
              } catch {}
              // Fallback for Postgres array syntax: {"url1","url2"}
              const trimmed = images.replace(/^\{|\}$/g, '');
              const first = trimmed.split(',')[0];
              if (first) return first.replace(/^\"|\"$/g, '');
            }
            return undefined;
          };

          (data || []).forEach((p: any) => {
            info[p.id] = {
              id: p.id,
              name: p.name,
              price: p.price,
              image: parseFirstImage(p.images),
              description: p.description,
            };
          });
          setProductInfoById(info);
        } else {
          setProductInfoById({});
        }
      } catch (e: any) {
        console.error('Error fetching offers/applications:', e);
        setOffersError(e?.message || 'Failed to load data');
      } finally {
        setLoadingOffers(false);
      }
    };
    fetchOffers();
  }, [user?.id, companyId, isCompanyDashboard, is_job, currentPage, applicationsPerPage, showAIGeneratedOnly, applicationStatusFilter]);

  // Filter auctions based on selected filter
  const filterAuctions = (auctions: AuctionWithProduct[], filter: string) => {
    if (filter === 'all') return auctions;
    return auctions.filter(auction => auction.status === filter);
  };

  // Check and update auction status based on end time
  const updateAuctionStatus = (auction: AuctionWithProduct): AuctionWithProduct => {
    const now = new Date();
    const endTime = new Date(auction.end_time);
    
    // If auction is marked as active but end time has passed, mark as ended
    if (auction.status === 'active' && now > endTime) {
      return {
        ...auction,
        status: 'ended' as const
      };
    }
    
    return auction;
  };

  // Fetch user's auctions
  useEffect(() => {
    const fetchAuctions = async () => {
      if (!user?.id || is_job || isCompanyDashboard) return; // Only for shop mode user dashboard
      setLoadingAuctions(true);
      setAuctionsError(null);
      try {
        const userAuctions = await AuctionService.getAuctionsByUser(user.id);
        // Update auction statuses based on current time
        const updatedAuctions = userAuctions.map(updateAuctionStatus);
        setAuctions(updatedAuctions);
      } catch (e: any) {
        console.error('Error fetching auctions:', e);
        setAuctionsError(e?.message || t('dashboard.favorites.error.load'));
      } finally {
        setLoadingAuctions(false);
      }
    };
    fetchAuctions();
  }, [user?.id, is_job, isCompanyDashboard]);

  // Filter auctions when filter changes
  useEffect(() => {
    // Update auction statuses before filtering
    const updatedAuctions = auctions.map(updateAuctionStatus);
    const filtered = filterAuctions(updatedAuctions, auctionFilter);
    setFilteredAuctions(filtered);
  }, [auctions, auctionFilter]);

  // Dynamic translation for auction product names/descriptions
  useEffect(() => {
    const run = async () => {
      if (!autoTranslate || language === 'en' || filteredAuctions.length === 0) {
        setTranslatedAuctionNames({});
        setTranslatedAuctionDescriptions({});
        return;
      }
      try {
        const nameEntries = await Promise.all(
          filteredAuctions.map(async (a) => {
            const tr = await translateText(a.product.name || 'Untitled', language as any, 'en');
            return [a.id, tr] as const;
          })
        );
        const descEntries = await Promise.all(
          filteredAuctions.map(async (a) => {
            const text = a.product.description || '';
            const tr = text ? await translateText(text, language as any, 'en') : '';
            return [a.id, tr] as const;
          })
        );
        const nameMap: Record<string, string> = {};
        const descMap: Record<string, string> = {};
        nameEntries.forEach(([id, v]) => (nameMap[id] = v));
        descEntries.forEach(([id, v]) => (descMap[id] = v));
        setTranslatedAuctionNames(nameMap);
        setTranslatedAuctionDescriptions(descMap);
      } catch {
        setTranslatedAuctionNames({});
        setTranslatedAuctionDescriptions({});
      }
    };
    run();
  }, [filteredAuctions, language, autoTranslate]);

  // Refresh applications function for meeting creation
  const refreshApplications = async () => {
    if (!user?.id) return;
    setLoadingOffers(true);
    try {
      if (isCompanyDashboard) {
        // Company dashboard: refresh received applications
        const receivedApps = await ApplicationService.getReceivedByCompany(companyId!);
        setApplicationsReceived(receivedApps as any);
      } else if (is_job) {
        // Job mode: refresh submitted applications
        const submittedResult = await ApplicationService.getSubmittedByUser(
          user.id, 
          currentPage, 
          applicationsPerPage, 
          showAIGeneratedOnly,
          applicationStatusFilter
        );
        setApplicationsSubmitted(submittedResult.data);
      }
    } catch (e: any) {
      console.error('Error refreshing applications:', e);
    } finally {
      setLoadingOffers(false);
    }
  };

  // Fetch inquiries data
  useEffect(() => {
    const fetchInquiries = async () => {
      if (!user?.id || !is_job || isCompanyDashboard) return;
      
      setLoadingInquiries(true);
      setInquiriesError(null);
      
      try {
        // Fetch both received and sent inquiries
        const [receivedInquiries, sentInquiries] = await Promise.all([
          InquiryService.getInquiriesForProfile(user.id),
          InquiryService.getInquiriesByUser(user.id)
        ]);
        
        // Combine and deduplicate inquiries
        const allInquiries = [...receivedInquiries, ...sentInquiries];
        const uniqueInquiries = allInquiries.filter((inquiry, index, self) => 
          index === self.findIndex(i => i.id === inquiry.id)
        );
        
        setInquiries(uniqueInquiries);
        
        // Load profile pictures for inquiries
        const profilePicturePromises = uniqueInquiries.map(async (inquiry) => {
          const targetUserId = inquiriesView === 'received' ? inquiry.requester_user_id : inquiry.profile_user_id;
          try {
            console.log('Loading profile picture for user:', targetUserId);
            
            // Try to get profile images
            const images = await ProfileImageService.getUserProfileImages(targetUserId);
            console.log('Profile images result:', images);
            
            if (images?.profile_picture_url) {
              return { inquiryId: inquiry.id, profilePicture: images.profile_picture_url };
            }
            
            // If no profile image, try to get user data to see if there's an avatar_url
            try {
              const userData = await UserService.getUserProfile(targetUserId);
              console.log('User data for profile picture:', userData);
              if (userData.avatar_url) {
                return { inquiryId: inquiry.id, profilePicture: userData.avatar_url };
              }
            } catch (userError) {
              console.log('No user data available for profile picture');
            }
            
            return { inquiryId: inquiry.id, profilePicture: null };
          } catch (error) {
            console.error('Error loading profile picture for inquiry:', error);
            return { inquiryId: inquiry.id, profilePicture: null };
          }
        });
        
        const profilePictures = await Promise.all(profilePicturePromises);
        console.log('All profile pictures loaded:', profilePictures);
        const profilePictureMap = profilePictures.reduce((acc, { inquiryId, profilePicture }) => {
          if (profilePicture) {
            acc[inquiryId] = profilePicture;
          }
          return acc;
        }, {} as Record<string, string>);
        
        console.log('Profile picture map:', profilePictureMap);
        setInquiryProfilePictures(profilePictureMap);
      } catch (error) {
        console.error('Error fetching inquiries:', error);
        setInquiriesError('Failed to load inquiries');
      } finally {
        setLoadingInquiries(false);
      }
    };
    
    fetchInquiries();
  }, [user?.id, is_job, isCompanyDashboard]);

  // Fetch user products and alias on component mount
  useEffect(() => {
    const fetchUserData = async () => {
      if (!user) return;

      try {
        await initSupabase();
        ensureSupabaseConfigured();

        setLoading(true);
        if (!userAlias) {
          setLoadingAlias(true);
        }
        setError(null);

        // Fetch alias and profile images in parallel, then load products separately
        const [alias, profileImages] = await Promise.all([
          UserService.getCurrentUserAlias(user.id),
          ProfileImageService.getUserProfileImages(user.id)
        ]);

        console.log('🔍 Dashboard data fetch:', {
          isCompanyDashboard,
          timestamp: new Date().toISOString()
        });

        setProfileImages(profileImages);
        // Load website settings to check upload target (Backblaze)
        try {
          const res = await fetch('/website-settings.json', { cache: 'no-store' });
          if (res.ok) {
            const ws = await res.json();
            const enabled = ws?.uploadTarget === 'backblaze';
            setB2Enabled(!!enabled);
          }
        } catch {}
        // Resolve avatar with Backblaze first, then Supabase storage, then DB URL
        try {
          let resolved: string | undefined;
          if (b2Enabled) {
            try {
              const { data: { session } } = await supabase.auth.getSession();
              const payload: any = { signRead: true, maxKeys: 100 };
              const resp = await fetch('/api/b2-list.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', ...(session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : {}) },
                body: JSON.stringify(payload)
              });
              const raw = await resp.text();
              let json: any = null; try { json = raw ? JSON.parse(raw) : null; } catch {}
              if (resp.ok && json?.items) {
                const items = json.items as Array<{ key: string; url?: string }>;
                const mine = items.filter(it => it.key && it.key.startsWith(`profile-pictures/${user.id}/`));
                if (mine.length > 0) {
                  // Prefer the last item (assumed newest)
                  const last = mine[mine.length - 1];
                  resolved = last.url;
                }
              }
            } catch {}
          }
          if (!resolved) {
            // Try Supabase storage bucket 'profile-pictures' under user folder
            try {
              const list = await supabase.storage.from('profile-pictures').list(user.id, { limit: 100, sortBy: { column: 'created_at', order: 'desc' } } as any);
              const files = (list?.data || []).filter((f: any) => !!f?.name);
              if (files.length > 0) {
                const latest = files[0].name;
                const { data: pub } = supabase.storage.from('profile-pictures').getPublicUrl(`${user.id}/${latest}`);
                resolved = pub?.publicUrl;
              }
            } catch {}
          }
          if (!resolved && profileImages?.profile_picture_url) resolved = profileImages.profile_picture_url;
          if (!resolved && (user as any)?.avatar) resolved = (user as any).avatar;
          setAvatarUrl(resolved);
          if (resolved) {
            cacheAvatar(user.id, resolved);
          }
        } catch {}
        if (alias) {
          setUserAlias(alias);
          cacheAlias(user.id, alias);
        }
        
        // Load products with pagination
        await loadProducts(1, productsPerPage);
      } catch (err) {
        console.error('Error fetching user data:', err);
        const message = err instanceof Error ? err.message : 'Failed to fetch user data';
        setError(message);
        toast.error(message);
      } finally {
        setLoading(false);
        setLoadingAlias(false);
      }
    };

    fetchUserData();
  }, [user, isCompanyDashboard]);

  // Reset pagination when products per page changes
  useEffect(() => {
    if (productsPerPage !== 6) { // Only reset if not default
      setProductsCurrentPage(1);
      loadProducts(1, productsPerPage);
    }
  }, [productsPerPage]);

  // Clear cache when user changes or component unmounts
  useEffect(() => {
    return () => {
      clearCache();
    };
  }, [user?.id]);

  // Clear cache when switching between company and regular dashboard
  useEffect(() => {
    clearCache();
  }, [isCompanyDashboard]);

  useEffect(() => {
    const loadCompanies = async () => {
      if (!is_job || isCompanyDashboard || !user?.id) return;
      try {
        setDashboardCompaniesLoading(true);
        setDashboardCompaniesError(null);
        const [companies, memberMeta] = await Promise.all([
          CompanyService.getUserCompanies(user.id),
          supabase
            .from('company_members')
            .select('company_id, role, job_title, department')
            .eq('user_id', user.id)
            .eq('is_active', true),
        ]);

        const metaMap: Record<string, { role?: string; job_title?: string | null; department?: string | null }> = {};
        const memberRows = (memberMeta as any)?.data || [];
        memberRows.forEach((row: any) => {
          if (!row?.company_id) return;
          metaMap[row.company_id] = {
            role: row.role,
            job_title: row.job_title ?? null,
            department: row.department ?? null,
          };
        });

        const sorted = [...(companies || [])].sort((a, b) => a.name.localeCompare(b.name));
        setDashboardCompanies(sorted);
        setCompanyMemberMetaById(metaMap);
      } catch (e) {
        setDashboardCompaniesError(e instanceof Error ? e.message : 'Failed to load companies');
      } finally {
        setDashboardCompaniesLoading(false);
      }
    };
    loadCompanies();
  }, [is_job, isCompanyDashboard, user?.id]);

  // Handle URL parameters for company dashboard
  useEffect(() => {
    const companyParam = searchParams.get('company');
    const nameParam = searchParams.get('name');
    
    if (companyParam && nameParam) {
      setCompanyId(companyParam);
      setCompanyName(decodeURIComponent(nameParam));
    } else {
      setCompanyId(null);
      setCompanyName(null);
    }
  }, [searchParams]);

  // If no company is passed via URL, try to infer the user's company for company dashboard mode
  useEffect(() => {
    const fetchUserCompany = async () => {
      if (!user?.id) return;
      if (companyId) return; // already set via URL
      try {
        // First, try users.company_id
        const { data: userRow } = await supabase
          .from('users')
          .select('company_id')
          .eq('id', user.id)
          .single();
        const cid = userRow?.company_id;
        if (cid) {
          const { data: company } = await supabase
            .from('companies')
            .select('id, name')
            .eq('id', cid)
            .single();
          if (company?.id) {
            setCompanyId(company.id);
            setCompanyName(company.name || 'Company');
          }
        }
      } catch (err) {
        // silently ignore
      }
    };
    fetchUserCompany();
  }, [user?.id, companyId]);

  // Handle product deletion
  const handleDeleteProduct = async (productId: string, productName: string) => {
    setDeleteProduct({ id: productId, name: productName });
  };

  // State for delete confirmation
  const [deleteProduct, setDeleteProduct] = useState<{ id: string; name: string } | null>(null);
  const [deletingProduct, setDeletingProduct] = useState(false);
  // IDs to hide immediately from the UI (optimistic removal)
  const [hiddenProductIds, setHiddenProductIds] = useState<string[]>([]);

  const confirmDeleteProduct = async () => {
    if (!deleteProduct) return;
    
    setDeletingProduct(true);
    try {
      // Optimistic UI update: remove locally first
      const removedId = deleteProduct.id;
      // Hide immediately in case other state refreshes temporarily overwrite the list
      setHiddenProductIds(prev => prev.includes(removedId) ? prev : [...prev, removedId]);
      const prevList = userProducts;
      const prevCount = productsTotalCount;
      const prevPages = productsTotalPages;
      setUserProducts((list: UserProduct[]) => list.filter((p) => p.id !== removedId));
      setProductsTotalCount((n: number) => Math.max(0, (n || 0) - 1));
      setProductsTotalPages((n: number) => {
        const newCount = Math.max(0, (prevCount || 0) - 1);
        const per = productsPerPage || 6;
        return Math.max(1, Math.ceil(newCount / per));
      });
      // Also remove from cache immediately so pagination won't bring it back
      removeFromCache(removedId);

      await UserProductService.deleteProduct(deleteProduct.id);
      // Invalidate cache and refresh current page
      invalidateCache();
      // If current page becomes empty after deletion, go to previous page
      const per = productsPerPage || 6;
      const newCount = Math.max(0, (prevCount || 0) - 1);
      const newTotalPages = Math.max(1, Math.ceil(newCount / per));
      const nextPage = Math.min(productsCurrentPage, newTotalPages);
      await loadProducts(nextPage, productsPerPage);
      toast.success('Product deleted successfully');
      setDeleteProduct(null);
    } catch (err) {
      console.error('Error deleting product:', err);
      // Rollback UI on failure
      try {
        // Restore previous list and counters
        // @ts-ignore
        setUserProducts(prevList);
        // @ts-ignore
        setProductsTotalCount(prevCount);
        // @ts-ignore
        setProductsTotalPages(prevPages);
        // Unhide on failure
        setHiddenProductIds(prev => prev.filter(id => id !== (deleteProduct?.id || '')));
      } catch {}
      toast.error('Failed to delete product');
    } finally {
      setDeletingProduct(false);
    }
  };

  // Handle product status toggle
  const handleToggleStatus = async (productId: string, currentStatus: boolean) => {
    try {
      await UserProductService.toggleProductStatus(productId, !currentStatus);
      // Invalidate cache and refresh current page
      invalidateCache();
      await loadProducts(productsCurrentPage, productsPerPage);
      toast.success(`Product ${!currentStatus ? 'activated' : 'deactivated'} successfully`);
    } catch (err) {
      console.error('Error toggling product status:', err);
      toast.error('Failed to update product status');
    }
  };

  // Handle product menu actions
  const handleProductMenuOpen = (event: React.MouseEvent<HTMLElement>, product: UserProduct) => {
    setProductMenuAnchor(event.currentTarget);
    setSelectedProduct(product);
  };

  const handleProductMenuClose = () => {
    setProductMenuAnchor(null);
    setSelectedProduct(null);
  };

  const handleProductUpdate = async (updates: Partial<UserProduct>) => {
    if (!selectedProduct) return;
    
    setUpdatingProduct(true);
    try {
      const { error } = await supabase
        .from('products')
        .update(updates)
        .eq('id', selectedProduct.id)
        .eq('seller_id', user?.id);

      if (error) throw error;

      // Invalidate cache and refresh current page to show updated data
      invalidateCache();
      await loadProducts(productsCurrentPage, productsPerPage);
      
      toast.success('Product updated successfully');
      handleProductMenuClose();
    } catch (err) {
      console.error('Error updating product:', err);
      toast.error('Failed to update product');
    } finally {
      setUpdatingProduct(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'delivered': return 'success';
      case 'shipped': return 'info';
      case 'pending': return 'warning';
      case 'cancelled': return 'error';
      default: return 'default';
    }
  };

  const getStatusIcon = (status: string, isOffer: boolean = false): React.ReactElement | undefined => {
    switch (status) {
      case 'pending':
        return <PendingIcon />;
      case 'succeeded':
        return <CheckIcon />;
      case 'cancelled':
        return <CloseIcon />;
      default:
        return undefined;
    }
  };

  const getStatusLabel = (status: string, isOffer: boolean = false) => {
    switch (status) {
      case 'succeeded':
        return 'Transaction Complete';
      case 'pending':
        // For offers, pending means waiting for seller response, not payment required
        return isOffer ? 'Pending' : 'Transaction Required, Please Pay';
      case 'requires_action':
        return 'Action Required';
      case 'refunded':
        return 'Refunded';
      case 'failed':
        return 'Payment Failed';
      case 'cancelled':
        return 'Cancelled';
      default:
        return status.charAt(0).toUpperCase() + status.slice(1);
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'grey.50' }}>
      
      {/* Header Section */}
      <Box
        className={`dashboard-background ${currentTheme}`}
        sx={{
          background: 'var(--dashboard-background, linear-gradient(135deg, #667eea 0%, #764ba2 100%))',
          color: 'white',
          pt: 6,
          pb: { xs: 5, md: 4 },
          transition: 'background 0.5s ease-in-out',
          position: 'relative',
          overflow: 'hidden',
          zIndex: 10,
        }}
      >
        <Container maxWidth="lg">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            {/* Header with Logo and Theme Switcher */}
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center', 
              mb: 4,
              position: 'relative'
            }}>
              {/* Company Dashboard Title (Left) */}
              {isCompanyDashboard && (
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 2,
                  position: 'absolute',
                  left: 0,
                  top: 0,
                  zIndex: 20
                }}>
                  <Button
                    onClick={() => navigate('/dashboard')}
                    sx={{ 
                      color: 'white',
                      minWidth: 'auto',
                      px: 1,
                      '&:hover': {
                        backgroundColor: 'rgba(255, 255, 255, 0.1)'
                      }
                    }}
                  >
                    ← Back to Dashboard
                  </Button>
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 1,
                    background: 'rgba(255, 255, 255, 0.1)',
                    borderRadius: 2,
                    px: 2,
                    py: 1,
                    backdropFilter: 'blur(10px)'
                  }}>
                    <BusinessIcon sx={{ color: 'white' }} />
                    <Typography variant="h6" sx={{ color: 'white', fontWeight: 'bold' }}>
                      Company Dashboard
                    </Typography>
                    <Typography variant="h6" sx={{ color: 'white', opacity: 0.8 }}>
                      {companyName}
                    </Typography>
                  </Box>
                </Box>
              )}
              
              {/* Logo */}
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1, flex: 1 }}>
                {is_job ? (
                  <Typography variant="h4" component="div" sx={{ fontWeight: 800, letterSpacing: 0.4, color: 'white' }}>
                    JobFinder
                  </Typography>
                ) : (
                  <>
                    <img src="/sistasouk.png" alt="Sistasouk" style={{ height: 40 }} />
                    <img src="/sistasouk_font.png" alt="Sistasouk wordmark" style={{ height: 36 }} />
                  </>
                )}
              </Box>
              
              {/* Theme Switcher and Settings Button */}
              <Box sx={{ position: 'absolute', right: 0, top: 0, zIndex: 20, display: 'flex', alignItems: 'center', gap: 1 }}>
                {/* Settings Button */}
                <Button
                  variant="contained"
                  startIcon={<SettingsIcon />}
                  onClick={() => setSettingsOpen(true)}
                  sx={{
                    background: 'rgba(255, 255, 255, 0.2)',
                    backdropFilter: 'blur(10px)',
                    border: '2px solid rgba(255, 255, 255, 0.3)',
                    borderRadius: 2,
                    px: 2,
                    py: 1,
                    textTransform: 'none',
                    fontWeight: 'bold',
                    color: 'white',
                    fontSize: '0.9rem',
                    '&:hover': {
                      background: 'rgba(255, 255, 255, 0.3)',
                      border: '2px solid rgba(255, 255, 255, 0.5)',
                      transform: 'translateY(-2px)',
                      boxShadow: '0 6px 20px rgba(255, 255, 255, 0.2)',
                    },
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  }}
                >
                  {t('dashboard.hero.settings')}
                </Button>
                <ThemeSwitcher 
                  currentTheme={currentTheme} 
                  onThemeChange={changeTheme} 
                />
              </Box>
            </Box>
            
            {/* Beautiful Header Container */}
            <Card sx={{ 
              mb: { xs: 3, md: 2 },
              borderRadius: 4,
              background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.05) 100%)',
              backdropFilter: 'blur(20px)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
              overflow: 'hidden',
              position: 'relative',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.05) 100%)',
                borderRadius: 4,
                zIndex: 1,
              }
            }}>
              <CardContent sx={{ p: { xs: 2.5, md: 4 }, position: 'relative', zIndex: 2 }}>
            
                <Box sx={{ 
                  display: 'grid',
                  gridTemplateColumns: { xs: '80px 1fr', sm: '96px 1fr', md: 'auto 1fr' },
                  alignItems: 'center',
                  gap: { xs: 2, md: 4 },
                  justifyContent: 'center',
                  textAlign: { xs: 'left', md: 'left' }
                }}>
                  <Box sx={{ display: 'flex', justifyContent: { xs: 'center', md: 'flex-start' } }}>
                    <UserAvatar
                      src={avatarUrl || profileImages?.profile_picture_url || user?.avatar}
                      name={user?.name}
                      email={user?.email}
                      sx={{
                        width: { xs: 72, sm: 88, md: 120 },
                        height: { xs: 72, sm: 88, md: 120 },
                        border: '4px solid rgba(255, 255, 255, 0.3)',
                        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
                        backdropFilter: 'blur(10px)',
                        borderRadius: '50%',
                        cursor: 'pointer'
                      }}
                      onClick={() => setProfileImageOpen(true)}
                    />
                  </Box>
                  <Typography 
                    variant="h4" 
                    fontWeight="bold" 
                    gutterBottom 
                    sx={{ 
                      color: 'white', 
                      fontSize: { xs: '1.6rem', sm: '1.9rem', md: '2.125rem' }, 
                      lineHeight: 1.2,
                      gridColumn: { xs: '2', md: '2' }
                    }}
                  >
                    {(() => {
                      const text = isCompanyDashboard
                        ? t('dashboard.hero.welcomeCompany').replace('{company}', companyName || '')
                        : t('dashboard.hero.welcomeBack').replace('{name}', user?.name || '');
                      const idx = text.indexOf(',');
                      if (idx !== -1) {
                        return (
                          <>
                            <span>{text.slice(0, idx + 1)}</span>
                            <Box component="br" sx={{ display: { xs: 'none', md: 'block' } }} />
                            <span>{text.slice(idx + 1).trimStart()}</span>
                          </>
                        );
                      }
                      return text;
                    })()}
                  </Typography>
                  <Typography 
                    variant="h6" 
                    sx={{ 
                      opacity: 0.9, 
                      mb: { xs: 2, md: 3 }, 
                      color: 'white', 
                      fontSize: { xs: '1rem', sm: '1.1rem', md: '1.25rem' }, 
                      display: 'block', 
                      whiteSpace: 'normal', 
                      wordBreak: 'break-word',
                      gridColumn: '1 / -1'
                    }}
                  >
                      {isCompanyDashboard 
                        ? t('dashboard.hero.subtitle.company').replace('{company}', companyName || '')
                        : t('dashboard.hero.subtitle.user')
                      }
                    </Typography>
                
                {/* User Info Display - Compact chips on a single line */}
                <Box sx={{ 
                  gridColumn: '1 / -1',
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: { xs: 1, sm: 2 },
                  flexWrap: 'wrap',
                  overflow: 'hidden',
                  minWidth: 0,
                }}>
                  {/* User Alias Display */}
                  {userAlias ? (
                    <Box 
                      onClick={() => setUserInfoOpen(true)}
                      sx={{ 
                        display: 'inline-flex', 
                        alignItems: 'center', 
                        gap: 1,
                        background: 'rgba(255, 255, 255, 0.15)',
                        backdropFilter: 'blur(10px)',
                        borderRadius: 3,
                        px: 2,
                        py: 1,
                        border: '1px solid rgba(255, 255, 255, 0.2)',
                        boxShadow: '0 4px 15px rgba(0, 0, 0, 0.1)',
                        flexShrink: 0,
                        minWidth: 'fit-content',
                        width: { xs: '100%', sm: 'auto' },
                        justifyContent: 'space-between',
                        cursor: 'pointer',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          background: 'rgba(255, 255, 255, 0.25)',
                          transform: 'translateY(-1px)',
                          boxShadow: '0 6px 20px rgba(0, 0, 0, 0.15)',
                        },
                      }}>
                      <Typography variant="body2" sx={{ 
                        fontWeight: 600, 
                        opacity: 0.9,
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                        fontSize: { xs: '0.72rem', sm: '0.8rem' },
                        whiteSpace: 'nowrap',
                      }}>
                        {t('dashboard.hero.yourAlias')}
                      </Typography>
                      <Typography variant="body1" sx={{ 
                        fontWeight: 'bold',
                        color: 'white',
                        background: 'linear-gradient(45deg, #fff 30%, #f0f0f0 90%)',
                        backgroundClip: 'text',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        fontSize: { xs: '0.95rem', sm: '1rem' },
                        letterSpacing: '0.5px',
                        whiteSpace: 'normal',
                        wordBreak: 'break-all',
                      }}>
                        @{userAlias}
                      </Typography>
                    </Box>
                  ) : loadingAlias ? (
                    <></>
                  ) : (
                    <Box sx={{ 
                      display: 'inline-flex', 
                      alignItems: 'center', 
                      gap: 1,
                      background: 'rgba(255, 255, 255, 0.1)',
                      borderRadius: 3,
                      px: 2,
                      py: 1,
                      border: '1px solid rgba(255, 255, 255, 0.2)',
                      flexShrink: 0,
                      minWidth: 'fit-content',
                      width: { xs: '100%', sm: 'auto' },
                      justifyContent: 'space-between',
                    }}>
                      <Typography variant="body2" sx={{ 
                        opacity: 0.8,
                        fontStyle: 'italic',
                        fontSize: { xs: '0.8rem', sm: '0.9rem' },
                        whiteSpace: 'nowrap',
                      }}>
                        {t('dashboard.hero.noAlias')}
                      </Typography>
                    </Box>
                  )}

                  {/* Email display */}
                  {user?.email && (
                    <Box 
                      onClick={() => setUserInfoOpen(true)}
                      sx={{ 
                        display: 'inline-flex', 
                        alignItems: 'center', 
                        gap: 1,
                        background: 'rgba(255, 255, 255, 0.15)',
                        backdropFilter: 'blur(10px)',
                        borderRadius: 3,
                        px: 2,
                        py: 1,
                        border: '1px solid rgba(255, 255, 255, 0.2)',
                        boxShadow: '0 4px 15px rgba(0, 0, 0, 0.1)',
                        flexShrink: 0,
                        minWidth: 'fit-content',
                        width: { xs: '100%', sm: 'auto' },
                        justifyContent: 'space-between',
                        cursor: 'pointer',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          background: 'rgba(255, 255, 255, 0.25)',
                          transform: 'translateY(-1px)',
                          boxShadow: '0 6px 20px rgba(0, 0, 0, 0.15)',
                        },
                      }}>
                      <Typography variant="body2" sx={{ 
                        fontWeight: 600, 
                        opacity: 0.9,
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                        fontSize: { xs: '0.72rem', sm: '0.8rem' },
                        whiteSpace: 'nowrap',
                      }}>
                        {t('dashboard.hero.email')}
                      </Typography>
                      <Typography variant="body1" sx={{ 
                        fontWeight: 600,
                        color: 'white',
                        fontSize: { xs: '0.9rem', sm: '0.95rem' },
                        letterSpacing: '0.2px',
                        whiteSpace: 'normal',
                        wordBreak: 'break-all',
                      }}>
                        {user.email}
                      </Typography>
                    </Box>
                  )}
                </Box>

                {/* Right-side vertical actions (desktop) */}
                <Box
                  sx={{
                    position: 'absolute',
                    right: { md: 16 },
                    top: { md: '50%' },
                    transform: { md: 'translateY(-50%)' },
                    display: { xs: 'none', md: 'flex' },
                    flexDirection: 'column',
                    gap: 1,
                    zIndex: 3,
                    alignItems: 'stretch',
                  }}
                >
                  <Button
                    variant="contained"
                    startIcon={<VerifiedIcon />}
                    onClick={() => (isVerified ? setVerificationInfoOpen(true) : setVerificationOpen(true))}
                    sx={{
                      background: 'rgba(255, 255, 255, 0.2)',
                      backdropFilter: 'blur(10px)',
                      border: '2px solid rgba(255, 255, 255, 0.3)',
                      borderRadius: 3,
                      px: 2.5,
                      py: 1,
                      textTransform: 'none',
                      fontWeight: 'bold',
                      color: isVerified ? '#12B76A' : 'white',
                      fontSize: '0.95rem',
                      '&:hover': {
                        background: 'rgba(255, 255, 255, 0.3)',
                        border: '2px solid rgba(255, 255, 255, 0.5)',
                        transform: 'translateY(-2px)',
                        boxShadow: '0 6px 20px rgba(255, 255, 255, 0.2)',
                      },
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    }}
                  >
                    {isVerified ? 'Verified' : 'Verifizierung'}
                  </Button>
                  <Button
                    variant="contained"
                    startIcon={<MessageIcon />}
                    onClick={() => setMessagesSidebarOpen(true)}
                    sx={{
                      background: 'rgba(255, 255, 255, 0.2)',
                      backdropFilter: 'blur(10px)',
                      border: '2px solid rgba(255, 255, 255, 0.3)',
                      borderRadius: 3,
                      px: 2.5,
                      py: 1,
                      textTransform: 'none',
                      fontWeight: 'bold',
                      color: 'white',
                      fontSize: '0.95rem',
                      '&:hover': {
                        background: 'rgba(255, 255, 255, 0.3)',
                        border: '2px solid rgba(255, 255, 255, 0.5)',
                        transform: 'translateY(-2px)',
                        boxShadow: '0 6px 20px rgba(255, 255, 255, 0.2)',
                      },
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    }}
                  >
                    {t('dashboard.hero.buttons.messages')}
                  </Button>
                  <Button
                    variant="contained"
                    startIcon={<SupportAgentIcon />}
                    onClick={() => navigate('/kundencenter')}
                    sx={{
                      background: 'rgba(255, 255, 255, 0.2)',
                      backdropFilter: 'blur(10px)',
                      border: '2px solid rgba(255, 255, 255, 0.3)',
                      borderRadius: 3,
                      px: 2.5,
                      py: 1,
                      textTransform: 'none',
                      fontWeight: 'bold',
                      color: 'white',
                      fontSize: '0.95rem',
                      '&:hover': {
                        background: 'rgba(255, 255, 255, 0.3)',
                        border: '2px solid rgba(255, 255, 255, 0.5)',
                        transform: 'translateY(-2px)',
                        boxShadow: '0 6px 20px rgba(255, 255, 255, 0.2)',
                      },
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    }}
                  >
                    Kundencenter
                  </Button>
                  <Button
                    variant="contained"
                    startIcon={<ShoppingCartIcon />}
                    onClick={() => navigate('/subscriptions-platform-purchases')}
                    sx={{
                      background: 'rgba(255, 255, 255, 0.2)',
                      backdropFilter: 'blur(10px)',
                      border: '2px solid rgba(255, 255, 255, 0.3)',
                      borderRadius: 3,
                      px: 2.5,
                      py: 1,
                      textTransform: 'none',
                      fontWeight: 'bold',
                      color: 'white',
                      fontSize: '0.95rem',
                      '&:hover': {
                        background: 'rgba(255, 255, 255, 0.3)',
                        border: '2px solid rgba(255, 255, 255, 0.5)',
                        transform: 'translateY(-2px)',
                        boxShadow: '0 6px 20px rgba(255, 255, 255, 0.2)',
                      },
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    }}
                  >
                    Subscriptions & Purchases
                  </Button>
                  <Button
                    variant="contained"
                    startIcon={<PersonIcon />}
                    onClick={() => navigate('/user-profile')}
                    sx={{
                      background: 'rgba(255, 255, 255, 0.2)',
                      backdropFilter: 'blur(10px)',
                      border: '2px solid rgba(255, 255, 255, 0.3)',
                      borderRadius: 3,
                      px: 2.5,
                      py: 1,
                      textTransform: 'none',
                      fontWeight: 'bold',
                      color: 'white',
                      fontSize: '0.95rem',
                      '&:hover': {
                        background: 'rgba(255, 255, 255, 0.3)',
                        border: '2px solid rgba(255, 255, 255, 0.5)',
                        transform: 'translateY(-2px)',
                        boxShadow: '0 6px 20px rgba(255, 255, 255, 0.2)',
                      },
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    }}
                  >
                    {t('dashboard.hero.buttons.profile')}
                  </Button>
                  <Button
                    variant="contained"
                    startIcon={<SettingsIcon />}
                    onClick={() => setSettingsOpen(true)}
                    sx={{
                      background: 'rgba(255, 255, 255, 0.2)',
                      backdropFilter: 'blur(10px)',
                      border: '2px solid rgba(255, 255, 255, 0.3)',
                      borderRadius: 3,
                      px: 2.5,
                      py: 1,
                      textTransform: 'none',
                      fontWeight: 'bold',
                      color: 'white',
                      fontSize: '0.95rem',
                      '&:hover': {
                        background: 'rgba(255, 255, 255, 0.3)',
                        border: '2px solid rgba(255, 255, 255, 0.5)',
                        transform: 'translateY(-2px)',
                        boxShadow: '0 6px 20px rgba(255, 255, 255, 0.2)',
                      },
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    }}
                  >
                    Settings
                  </Button>
                </Box>
                </Box>
              </CardContent>
            </Card>

            {/* Scroll down button (desktop only) */}
            <Box sx={{ display: { xs: 'none', md: 'flex' }, justifyContent: 'center', mb: 2 }}>
              <IconButton
                aria-label={t('common.scrollDown')}
                onClick={handleScrollToMainTabs}
                sx={{
                  width: 44,
                  height: 44,
                  borderRadius: '999px',
                  backgroundColor: 'rgba(255,255,255,0.25)',
                  color: 'white',
                  border: '2px solid rgba(255,255,255,0.35)',
                  backdropFilter: 'blur(8px)',
                  boxShadow: '0 6px 18px rgba(0,0,0,0.2)',
                  '&:hover': { backgroundColor: 'rgba(255,255,255,0.35)' },
                }}
              >
                <ArrowDownwardIcon />
              </IconButton>
            </Box>

            {/* Action Buttons - Only show for regular dashboard, not company dashboard */}
            {!isCompanyDashboard && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.3 }}
              >
                <Box sx={{ 
                  display: { xs: 'grid', md: 'none' },
                  gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(3, 1fr)' },
                  gap: { xs: 1.5, md: 2 },
                  justifyContent: 'center',
                  alignItems: { xs: 'stretch', md: 'center' },
                  flexWrap: { md: 'wrap' },
                  mt: 3,
                  '& > .MuiButton-root': {
                    width: { xs: '100%', md: 'auto' },
                    minHeight: { xs: 44, sm: 48 },
                    borderRadius: { xs: 2, md: 3 },
                    px: { xs: 2, md: 4 },
                    py: { xs: 1, md: 1.5 },
                    fontSize: { xs: '0.95rem', md: '1rem' },
                  }
                }}>
                  <Button
                    variant="contained"
                    startIcon={<MessageIcon />}
                    onClick={() => setMessagesSidebarOpen(true)}
                    sx={{
                      background: 'rgba(255, 255, 255, 0.2)',
                      backdropFilter: 'blur(10px)',
                      border: '2px solid rgba(255, 255, 255, 0.3)',
                      borderRadius: 3,
                      px: 4,
                      py: 1.5,
                      textTransform: 'none',
                      fontWeight: 'bold',
                      color: 'white',
                      fontSize: '1rem',
                      '&:hover': {
                        background: 'rgba(255, 255, 255, 0.3)',
                        border: '2px solid rgba(255, 255, 255, 0.5)',
                        transform: 'translateY(-3px)',
                        boxShadow: '0 8px 25px rgba(255, 255, 255, 0.2)',
                      },
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    }}
                  >
                    {t('dashboard.hero.buttons.messages')}
                  </Button>
                  <Button
                    variant="contained"
                    startIcon={<PersonIcon />}
                    onClick={() => navigate('/user-profile')}
                    sx={{
                      background: 'rgba(255, 255, 255, 0.2)',
                      backdropFilter: 'blur(10px)',
                      border: '2px solid rgba(255, 255, 255, 0.3)',
                      borderRadius: 3,
                      px: 4,
                      py: 1.5,
                      textTransform: 'none',
                      fontWeight: 'bold',
                      color: 'white',
                      fontSize: '1rem',
                      '&:hover': {
                        background: 'rgba(255, 255, 255, 0.3)',
                        border: '2px solid rgba(255, 255, 255, 0.5)',
                        transform: 'translateY(-3px)',
                        boxShadow: '0 8px 25px rgba(255, 255, 255, 0.2)',
                      },
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    }}
                  >
                    {t('dashboard.hero.buttons.profile')}
                  </Button>
                  <Button
                    variant="contained"
                    startIcon={<SupportAgentIcon />}
                    onClick={() => navigate('/kundencenter')}
                    sx={{
                      background: 'rgba(255, 255, 255, 0.2)',
                      backdropFilter: 'blur(10px)',
                      border: '2px solid rgba(255, 255, 255, 0.3)',
                      borderRadius: 3,
                      px: 4,
                      py: 1.5,
                      textTransform: 'none',
                      fontWeight: 'bold',
                      color: 'white',
                      fontSize: '1rem',
                      '&:hover': {
                        background: 'rgba(255, 255, 255, 0.3)',
                        border: '2px solid rgba(255, 255, 255, 0.5)',
                        transform: 'translateY(-3px)',
                        boxShadow: '0 8px 25px rgba(255, 255, 255, 0.2)',
                      },
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    }}
                  >
                    Kundencenter
                  </Button>
                  <Button
                    variant="contained"
                    startIcon={<ShoppingCartIcon />}
                    onClick={() => navigate('/subscriptions-platform-purchases')}
                    sx={{
                      background: 'rgba(255, 255, 255, 0.2)',
                      backdropFilter: 'blur(10px)',
                      border: '2px solid rgba(255, 255, 255, 0.3)',
                      borderRadius: 3,
                      px: 4,
                      py: 1.5,
                      textTransform: 'none',
                      fontWeight: 'bold',
                      color: 'white',
                      fontSize: '1rem',
                      '&:hover': {
                        background: 'rgba(255, 255, 255, 0.3)',
                        border: '2px solid rgba(255, 255, 255, 0.5)',
                        transform: 'translateY(-3px)',
                        boxShadow: '0 8px 25px rgba(255, 255, 255, 0.2)',
                      },
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    }}
                  >
                    Subscriptions & Purchases
                  </Button>
                  {is_job && !isCompanyDashboard && (
                    <Button
                      variant="contained"
                      startIcon={<BusinessIcon />}
                      onClick={() => setMyCompaniesOpen(true)}
                      sx={{
                        background: 'rgba(255, 255, 255, 0.2)',
                        backdropFilter: 'blur(10px)',
                        border: '2px solid rgba(255, 255, 255, 0.3)',
                        borderRadius: 3,
                        px: 4,
                        py: 1.5,
                        textTransform: 'none',
                        fontWeight: 'bold',
                        color: 'white',
                        fontSize: '1rem',
                        '&:hover': {
                          background: 'rgba(255, 255, 255, 0.3)',
                          border: '2px solid rgba(255, 255, 255, 0.5)',
                          transform: 'translateY(-3px)',
                          boxShadow: '0 8px 25px rgba(255, 255, 255, 0.2)',
                        },
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      }}
                    >
                      {t('dashboard.hero.buttons.myCompanies')}
                    </Button>
                  )}
                  {is_job && !isCompanyDashboard && (
                    <Button
                      variant="contained"
                      startIcon={<WorkIcon />}
                      onClick={() => {
                        if (jobApplicationWriterMinimized) {
                          setJobApplicationWriterMinimized(false);
                          setJobApplicationWriterOpen(true);
                        } else {
                          setJobApplicationWriterOpen(true);
                        }
                      }}
                      sx={{
                        background: jobApplicationWriterMinimized 
                          ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)'
                          : 'linear-gradient(135deg, #8b5cf6 0%, #6366f1 100%)',
                        backdropFilter: 'blur(10px)',
                        border: jobApplicationWriterMinimized 
                          ? '2px solid rgba(16, 185, 129, 0.3)'
                          : '2px solid rgba(139, 92, 246, 0.3)',
                        borderRadius: 3,
                        px: 4,
                        py: 1.5,
                        textTransform: 'none',
                        fontWeight: 'bold',
                        color: 'white',
                        fontSize: '1rem',
                        mr: 2,
                        '&:hover': {
                          background: jobApplicationWriterMinimized 
                            ? 'linear-gradient(135deg, #059669 0%, #047857 100%)'
                            : 'linear-gradient(135deg, #7c3aed 0%, #5b21b6 100%)',
                          border: jobApplicationWriterMinimized 
                            ? '2px solid rgba(16, 185, 129, 0.5)'
                            : '2px solid rgba(139, 92, 246, 0.5)',
                          transform: 'translateY(-3px)',
                          boxShadow: jobApplicationWriterMinimized 
                            ? '0 8px 25px rgba(16, 185, 129, 0.3)'
                            : '0 8px 25px rgba(139, 92, 246, 0.3)',
                        },
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      }}
                    >
                      {jobApplicationWriterMinimized 
                        ? t('dashboard.hero.buttons.aiWriterRestore') 
                        : t('dashboard.hero.buttons.aiWriter')}
                    </Button>
                  )}
                  {is_job && !isCompanyDashboard && (
                    <Button
                      variant="contained"
                      startIcon={<WorkIcon />}
                      onClick={() => setJobShuffleOpen(true)}
                      sx={{
                        background: 'rgba(255, 255, 255, 0.2)',
                        backdropFilter: 'blur(10px)',
                        border: '2px solid rgba(255, 255, 255, 0.3)',
                        borderRadius: 3,
                        px: 4,
                        py: 1.5,
                        textTransform: 'none',
                        fontWeight: 'bold',
                        color: 'white',
                        fontSize: '1rem',
                        '&:hover': {
                          background: 'rgba(255, 255, 255, 0.3)',
                          border: '2px solid rgba(255, 255, 255, 0.5)',
                          transform: 'translateY(-3px)',
                          boxShadow: '0 8px 25px rgba(255, 255, 255, 0.2)',
                        },
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      }}
                    >
                      {t('dashboard.hero.buttons.jobShuffle')}
                    </Button>
                  )}
                  <Button
                    variant="contained"
                    startIcon={<SettingsIcon />}
                    onClick={() => setSettingsOpen(true)}
                    sx={{
                      background: 'rgba(255, 255, 255, 0.2)',
                      backdropFilter: 'blur(10px)',
                      border: '2px solid rgba(255, 255, 255, 0.3)',
                      borderRadius: 3,
                      px: 4,
                      py: 1.5,
                      textTransform: 'none',
                      fontWeight: 'bold',
                      color: 'white',
                      fontSize: '1rem',
                      '&:hover': {
                        background: 'rgba(255, 255, 255, 0.3)',
                        border: '2px solid rgba(255, 255, 255, 0.5)',
                        transform: 'translateY(-3px)',
                        boxShadow: '0 8px 25px rgba(255, 255, 255, 0.2)',
                      },
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    }}
                  >
                    Settings
                  </Button>
                </Box>
              </motion.div>
            )}
          </motion.div>
        </Container>
      </Box>

      {/* Floating Scroll-to-Top button */}
      {showScrollTop && (
        <IconButton
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          sx={{
            position: 'fixed',
            right: 24,
            bottom: 24,
            zIndex: 2000,
            width: 48,
            height: 48,
            borderRadius: '50%',
            background: 'rgba(0, 0, 0, 0.2)',
            color: 'white',
            border: '2px solid rgba(255, 255, 255, 0.35)',
            backdropFilter: 'blur(10px)',
            boxShadow: '0 8px 24px rgba(0,0,0,0.25)',
            '&:hover': { background: 'rgba(0, 0, 0, 0.3)' },
          }}
          aria-label={t('common.scrollUp')}
        >
          <ArrowUpwardIcon />
        </IconButton>
      )}

      {/* Profile Image Modal */}
      <ProfileImageModal
        open={profileImageOpen}
        onClose={() => setProfileImageOpen(false)}
        src={avatarUrl || profileImages?.profile_picture_url || (user as any)?.avatar}
        alt={user?.name || 'Profile image'}
      />

      {/* Companies Modal */}
      <MyCompaniesModal open={myCompaniesOpen} onClose={() => setMyCompaniesOpen(false)} />
      {/* Scroll target anchor for main tabs */}
      <Box ref={mainTabsRef} />
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Card sx={{ mb: 3, borderRadius: 3, boxShadow: 2, p: isMobile ? 2 : 0 }}>
          {isMobile ? (
            <FormControl fullWidth size="medium">
              <InputLabel id="dashboard-mobile-tab-select">{t('dashboard.tabs.' + (
                (is_job || isCompanyDashboard) ? 'interviews' : 'orders'
              ))}</InputLabel>
              <Select
                labelId="dashboard-mobile-tab-select"
                value={tabValue}
                label={t('dashboard.tabs.' + ((is_job || isCompanyDashboard) ? 'interviews' : 'orders'))}
                onChange={(e) => {
                  const v = Number(e.target.value);
                  setTabValue(v);
                  localStorage.setItem(DASHBOARD_TAB_STORAGE_KEY, String(v));
                }}
                sx={{
                  borderRadius: 2,
                  bgcolor: 'background.paper',
                  '& .MuiSelect-select': { display: 'flex', alignItems: 'center', gap: 1.5, py: 1.5 },
                }}
              >
                <MenuItem value={0}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {(is_job || isCompanyDashboard) ? <EventIcon /> : <OrdersIcon />}
                    <Typography>{(is_job || isCompanyDashboard) ? t('dashboard.tabs.interviews') : t('dashboard.tabs.orders')}</Typography>
                  </Box>
                </MenuItem>
                <MenuItem value={1}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {(is_job || isCompanyDashboard) ? <InboxIcon /> : <OrdersIcon />}
                    <Typography>{(is_job || isCompanyDashboard) ? t('dashboard.tabs.applications') : t('dashboard.tabs.offers')}</Typography>
                  </Box>
                </MenuItem>
                {!is_job && !isCompanyDashboard && (
                  <MenuItem value={2}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <GavelIcon />
                      <Typography>{t('dashboard.tabs.auctions')}</Typography>
                    </Box>
                  </MenuItem>
                )}
                {is_job && !isCompanyDashboard && (
                  <MenuItem value={2}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <MessageIcon />
                      <Typography>{t('dashboard.tabs.inquiries')}</Typography>
                    </Box>
                  </MenuItem>
                )}
                {!isCompanyDashboard && (
                  <MenuItem value={(is_job && !isCompanyDashboard) ? 3 : (!is_job && !isCompanyDashboard ? 3 : 2)}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <FavoritesIcon />
                      <Typography>{t('dashboard.tabs.favorites')}</Typography>
                    </Box>
                  </MenuItem>
                )}
                {is_job && !isCompanyDashboard && (
                  <MenuItem value={4}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <BusinessIcon />
                      <Typography>Unternehmen</Typography>
                    </Box>
                  </MenuItem>
                )}
                {!(is_job && !isCompanyDashboard) && (
                  <MenuItem value={(() => {
                    if (!isCompanyDashboard && !is_job) {
                      return 4; // orders, offers, auctions, favorites, myProducts
                    }
                    if (isCompanyDashboard || is_job) {
                      return (!isCompanyDashboard ? 4 : 4); // interviews, applications, favorites?, jobs
                    }
                    return 4;
                  })()}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <MyProductsIcon />
                      <Typography>{(isCompanyDashboard || is_job) ? t('dashboard.tabs.jobs') : t('dashboard.tabs.myProducts')}</Typography>
                    </Box>
                  </MenuItem>
                )}
                {!(is_job && !isCompanyDashboard) && (
                  <MenuItem value={(() => {
                    if (!isCompanyDashboard && !is_job) {
                      return 5; // upload product
                    }
                    if (isCompanyDashboard || is_job) {
                      return 5; // upload job
                    }
                    return 5;
                  })()}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <UploadIcon />
                      <Typography>{(is_job || isCompanyDashboard) ? t('dashboard.tabs.upload.job') : t('dashboard.tabs.upload.product')}</Typography>
                    </Box>
                  </MenuItem>
                )}
                <MenuItem value={(() => {
                  if (isCompanyDashboard) {
                    return 6; // documents after upload
                  }
                  if (!is_job && !isCompanyDashboard) {
                    return 6; // documents
                  }
                  return 5;
                })()}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <DocumentsIcon />
                    <Typography>{t('dashboard.tabs.documents')}</Typography>
                  </Box>
                </MenuItem>
                {isCompanyDashboard && (
                  <MenuItem value={7}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <PeopleIcon />
                      <Typography>{t('dashboard.tabs.talentPool')}</Typography>
                    </Box>
                  </MenuItem>
                )}
              </Select>
            </FormControl>
          ) : (
            <Tabs
              value={tabValue}
              onChange={handleTabChange}
              variant={isMobile ? 'scrollable' : 'fullWidth'}
              scrollButtons="auto"
              sx={{
                '& .MuiTab-root': {
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '1rem',
                  py: 2,
                },
              }}
            >
              <Tab
                label={(is_job || isCompanyDashboard) ? t('dashboard.tabs.interviews') : t('dashboard.tabs.orders')}
                icon={(is_job || isCompanyDashboard) ? <EventIcon /> : <OrdersIcon />}
                iconPosition="start"
              />
              <Tab
                label={(is_job || isCompanyDashboard) ? t('dashboard.tabs.applications') : t('dashboard.tabs.offers')}
                icon={(is_job || isCompanyDashboard) ? <InboxIcon /> : <OrdersIcon />}
                iconPosition="start"
              />
              {!is_job && !isCompanyDashboard && (
                <Tab
                  label={t('dashboard.tabs.auctions')}
                  icon={<GavelIcon />}
                  iconPosition="start"
                />
              )}
              {is_job && !isCompanyDashboard && (
                <Tab
                  label={t('dashboard.tabs.inquiries')}
                  icon={<MessageIcon />}
                  iconPosition="start"
                />
              )}
              {!isCompanyDashboard && (
                <Tab
                  label={t('dashboard.tabs.favorites')}
                  icon={<FavoritesIcon />}
                  iconPosition="start"
                />
              )}
              {is_job && !isCompanyDashboard && (
                <Tab
                  label="Unternehmen"
                  icon={<BusinessIcon />}
                  iconPosition="start"
                />
              )}
              {!(is_job && !isCompanyDashboard) && (
                <Tab
                  label={(isCompanyDashboard || is_job) ? t('dashboard.tabs.jobs') : t('dashboard.tabs.myProducts')}
                  icon={<MyProductsIcon />}
                  iconPosition="start"
                />
              )}
              {!(is_job && !isCompanyDashboard) && (
                <Tab
                  label={(is_job || isCompanyDashboard) ? t('dashboard.tabs.upload.job') : t('dashboard.tabs.upload.product')}
                  icon={<UploadIcon />}
                  iconPosition="start"
                />
              )}
              <Tab
                label={t('dashboard.tabs.documents')}
                icon={<DocumentsIcon />}
                iconPosition="start"
              />
              {isCompanyDashboard && (
                <Tab
                  label={t('dashboard.tabs.talentPool')}
                  icon={<PeopleIcon />}
                  iconPosition="start"
                />
              )}
            </Tabs>
          )}
        </Card>


        {/* Tab Panels */}
        {(() => {
          // Compute dynamic tab indices based on which tabs are hidden
          const showInquiries = is_job && !isCompanyDashboard;
          const showAuctions = !is_job && !isCompanyDashboard;
          const showFavorites = !isCompanyDashboard;
          const showCompanies = is_job && !isCompanyDashboard;
          const showProducts = !(is_job && !isCompanyDashboard);
          const showUpload = !(is_job && !isCompanyDashboard);

          // base fixed tabs
          let nextIndex = 2; // after Interviews(0) and Applications(1)
          const inquiriesIndex = showInquiries ? nextIndex++ : -1;
          const auctionsIndex = showAuctions ? nextIndex++ : -1;
          const favoritesIndex = showFavorites ? nextIndex++ : -1;
          const companiesIndex = showCompanies ? nextIndex++ : -1;
          const productsIndex = showProducts ? nextIndex++ : -1;
          const uploadIndex = showUpload ? nextIndex++ : -1;
          const documentsIndex = nextIndex++;
          const talentPoolIndex = isCompanyDashboard ? nextIndex++ : -1;

          // Store on a scoped object for use below
          (window as any).__dashIdx = { inquiriesIndex, auctionsIndex, favoritesIndex, companiesIndex, productsIndex, uploadIndex, documentsIndex, talentPoolIndex };
          return null;
        })()}
        <TabPanel value={tabValue} index={0}>
          {is_job && !isCompanyDashboard ? (
            // Job mode - User Dashboard: Show accepted applications
            <>
              <Typography variant="h5" fontWeight="bold" gutterBottom>
                Accepted Applications
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                Congratulations! Here are your accepted job applications. 🎉
              </Typography>
              
              <AcceptedApplicationsList
                applications={applicationsSubmitted}
                loading={loadingOffers}
                onViewApplication={(application) => {
                  setSelectedApplication(application);
                  setApplicationModalOpen(true);
                }}
              />
            </>
          ) : isCompanyDashboard ? (
            // Company Dashboard: Show accepted applications
            <>
              <Typography variant="h5" fontWeight="bold" gutterBottom>
                Interviews
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                Track your accepted job applications and candidate interviews
              </Typography>
              
              <CompanyAcceptedApplications
                applications={applicationsReceived}
                loading={loadingOffers}
                onRefreshApplications={refreshApplications}
                onViewApplication={(application) => {
                  setSelectedApplication(application);
                  setApplicationModalOpen(true);
                }}
                onContactApplicant={(application) => {
                  // Navigate to messages or contact the applicant
                  navigate('/messages');
                }}
                onViewPosting={(application) => {
                  if (application.product?.id) {
                    navigate(`/product/${application.product.id}`);
                  }
                }}
              />
            </>
          ) : (
            // Shop mode: Show orders
            <>
              <Typography variant="h5" fontWeight="bold" gutterBottom>
                {t('dashboard.orders.title')}
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                {t('dashboard.orders.subtitle')}
              </Typography>

              <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center', justifyContent: 'space-between', flexWrap: { xs: 'wrap', md: 'nowrap' } }}>
                <Box
                  sx={{
                    p: 0.5,
                    backgroundColor: 'rgba(0,0,0,0.04)',
                    borderRadius: 999,
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr 1fr', md: 'auto auto' },
                    gap: 0.5,
                    width: { xs: '100%', md: 'auto' },
                  }}
                >
                  <Button
                    fullWidth
                    variant={ordersView === 'sold' ? 'contained' : 'text'}
                    onClick={() => setOrdersView('sold')}
                    sx={{
                      textTransform: 'none',
                      borderRadius: 999,
                      px: { xs: 2, md: 3 },
                      py: 1,
                      boxShadow: ordersView === 'sold' ? 1 : 0,
                      background: ordersView === 'sold' ? 'linear-gradient(135deg, #667eea 0%, #1ea7fd 100%)' : 'transparent',
                      color: ordersView === 'sold' ? '#fff' : 'text.primary',
                    }}
                  >
                    {t('dashboard.orders.toggle.sold')}
                  </Button>
                  <Button
                    fullWidth
                    variant={ordersView === 'bought' ? 'contained' : 'text'}
                    onClick={() => setOrdersView('bought')}
                    sx={{
                      textTransform: 'none',
                      borderRadius: 999,
                      px: { xs: 2, md: 3 },
                      py: 1,
                      boxShadow: ordersView === 'bought' ? 1 : 0,
                      background: ordersView === 'bought' ? 'linear-gradient(135deg, #667eea 0%, #1ea7fd 100%)' : 'transparent',
                      color: ordersView === 'bought' ? '#fff' : 'text.primary',
                    }}
                  >
                    {t('dashboard.orders.toggle.bought')}
                  </Button>
                </Box>

                {/* Orders Filters */}
                <Card
                  component={motion.div as any}
                  animate={{ width: ordersFilterOpen ? 'auto' as any : (isMobile ? 44 : ('auto' as any)) }}
                  transition={{ duration: 0.25, ease: 'easeInOut' }}
                  sx={{
                    p: { xs: ordersFilterOpen ? 1 : 0.5, md: ordersFilterOpen ? 1.1 : 0.5 },
                    borderRadius: 2,
                    boxShadow: 1,
                    width: { xs: ordersFilterOpen ? '100%' : 'fit-content', md: ordersFilterOpen ? 'auto' : 'fit-content' },
                    overflow: 'hidden',
                  }}
                >
                  <Box
                    sx={{
                      display: 'grid',
                      gap: { xs: 1, sm: 1.25 },
                      alignItems: 'center',
                      minWidth: ordersFilterOpen ? ({ md: 520 } as any) : undefined,
                      gridTemplateColumns: ordersFilterOpen ? { xs: '1fr', sm: 'auto auto auto auto auto' } : { xs: 'auto', sm: 'auto auto' },
                      gridTemplateAreas: {
                        xs: '"chip" "status" "range"',
                        sm: '"chip status min max clear"'
                      }
                    }}
                  >
                    {!ordersFilterOpen && (
                      <Typography variant="body2" color="primary" fontWeight={600} sx={{ display: { xs: 'none', sm: 'block' }, mr: 1 }}>
                        {t('dashboard.offers.filters.title')}
                      </Typography>
                    )}
                    <Chip
                      icon={<FilterListIcon />}
                      color="primary"
                      variant="outlined"
                      sx={{
                        justifySelf: 'start',
                        gridArea: { xs: 'chip', sm: 'chip' },
                        height: 36,
                        minHeight: 36,
                        borderRadius: 999,
                        px: 0,
                        borderColor: 'primary.main',
                        '& .MuiChip-label': { display: 'none' },
                        '& .MuiChip-icon': { m: 0, fontSize: 20, color: 'primary.main' },
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 36,
                        cursor: 'pointer',
                      }}
                      title={t('dashboard.offers.filters.title')}
                      aria-label={t('dashboard.offers.filters.title')}
                      onClick={() => setOrdersFilterOpen(v => !v)}
                    />
                    {ordersFilterOpen && (
                      <TextField
                        select
                        fullWidth
                        size="small"
                        label={t('dashboard.offers.filters.allStatus')}
                        value={ordersStatusFilter}
                        onChange={(e) => setOrdersStatusFilter(e.target.value as any)}
                        SelectProps={{ native: true }}
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 }, gridArea: { xs: 'status', sm: 'status' }, width: { sm: 200, md: 220 } }}
                      >
                        <option value="all">{t('dashboard.offers.filters.allStatus')}</option>
                        <option value="pending">{t('dashboard.orders.status.pending')}</option>
                        <option value="requires_action">{t('dashboard.orders.status.requires_action')}</option>
                        <option value="succeeded">{t('dashboard.orders.status.succeeded')}</option>
                        <option value="failed">{t('dashboard.orders.status.failed')}</option>
                        <option value="refunded">{t('dashboard.orders.status.refunded')}</option>
                        <option value="cancelled">{t('dashboard.orders.status.cancelled')}</option>
                      </TextField>
                    )}
                    {ordersFilterOpen && (
                      <Box sx={{ display: { xs: 'grid', sm: 'none' }, gridTemplateColumns: { xs: '1fr 1fr auto' }, gap: 1, alignItems: 'center', width: '100%', gridArea: { xs: 'range', sm: 'unset' } }}>
                        <TextField fullWidth size="small" type="number" label={t('dashboard.offers.filters.minPrice')} value={ordersMinPrice} onChange={(e) => setOrdersMinPrice(e.target.value)} sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
                        <TextField fullWidth size="small" type="number" label={t('dashboard.offers.filters.maxPrice')} value={ordersMaxPrice} onChange={(e) => setOrdersMaxPrice(e.target.value)} sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
                        <IconButton size="small" onClick={() => { setOrdersStatusFilter('all'); setOrdersMinPrice(''); setOrdersMaxPrice(''); }} aria-label="clear filters">
                          <CloseIcon />
                        </IconButton>
                      </Box>
                    )}
                    {ordersFilterOpen && (
                      <TextField fullWidth size="small" type="number" label={t('dashboard.offers.filters.minPrice')} value={ordersMinPrice} onChange={(e) => setOrdersMinPrice(e.target.value)} sx={{ display: { xs: 'none', sm: 'block' }, '& .MuiOutlinedInput-root': { borderRadius: 2 }, width: { sm: 120, md: 140 } }} />
                    )}
                    {ordersFilterOpen && (
                      <TextField fullWidth size="small" type="number" label={t('dashboard.offers.filters.maxPrice')} value={ordersMaxPrice} onChange={(e) => setOrdersMaxPrice(e.target.value)} sx={{ display: { xs: 'none', sm: 'block' }, '& .MuiOutlinedInput-root': { borderRadius: 2 }, width: { sm: 120, md: 140 } }} />
                    )}
                    {ordersFilterOpen && (
                      <Box sx={{ justifySelf: 'end', display: { xs: 'none', sm: 'flex' }, alignItems: 'center' }}>
                        <Button startIcon={<CloseIcon />} variant="text" onClick={() => { setOrdersStatusFilter('all'); setOrdersMinPrice(''); setOrdersMaxPrice(''); }} sx={{ textTransform: 'none' }}>
                          {t('dashboard.offers.filters.clear')}
                        </Button>
                      </Box>
                    )}
                  </Box>
                </Card>
              </Box>

              <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 700 }}>
                {ordersView === 'sold' ? t('dashboard.orders.section.sold') : t('dashboard.orders.section.bought')}
              </Typography>
            </>
          )}

          {!is_job && !isCompanyDashboard && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {loadingPurchases ? (
              <Card sx={{ borderRadius: 2, boxShadow: 1 }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Skeleton variant="rectangular" width={120} height={120} />
                    <Box sx={{ flex: 1 }}>
                      <Skeleton variant="text" height={32} width="40%" />
                      <Skeleton variant="text" height={24} width="25%" />
                      <Skeleton variant="text" height={20} width="80%" />
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            ) : purchasesError ? (
              <Alert severity="error">{purchasesError}</Alert>
            ) : (
              (() => {
                const filteredPurchases = purchases.filter(p => (ordersView === 'sold' ? p.seller_id === user?.id : p.buyer_id === user?.id));
                
                if (filteredPurchases.length === 0) {
                  return (
                    <Card sx={{ borderRadius: 2, boxShadow: 1, textAlign: 'center', py: 6 }}>
                      <CardContent>
                        {ordersView === 'sold' ? (
                          <>
                            <StorefrontIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                            <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
                              {t('dashboard.orders.empty.sold.title')}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                              {t('dashboard.orders.empty.sold.subtitle')}
                            </Typography>
                            <Button
                              variant="contained"
                              startIcon={<UploadIcon />}
                              onClick={() => navigate(isCompanyDashboard ? `/upload-product?company=${companyId}&name=${encodeURIComponent(companyName || '')}` : '/upload-product')}
                              sx={{
                                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                '&:hover': {
                                  background: 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)',
                                },
                              }}
                            >
                              {t('dashboard.orders.empty.sold.cta')}
                            </Button>
                          </>
                        ) : (
                          <>
                            <ShoppingCartIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                            <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
                              {t('dashboard.orders.empty.bought.title')}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                              {t('dashboard.orders.empty.bought.subtitle')}
                            </Typography>
                            <Button
                              variant="contained"
                              startIcon={<StorefrontIcon />}
                              onClick={() => navigate('/products')}
                              sx={{
                                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                '&:hover': {
                                  background: 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)',
                                },
                              }}
                            >
                              {t('dashboard.orders.empty.bought.cta')}
                            </Button>
                          </>
                        )}
                      </CardContent>
                    </Card>
                  );
                }
                
                return filteredPurchases.map((order, index) => (
                  <motion.div
                    key={order.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4, delay: index * 0.1 }}
                  >
                    <Card sx={{ borderRadius: 2, boxShadow: 1, '&:hover': { boxShadow: 3 } }}>
                      <CardContent>
                        <Box sx={{ display: 'flex', alignItems: 'stretch', gap: 2 }}>
                          <Box
                            component="img"
                            src={(order.product_images && order.product_images[0]) || '/placeholder-product.jpg'}
                            alt={order.product_name}
                            sx={{ width: 120, height: 120, borderRadius: 2, objectFit: 'cover', flexShrink: 0 }}
                          />
                          <Box sx={{ flex: 1 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                              <Box>
                                <Typography variant="h6" fontWeight="bold">{translatedOrders[order.id]?.name || order.product_name}</Typography>
                                <Typography variant="body2" color="text.secondary">{new Date(order.created_at).toLocaleDateString(language)}</Typography>
                              </Box>
                              <Box sx={{ textAlign: 'right' }}>
                                {order.paid_status === 'succeeded' ? (
                                  <TransactionStatusLabel
                                    orderId={order.id}
                                    transactionId={(order as any).transaction_id}
                                    paidStatus={order.paid_status}
                                    onClick={() => {
                                      setSelectedOrderForDetails(order.id);
                                      setTransactionDetailsOpen(true);
                                    }}
                                  />
                                ) : (
                                  <Chip
                                    label={t(`dashboard.orders.status.${order.paid_status}`)}
                                    color={(order.paid_status === 'pending' || order.paid_status === 'requires_action' ? 'warning' : order.paid_status === 'refunded' ? 'info' : order.paid_status === 'failed' || order.paid_status === 'cancelled' ? 'error' : 'default') as any}
                                    size="small"
                                    sx={{ mb: 1, textTransform: 'capitalize' }}
                                  />
                                )}
                                <Typography variant="h6" fontWeight="bold" color="primary.main">${order.purchase_price}</Typography>
                              </Box>
                            </Box>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                              {translatedOrders[order.id]?.description || order.product_description || t('dashboard.orders.fallback.noDescription')}
                            </Typography>
                            {/* Order Timeline */}
                            <Box sx={{ mb: 2 }}>
                              <OrderTimeline
                                offerId={order.offer_id}
                                paidStatus={order.paid_status}
                                deliveryStatus={order.delivery_status}
                                trackingNumber={order.tracking_number}
                                orderId={order.id}
                                transactionId={(order as any).transaction_id}
                                onPaymentClick={() => {
                                  if (ordersView === 'bought' && (order.paid_status === 'pending' || order.paid_status === 'requires_action' || order.paid_status === 'failed' || order.paid_status === 'cancelled')) {
                                    handlePayNow(order);
                                    return;
                                  }
                                  setSelectedOrderForDetails(order.id);
                                  setTransactionDetailsOpen(true);
                                }}
                                onDeliveryClick={() => {
                                  if (ordersView !== 'sold') return;
                                  setDeliveryManagerTarget(order);
                                  setDeliveryManagerOpen(true);
                                }}
                                createdAt={order.created_at}
                                completedAt={order.paid_status === 'succeeded' ? order.updated_at : null}
                              />
                            </Box>
                            
                            <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                              <Button variant="outlined" onClick={() => navigate(`/product/${order.product_id}`)}>{t('dashboard.products.button.viewProduct')}</Button>
                              {order.offer_id && (
                                <Button variant="contained" onClick={() => navigate('/messages', { state: { isOffer: true, productId: order.product_id } })}>{t('dashboard.offers.button.openChat')}</Button>
                              )}
                              {/* Leave Review (available 24h after purchase creation and for buyer) */}
                              {ordersView === 'bought' && order.paid_status === 'succeeded' && (
                                (() => {
                                  const createdMs = new Date(order.created_at).getTime();
                                  const elapsedMs = Date.now() - createdMs;
                                  const REQUIRED = 24 * 60 * 60 * 1000; // 24h
                                  const canReview = elapsedMs >= REQUIRED;
                                  const remainingMs = Math.max(0, REQUIRED - elapsedMs);
                                  const hoursLeft = Math.ceil(remainingMs / (60 * 60 * 1000));
                                  return (
                                    <>
                                      <Box
                                        onClick={(e) => {
                                          if (!canReview && !(e as any).shiftKey) return; // allow Shift+Click bypass
                                          setReviewOpen(true);
                                          setReviewTarget(order);
                                        }}
                                      >
                                        <Button 
                                          variant="outlined"
                                          color="secondary"
                                          disabled={!canReview}
                                        >
                                          {t('dashboard.orders.button.leaveReview')}
                                        </Button>
                                      </Box>
                                      {!canReview && (
                                        <Typography variant="body2" color="text.secondary" sx={{ alignSelf: 'center' }}>
                                          {t('dashboard.orders.review.availableAfter').replace('{hours}', String(hoursLeft))}
                                        </Typography>
                                      )}
                                    </>
                                  );
                                })()
                              )}
                              {ordersView === 'bought' && order.paid_status === 'pending' && (
                                <Button 
                                  variant="contained" 
                                  color="primary"
                                  onClick={() => handlePayNow(order)}
                                  sx={{
                                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                    '&:hover': {
                                      background: 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)',
                                    },
                                  }}
                                >
                                  {t('dashboard.orders.button.payNow')}
                                </Button>
                              )}
                            </Box>
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  </motion.div>
                ));
              })()
            )}
            </Box>
          )}
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          <Typography variant="h5" fontWeight="bold" gutterBottom>
            {is_job && !isCompanyDashboard 
              ? t('dashboard.offers.title.jobUser') 
              : (isCompanyDashboard ? t('dashboard.offers.title.company') : t('dashboard.offers.title.user'))}
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
            {is_job && !isCompanyDashboard
              ? t('dashboard.offers.subtitle.jobUser')
              : (isCompanyDashboard 
                  ? t('dashboard.offers.subtitle.company')
                  : t('dashboard.offers.subtitle.user'))}
          </Typography>

          {/* Application Filters - only show for user applications */}
          {is_job && !isCompanyDashboard && (
            <ApplicationFilters
              showAIGeneratedOnly={showAIGeneratedOnly}
              onToggleAIGenerated={(show) => {
                setShowAIGeneratedOnly(show);
                setCurrentPage(1); // Reset to first page when filter changes
              }}
              statusFilter={applicationStatusFilter}
              onStatusFilterChange={(status) => {
                setApplicationStatusFilter(status);
                setCurrentPage(1); // Reset to first page when filter changes
              }}
              onClearFilters={() => {
                setShowAIGeneratedOnly(false);
                setApplicationStatusFilter('all');
                setCurrentPage(1);
              }}
              totalCount={totalApplications}
              filteredCount={totalApplications}
            />
          )}

          <Box sx={{ display: 'flex', gap: 2, mb: 3, alignItems: 'center', justifyContent: 'space-between', flexWrap: { xs: 'wrap', md: 'nowrap' } }}>
            {(isCompanyDashboard || (is_job && !isCompanyDashboard)) ? null : (
              <Box
                sx={{
                  p: 0.5,
                  backgroundColor: 'rgba(0,0,0,0.04)',
                  borderRadius: 999,
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr 1fr', md: 'auto auto' },
                  gap: 0.5,
                  width: { xs: '100%', md: 'auto' },
                }}
              >
                <Button
                  fullWidth
                  variant={ordersTab === 'made' ? 'contained' : 'text'}
                  onClick={() => setOrdersTab('made')}
                  startIcon={<SendIcon />}
                  sx={{
                    textTransform: 'none',
                    borderRadius: 999,
                    px: { xs: 2, md: 3 },
                    py: 1,
                    boxShadow: ordersTab === 'made' ? 1 : 0,
                    background: ordersTab === 'made' ? 'linear-gradient(135deg, #667eea 0%, #1ea7fd 100%)' : 'transparent',
                    color: ordersTab === 'made' ? '#fff' : 'text.primary',
                  }}
                >
                  {t('dashboard.offers.buttons.made')}
                </Button>
                <Button
                  fullWidth
                  variant={ordersTab === 'received' ? 'contained' : 'text'}
                  onClick={() => setOrdersTab('received')}
                  startIcon={<InboxIcon />}
                  sx={{
                    textTransform: 'none',
                    borderRadius: 999,
                    px: { xs: 2, md: 3 },
                    py: 1,
                    boxShadow: ordersTab === 'received' ? 1 : 0,
                    background: ordersTab === 'received' ? 'linear-gradient(135deg, #667eea 0%, #1ea7fd 100%)' : 'transparent',
                    color: ordersTab === 'received' ? '#fff' : 'text.primary',
                  }}
                >
                  {t('dashboard.offers.buttons.received')}
                </Button>
              </Box>
            )}
            {(is_job && !isCompanyDashboard) ? null : (
              <Card
                component={motion.div as any}
                animate={{ width: offersFilterOpen ? 'auto' as any : (isMobile ? 44 : ('auto' as any)) }}
                transition={{ duration: 0.25, ease: 'easeInOut' }}
                sx={{
                  p: { xs: offersFilterOpen ? 1 : 0.5, md: offersFilterOpen ? 1.1 : 0.5 },
                  borderRadius: 2,
                  boxShadow: 1,
                  width: { xs: offersFilterOpen ? '100%' : 'fit-content', md: offersFilterOpen ? 'auto' : 'fit-content' },
                  overflow: 'hidden',
                }}
              >
                <Box
                  sx={{
                    display: 'grid',
                    gap: { xs: 1, sm: 1.25 },
                    alignItems: 'center',
                    minWidth: offersFilterOpen ? ({ md: 520 } as any) : undefined,
                    gridTemplateColumns: offersFilterOpen ? { xs: '1fr', sm: 'auto auto auto auto auto' } : { xs: 'auto', sm: 'auto auto' },
                    gridTemplateAreas: {
                      xs: '"chip" "status" "range"',
                      sm: '"chip status min max clear"'
                    }
                  }}
                >
                  {!offersFilterOpen && (
                    <Typography
                      variant="body2"
                      color="primary"
                      fontWeight={600}
                      sx={{ display: { xs: 'none', sm: 'block' }, mr: 1 }}
                    >
                      {t('dashboard.offers.filters.title')}
                    </Typography>
                  )}
                  <Chip
                    icon={<FilterListIcon />}
                    color="primary"
                    variant="outlined"
                    sx={{
                      justifySelf: 'start',
                      gridArea: { xs: 'chip', sm: 'chip' },
                      height: 36,
                      minHeight: 36,
                      borderRadius: 999,
                      px: 0,
                      borderColor: 'primary.main',
                      '& .MuiChip-label': { display: 'none' },
                      '& .MuiChip-icon': { m: 0, fontSize: 20, color: 'primary.main' },
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: 36,
                      cursor: 'pointer',
                    }}
                    title={t('dashboard.offers.filters.title')}
                    aria-label={t('dashboard.offers.filters.title')}
                    onClick={() => setOffersFilterOpen(v => !v)}
                  />
                  {offersFilterOpen && (
                  <TextField
                    select
                    fullWidth
                    size="small"
                    label={t('dashboard.offers.filters.allStatus')}
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value as any)}
                    SelectProps={{ native: true }}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 }, gridArea: { xs: 'status', sm: 'status' }, width: { sm: 200, md: 220 } }}
                  >
                    <option value="all">{t('dashboard.offers.filters.allStatus')}</option>
                    <option value="pending">{t('dashboard.offers.filters.pending')}</option>
                    <option value="accepted">{t('dashboard.offers.filters.accepted')}</option>
                    <option value="declined">{t('dashboard.offers.filters.declined')}</option>
                    <option value="countered">{t('dashboard.offers.filters.countered')}</option>
                    <option value="expired">{t('dashboard.offers.filters.expired')}</option>
                  </TextField>
                  )}
                  {/* Range row on xs */}
                  {offersFilterOpen && (
                  <Box sx={{ display: { xs: 'grid', sm: 'none' }, gridTemplateColumns: { xs: '1fr 1fr auto' }, gap: 1, alignItems: 'center', width: '100%', gridArea: { xs: 'range', sm: 'unset' } }}>
                    <TextField
                      fullWidth
                      size="small"
                      type="number"
                      label={t('dashboard.offers.filters.minPrice')}
                      value={minPrice}
                      onChange={(e) => setMinPrice(e.target.value)}
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 }, gridArea: { sm: 'min' } }}
                    />
                    <TextField
                      fullWidth
                      size="small"
                      type="number"
                      label={t('dashboard.offers.filters.maxPrice')}
                      value={maxPrice}
                      onChange={(e) => setMaxPrice(e.target.value)}
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 }, gridArea: { sm: 'max' } }}
                    />
                    <Box sx={{ justifySelf: 'end', display: 'flex', alignItems: 'center' }}>
                      <Button
                        startIcon={<CloseIcon />}
                        variant="text"
                        onClick={() => { setStatusFilter('all'); setMinPrice(''); setMaxPrice(''); }}
                        sx={{ display: { xs: 'none', sm: 'none' }, textTransform: 'none' }}
                      >
                        {t('dashboard.offers.filters.clear')}
                      </Button>
                      <IconButton
                        size="small"
                        onClick={() => { setStatusFilter('all'); setMinPrice(''); setMaxPrice(''); }}
                        aria-label="clear filters"
                        sx={{ display: { xs: 'inline-flex', sm: 'none' } }}
                      >
                        <CloseIcon />
                      </IconButton>
                    </Box>
                  </Box>
                  )}
                  {/* On sm+, keep items inline in one row */}
                  {offersFilterOpen && (
                  <TextField
                    fullWidth
                    size="small"
                    type="number"
                    label={t('dashboard.offers.filters.minPrice')}
                    value={minPrice}
                    onChange={(e) => setMinPrice(e.target.value)}
                    sx={{ display: { xs: 'none', sm: 'block' }, '& .MuiOutlinedInput-root': { borderRadius: 2 }, width: { sm: 120, md: 140 } }}
                  />
                  )}
                  {offersFilterOpen && (
                  <TextField
                    fullWidth
                    size="small"
                    type="number"
                    label={t('dashboard.offers.filters.maxPrice')}
                    value={maxPrice}
                    onChange={(e) => setMaxPrice(e.target.value)}
                    sx={{ display: { xs: 'none', sm: 'block' }, '& .MuiOutlinedInput-root': { borderRadius: 2 }, width: { sm: 120, md: 140 } }}
                  />
                  )}
                  {offersFilterOpen && (
                  <Box sx={{ justifySelf: 'end', display: { xs: 'none', sm: 'flex' }, alignItems: 'center' }}>
                    <Button
                      startIcon={<CloseIcon />}
                      variant="text"
                      onClick={() => { setStatusFilter('all'); setMinPrice(''); setMaxPrice(''); }}
                      sx={{ textTransform: 'none' }}
                    >
                      {t('dashboard.offers.filters.clear')}
                    </Button>
                  </Box>
                  )}
                </Box>
              </Card>
            )}
          </Box>

          {loadingOffers ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : offersError ? (
            <Alert severity="error" sx={{ mb: 3 }}>{t('dashboard.offers.error.load')}</Alert>
          ) : (is_job && !isCompanyDashboard) ? (
            (() => {
              // Applications are already filtered server-side
              const list = applicationsSubmitted;
              
              if (!list || list.length === 0) {
                return (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <Typography variant="h6" color="text.secondary">
                      {showAIGeneratedOnly 
                        ? "No AI generated applications found." 
                        : "You haven't submitted any applications yet."
                      }
                    </Typography>
                    {showAIGeneratedOnly && (
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        Try turning off the AI filter to see all applications.
                      </Typography>
                    )}
                  </Box>
                );
              }
              return (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {list.map((item: any, index: number) => (
                    <motion.div key={`app-sub-${item.id}`} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3, delay: index * 0.04 }}>
                      <Box
                        sx={{
                          borderRadius: 3,
                          p: 2.25,
                          boxShadow: 1,
                          background: 'linear-gradient(180deg, rgba(2,132,199,0.04) 0%, rgba(2,132,199,0.01) 100%)',
                          border: '1px solid',
                          borderColor: 'divider',
                          transition: 'box-shadow 0.2s ease, transform 0.2s ease',
                          '&:hover': { boxShadow: 3, transform: 'translateY(-1px)' }
                        }}
                      >
                        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '72px 1fr auto' }, gap: 2, alignItems: 'start' }}>
                          <Box sx={{ width: 72, height: 72, borderRadius: '50%', bgcolor: 'primary.light', color: 'primary.main', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <WorkIcon />
                          </Box>
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, minWidth: 0 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                              <Typography variant="h6" fontWeight={800} sx={{ mr: 1 }}>{item.product?.name || 'Application'}</Typography>
                              <Chip label={(item.status || 'submitted').toUpperCase()} size="small" />
                              {item.ai_application && (
                                <Chip 
                                  label="AI Generated" 
                                  size="small" 
                                  color="primary" 
                                  variant="outlined"
                                  sx={{ 
                                    fontWeight: 'bold',
                                    borderColor: 'primary.main',
                                    color: 'primary.main',
                                    '& .MuiChip-label': {
                                      fontSize: '0.75rem'
                                    }
                                  }}
                                />
                              )}
                            </Box>
                            <Typography variant="body2" color="text.secondary">Submitted on {new Date(item.created_at).toLocaleDateString()}</Typography>
                            {item.letter && (
                              <Typography variant="body2" sx={{ mt: 0.5 }} noWrap>
                                {item.letter}
                              </Typography>
                            )}
                            {(item.documents || []).length > 0 && (
                              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
                                {(item.documents || []).slice(0, 4).map((url: string, idx: number) => (
                                  <Button key={idx} size="small" variant="outlined" href={url} target="_blank">Document {idx + 1}</Button>
                                ))}
                              </Box>
                            )}
                          </Box>
                          <Box sx={{ textAlign: 'right', display: 'flex', flexDirection: 'column', gap: 1 }}>
                            <Button 
                              variant="contained" 
                              onClick={() => { setSelectedApplication(item); setApplicationModalOpen(true); }}
                              sx={{ textTransform: 'none' }}
                            >
                              View Application
                            </Button>
                            {item.product_id && (
                              <Button variant="outlined" onClick={() => navigate(`/product/${item.product_id}`)} sx={{ textTransform: 'none' }}>Open Posting</Button>
                            )}
                            <Button variant="text" onClick={() => navigate('/messages')} sx={{ textTransform: 'none' }}>Contact</Button>
                          </Box>
                        </Box>
                      </Box>
                    </motion.div>
                  ))}
                </Box>
              );
            })()
          ) : isCompanyDashboard ? (
            (() => {
              const list = applicationsReceived;
              if (!list || list.length === 0) {
                return (
                  <Box sx={{ textAlign: 'center', py: 6 }}>
                    <BusinessIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                    <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
                      No Applications Received Yet
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                      Applications for your job postings will appear here
                    </Typography>
                  </Box>
                );
              }
              return (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                  {list.map((item: any, index: number) => (
                    <motion.div 
                      key={`app-${item.id}`} 
                      initial={{ opacity: 0, y: 20 }} 
                      animate={{ opacity: 1, y: 0 }} 
                      transition={{ duration: 0.4, delay: index * 0.1 }}
                    >
                      <Box
                        sx={{
                          position: 'relative',
                          borderRadius: 3,
                          p: 3,
                          boxShadow: 3,
                          background: 'linear-gradient(135deg, rgba(2,132,199,0.03) 0%, rgba(34,211,238,0.02) 100%)',
                          border: '1px solid',
                          borderColor: 'divider',
                          '&:hover': {
                            boxShadow: 4,
                            transform: 'translateY(-2px)',
                            transition: 'all 0.3s ease-in-out'
                          },
                          '&:before': {
                            content: '""',
                            position: 'absolute',
                            left: 0,
                            top: 0,
                            bottom: 0,
                            width: 6,
                            borderTopLeftRadius: 12,
                            borderBottomLeftRadius: 12,
                            background: 'linear-gradient(180deg, #0284C7 0%, #22D3EE 100%)'
                          }
                        }}
                      >
                        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '100px 1fr 220px' }, gap: 3, alignItems: 'start' }}>
                          {/* Job/Company Avatar */}
                          <Box sx={{ 
                            width: 100, 
                            height: 100, 
                            borderRadius: 2, 
                            overflow: 'hidden', 
                            bgcolor: 'grey.100', 
                            display: { xs: 'none', sm: 'flex' },
                            background: 'linear-gradient(135deg, #0284C7 0%, #22D3EE 100%)',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}>
                            {item.product?.images?.[0] ? (
                              <Box 
                                component="img" 
                                src={item.product.images[0]} 
                                alt={item.product.name} 
                                sx={{ width: '100%', height: '100%', objectFit: 'cover' }} 
                              />
                            ) : (
                              <BusinessIcon sx={{ fontSize: 40, color: 'white' }} />
                            )}
                          </Box>

                          {/* Application Details */}
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, minWidth: 0 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                              <Typography variant="h5" fontWeight="bold" sx={{ color: 'text.primary' }}>
                                {item.product?.name || 'Job Application'}
                              </Typography>
                            </Box>
                            
                            {/* Applicant Info */}
                            {item.applicant && (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                <Typography variant="body1" fontWeight="600" color="primary.main">
                                  {item.applicant.first_name || item.applicant.last_name 
                                    ? `${item.applicant.first_name || ''} ${item.applicant.last_name || ''}`.trim() 
                                    : 'Anonymous Applicant'
                                  }
                                </Typography>
                                {item.applicant.alias && (
                                  <Chip 
                                    size="small" 
                                    label={`@${item.applicant.alias}`} 
                                    variant="outlined" 
                                    sx={{ fontSize: '0.75rem' }}
                                  />
                                )}
                              </Box>
                            )}

                            <Typography variant="body2" color="text.secondary">
                              Submitted on {new Date(item.created_at).toLocaleDateString('en-US', {
                                year: 'numeric',
                                month: 'long',
                                day: 'numeric'
                              })}
                            </Typography>

                            {/* Cover Letter Preview */}
                            {item.letter && (
                              <Box sx={{ mt: 1 }}>
                                <Typography 
                                  variant="body2" 
                                  sx={{ 
                                    display: '-webkit-box',
                                    WebkitLineClamp: 2,
                                    WebkitBoxOrient: 'vertical',
                                    overflow: 'hidden',
                                    lineHeight: 1.4,
                                    color: 'text.secondary'
                                  }}
                                >
                                  "{item.letter.substring(0, 150)}{item.letter.length > 150 ? '...' : ''}"
                                </Typography>
                              </Box>
                            )}

                            {/* Documents */}
                            {(item.documents || []).length > 0 && (
                              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
                                {(item.documents || []).slice(0, 3).map((doc: any, idx: number) => (
                                  <Button 
                                    key={idx} 
                                    size="small" 
                                    variant="outlined" 
                                    href={typeof doc === 'string' ? doc : doc.url} 
                                    target="_blank"
                                    sx={{ 
                                      borderRadius: 2,
                                      textTransform: 'none',
                                      fontSize: '0.75rem'
                                    }}
                                  >
                                    {typeof doc === 'string' ? `Document ${idx + 1}` : doc.name || `Document ${idx + 1}`}
                                  </Button>
                                ))}
                                {(item.documents || []).length > 3 && (
                                  <Typography variant="body2" color="text.secondary" sx={{ alignSelf: 'center' }}>
                                    +{(item.documents || []).length - 3} more
                                  </Typography>
                                )}
                              </Box>
                            )}
                          </Box>

                          {/* Action Buttons */}
                          {isCompanyDashboard && (
                            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                              <ApplicationStatusActions
                                application={item}
                                onStatusUpdate={handleApplicationStatusUpdateForActions}
                                onViewApplication={() => {
                                  setSelectedApplication(item);
                                  setApplicationModalOpen(true);
                                }}
                                onContact={() => navigate('/messages')}
                                onViewPosting={item.product_id ? () => navigate(`/product/${item.product_id}`) : undefined}
                              />
                            </Box>
                          )}
                        </Box>
                      </Box>
                    </motion.div>
                  ))}
                </Box>
              );
            })()
          ) : (
            // Original offers UI when not in company dashboard
            (() => {
              return (
                <>
                {/* Made offers */}
                {ordersTab === 'made' ? (
                  offersMade.length === 0 ? (
                    <Box sx={{ textAlign: 'center', py: 4 }}>
                      <Typography variant="h6" color="text.secondary">{t('dashboard.offers.empty.made.title')}</Typography>
                    </Box>
                  ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      {offersMade
                        .filter(o => statusFilter === 'all' ? true : o.status === statusFilter)
                        .filter(o => (minPrice ? o.offer_price >= parseFloat(minPrice) : true) && (maxPrice ? o.offer_price <= parseFloat(maxPrice) : true))
                        .map((offer, index) => (
                          <motion.div key={`made-${offer.id}`} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: index * 0.05 }}>
                            <Card sx={{ borderRadius: 2, boxShadow: 1, '&:hover': { boxShadow: 3 } }}>
                              <CardContent>
                                <Box sx={{ display: 'flex', alignItems: 'stretch', gap: 2 }}>
                                  <Box
                                    component="img"
                                    src={productInfoById[offer.product_id]?.image || '/placeholder-product.jpg'}
                                    alt={productInfoById[offer.product_id]?.name || 'Product image'}
                                    sx={{ width: 120, height: 120, borderRadius: 2, objectFit: 'cover', flexShrink: 0 }}
                                  />
                                  <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                      <Box>
                                        <Typography variant="h6" fontWeight="bold">{translatedOfferProducts[offer.product_id]?.name || productInfoById[offer.product_id]?.name || 'Product'}</Typography>
                                        <Typography variant="body2" color="text.secondary">{new Date(offer.created_at).toLocaleDateString()}</Typography>
                                        {!!(translatedOfferProducts[offer.product_id]?.description || productInfoById[offer.product_id]?.description) && (
                                          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                            {translatedOfferProducts[offer.product_id]?.description || productInfoById[offer.product_id]?.description}
                                          </Typography>
                                        )}
                                      </Box>
                                      <Box sx={{ textAlign: 'right' }}>
                                        <Chip 
                                          icon={getStatusIcon(offer.status, true)} 
                                          label={t(`dashboard.offers.status.${offer.status}`)} 
                                          color={getStatusColor(offer.status) as any} 
                                          size="small" 
                                          sx={{ 
                                            mb: 1,
                                            fontWeight: 600,
                                            fontSize: '0.75rem',
                                            height: '28px',
                                            '& .MuiChip-icon': {
                                              fontSize: '16px',
                                              color: 'inherit'
                                            },
                                            ...(offer.status === 'pending' && {
                                              bgcolor: 'warning.light',
                                              color: 'warning.contrastText',
                                              '&:hover': {
                                                bgcolor: 'warning.main',
                                              }
                                            })
                                          }} 
                                        />
                                        <Typography variant="h6" fontWeight="bold" color="primary.main">${offer.offer_price}</Typography>
                                      </Box>
                                    </Box>
                                    <Typography variant="body2" sx={{ mt: 1 }}>
                                      {translatedOfferMessages[offer.id] || offer.offer_message}
                                    </Typography>
                                    <Box sx={{ display: 'flex', gap: 1.5, mt: 2, flexWrap: 'wrap' }}>
                                      <Button 
                                        variant="outlined" 
                                        startIcon={<ViewIcon />}
                                        onClick={() => navigate(`/product/${offer.product_id}`)}
                                        sx={{ 
                                          textTransform: 'none',
                                          borderRadius: 2,
                                          px: 2.5,
                                          py: 1,
                                          fontWeight: 500,
                                          transition: 'all 0.2s ease-in-out',
                                          '&:hover': {
                                            transform: 'translateY(-1px)',
                                            boxShadow: 2
                                          }
                                        }}
                                      >
                                        {t('dashboard.products.button.viewProduct')}
                                      </Button>
                                      <Button 
                                        variant="contained" 
                                        startIcon={<ChatIcon />}
                                        onClick={() => navigate('/messages', { state: buildChatState(offer, 'made') })}
                                        sx={{ 
                                          textTransform: 'none',
                                          borderRadius: 2,
                                          px: 2.5,
                                          py: 1,
                                          fontWeight: 500,
                                          transition: 'all 0.2s ease-in-out',
                                          '&:hover': {
                                            transform: 'translateY(-1px)',
                                            boxShadow: 4
                                          }
                                        }}
                                      >
                                        {t('dashboard.offers.button.openChat')}
                                      </Button>
                                      {offer.status === 'pending' && (
                                        <>
                                          <Button 
                                            variant="outlined" 
                                            startIcon={<EditIcon />}
                                            onClick={() => setEditingOffer(offer)}
                                            sx={{ 
                                              textTransform: 'none',
                                              borderRadius: 2,
                                              px: 2.5,
                                              py: 1,
                                              fontWeight: 500,
                                              transition: 'all 0.2s ease-in-out',
                                              '&:hover': {
                                                transform: 'translateY(-1px)',
                                                boxShadow: 2
                                              }
                                            }}
                                          >
                                            Edit Offer
                                          </Button>
                                          <Button 
                                            color="error" 
                                            variant="outlined" 
                                            startIcon={<CancelIcon />}
                                            onClick={() => setDeleteOffer(offer)}
                                            sx={{ 
                                              textTransform: 'none',
                                              borderRadius: 2,
                                              px: 2.5,
                                              py: 1,
                                              fontWeight: 500,
                                              transition: 'all 0.2s ease-in-out',
                                              '&:hover': {
                                                transform: 'translateY(-1px)',
                                                boxShadow: 2
                                              }
                                            }}
                                          >
                                            Withdraw
                                          </Button>
                                        </>
                                      )}
                                    </Box>
                                  </Box>
                                </Box>
                              </CardContent>
                            </Card>
                          </motion.div>
                        ))}
                    </Box>
                  )
                ) : (
                  // Received offers
                  offersReceived.length === 0 ? (
                    <Box sx={{ textAlign: 'center', py: 4 }}>
                      <Typography variant="h6" color="text.secondary">{t('dashboard.offers.empty.received.title')}</Typography>
                    </Box>
                  ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      {offersReceived
                        .filter(o => statusFilter === 'all' ? true : o.status === statusFilter)
                        .filter(o => (minPrice ? o.offer_price >= parseFloat(minPrice) : true) && (maxPrice ? o.offer_price <= parseFloat(maxPrice) : true))
                        .map((offer, index) => (
                          <motion.div key={`received-${offer.id}`} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: index * 0.05 }}>
                            <Card sx={{ borderRadius: 2, boxShadow: 1, '&:hover': { boxShadow: 3 } }}>
                              <CardContent>
                                <Box sx={{ display: 'flex', alignItems: 'stretch', gap: 2 }}>
                                  <Box
                                    component="img"
                                    src={productInfoById[offer.product_id]?.image || '/placeholder-product.jpg'}
                                    alt={productInfoById[offer.product_id]?.name || 'Product image'}
                                    sx={{ width: 120, height: 120, borderRadius: 2, objectFit: 'cover', flexShrink: 0 }}
                                  />
                                  <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                      <Box>
                                        <Typography variant="h6" fontWeight="bold">{translatedOfferProducts[offer.product_id]?.name || productInfoById[offer.product_id]?.name || 'Product'}</Typography>
                                        <Typography variant="body2" color="text.secondary">{new Date(offer.created_at).toLocaleDateString()}</Typography>
                                        {!!(translatedOfferProducts[offer.product_id]?.description || productInfoById[offer.product_id]?.description) && (
                                          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                            {translatedOfferProducts[offer.product_id]?.description || productInfoById[offer.product_id]?.description}
                                          </Typography>
                                        )}
                                      </Box>
                                      <Box sx={{ textAlign: 'right' }}>
                                        <Chip 
                                          icon={getStatusIcon(offer.status, true)} 
                                          label={t(`dashboard.offers.status.${offer.status}`)} 
                                          color={getStatusColor(offer.status) as any} 
                                          size="small" 
                                          sx={{ 
                                            mb: 1,
                                            fontWeight: 600,
                                            fontSize: '0.75rem',
                                            height: '28px',
                                            '& .MuiChip-icon': {
                                              fontSize: '16px',
                                              color: 'inherit'
                                            },
                                            ...(offer.status === 'pending' && {
                                              bgcolor: 'warning.light',
                                              color: 'warning.contrastText',
                                              '&:hover': {
                                                bgcolor: 'warning.main',
                                              }
                                            })
                                          }} 
                                        />
                                        <Typography variant="h6" fontWeight="bold" color="primary.main">${offer.offer_price}</Typography>
                                      </Box>
                                    </Box>
                                    <Typography variant="body2" sx={{ mt: 1 }}>
                                      {translatedOfferMessages[offer.id] || offer.offer_message}
                                    </Typography>
                                    <Box sx={{ display: 'flex', gap: 1.5, mt: 2, flexWrap: 'wrap' }}>
                                      <Button 
                                        variant="outlined" 
                                        startIcon={<ViewIcon />}
                                        onClick={() => navigate(`/product/${offer.product_id}`)}
                                        sx={{ 
                                          textTransform: 'none',
                                          borderRadius: 2,
                                          px: 2.5,
                                          py: 1,
                                          fontWeight: 500,
                                          transition: 'all 0.2s ease-in-out',
                                          '&:hover': {
                                            transform: 'translateY(-1px)',
                                            boxShadow: 2
                                          }
                                        }}
                                      >
                                        {t('dashboard.products.button.viewProduct')}
                                      </Button>
                                      <Button 
                                        variant="contained" 
                                        startIcon={<ChatIcon />}
                                        onClick={() => navigate('/messages', { state: buildChatState(offer, 'received') })}
                                        sx={{ 
                                          textTransform: 'none',
                                          borderRadius: 2,
                                          px: 2.5,
                                          py: 1,
                                          fontWeight: 500,
                                          transition: 'all 0.2s ease-in-out',
                                          '&:hover': {
                                            transform: 'translateY(-1px)',
                                            boxShadow: 4
                                          }
                                        }}
                                      >
                                        {t('dashboard.offers.button.openChat')}
                                      </Button>
                                      {offer.status === 'pending' && (
                                        <>
                                          <Button 
                                            color="success" 
                                            variant="contained" 
                                            startIcon={<CheckIcon />}
                                            onClick={() => setConfirmOffer(offer)}
                                            sx={{ 
                                              textTransform: 'none',
                                              borderRadius: 2,
                                              px: 2.5,
                                              py: 1,
                                              fontWeight: 500,
                                              transition: 'all 0.2s ease-in-out',
                                              '&:hover': {
                                                transform: 'translateY(-1px)',
                                                boxShadow: 4
                                              }
                                            }}
                                          >
                                            Accept
                                          </Button>
                                          <Button 
                                            color="warning" 
                                            variant="outlined" 
                                            startIcon={<CloseIcon />}
                                            onClick={async () => {
                                            try {
                                              const updated = await OffersService.declineOffer(offer.id);
                                              setOffersReceived(prev => prev.map(o => o.id === offer.id ? updated : o));
                                              toast.success('Offer declined');
                                            } catch (e) {
                                              console.error('Failed to decline offer', e);
                                              toast.error('Failed to decline offer');
                                            }
                                          }}
                                            sx={{ 
                                              textTransform: 'none',
                                              borderRadius: 2,
                                              px: 2.5,
                                              py: 1,
                                              fontWeight: 500,
                                              transition: 'all 0.2s ease-in-out',
                                              '&:hover': {
                                                transform: 'translateY(-1px)',
                                                boxShadow: 2
                                              }
                                            }}
                                          >
                                            Decline
                                          </Button>
                                        </>
                                      )}
                                    </Box>
                                  </Box>
                                </Box>
                              </CardContent>
                            </Card>
                          </motion.div>
                        ))}
                    </Box>
                  )
                )}
                </>
              );
            })()
          )}

          {/* Pagination for User Applications */}
          {is_job && !isCompanyDashboard && !loadingOffers && !offersError && (
            <ApplicationPagination
              currentPage={currentPage}
              totalPages={totalPages}
              totalItems={totalApplications}
              itemsPerPage={applicationsPerPage}
              onPageChange={handlePageChange}
              loading={loadingOffers}
            />
          )}

          {isCompanyDashboard && (
            <ApplicationViewerModal 
              open={applicationModalOpen}
              onClose={() => { setApplicationModalOpen(false); setSelectedApplication(null); }}
              application={selectedApplication}
              onStatusUpdate={handleApplicationStatusUpdate}
            />
          )}

          {/* Application Details Modal for User Dashboard (Job Mode) */}
          {!isCompanyDashboard && is_job && (
            <ApplicationDetailsModal 
              open={applicationModalOpen}
              onClose={() => { setApplicationModalOpen(false); setSelectedApplication(null); }}
              application={selectedApplication}
              onViewPosting={(productId) => navigate(`/product/${productId}`)}
              onViewProfile={(userId) => navigate(`/user-profile/${userId}`)}
            />
          )}
        </TabPanel>

        {/* Auctions */}
        {!is_job && !isCompanyDashboard && (
        <TabPanel value={tabValue} index={(window as any).__dashIdx.auctionsIndex}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
            <Box>
              <Typography variant="h5" fontWeight="bold" gutterBottom>
                {t('dashboard.auctions.title')}
              </Typography>
              <Typography variant="body1" color="text.secondary">
                {t('dashboard.auctions.subtitle')}
              </Typography>
            </Box>
            <Button
              variant="contained"
              onClick={() => navigate('/upload-product?type=auction')}
              startIcon={<UploadIcon />}
              sx={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                borderRadius: 3,
                px: 4,
                py: 1.5,
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '1rem',
                boxShadow: '0 4px 15px rgba(102, 126, 234, 0.3)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)',
                  boxShadow: '0 6px 20px rgba(102, 126, 234, 0.4)',
                  transform: 'translateY(-2px)',
                },
                transition: 'all 0.3s ease',
              }}
            >
              {t('dashboard.auctions.create')}
            </Button>
          </Box>

          {/* Auction Filters */}
          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
              <Typography variant="body2" fontWeight="600" color="text.secondary" sx={{ mr: 1 }}>
                {t('dashboard.auctions.filter.label')}
              </Typography>
              {[
                { value: 'all', label: t('dashboard.auctions.filter.all'), count: auctions.length },
                { value: 'active', label: t('dashboard.auctions.filter.active'), count: auctions.filter(a => updateAuctionStatus(a).status === 'active').length },
                { value: 'ended', label: t('dashboard.auctions.filter.ended'), count: auctions.filter(a => updateAuctionStatus(a).status === 'ended').length },
                { value: 'cancelled', label: t('dashboard.auctions.filter.cancelled'), count: auctions.filter(a => updateAuctionStatus(a).status === 'cancelled').length },
              ].map((filter) => (
                <Chip
                  key={filter.value}
                  label={`${filter.label} (${filter.count})`}
                  onClick={() => setAuctionFilter(filter.value as any)}
                  variant={auctionFilter === filter.value ? 'filled' : 'outlined'}
                  color={auctionFilter === filter.value ? 'primary' : 'default'}
                  sx={{
                    cursor: 'pointer',
                    fontWeight: 600,
                    '&:hover': {
                      backgroundColor: auctionFilter === filter.value ? undefined : 'action.hover',
                    },
                  }}
                />
              ))}
            </Box>
          </Box>

          {loadingAuctions ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : auctionsError ? (
            <Alert severity="error" sx={{ mb: 3 }}>
              {auctionsError}
            </Alert>
          ) : filteredAuctions.length === 0 ? (
            <Box sx={{ 
              textAlign: 'center', 
              py: 6, 
              px: 4,
              borderRadius: 3,
              background: 'linear-gradient(135deg, #f8f9ff 0%, #f0f2ff 100%)',
              border: '2px dashed #e0e7ff',
            }}>
              <Box sx={{ mb: 3 }}>
                <Box sx={{ 
                  width: 80, 
                  height: 80, 
                  borderRadius: '50%', 
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mx: 'auto',
                  mb: 2,
                }}>
                  <GavelIcon sx={{ color: 'white', fontSize: 40 }} />
                </Box>
              </Box>
              <Typography variant="h6" color="text.secondary" gutterBottom sx={{ fontWeight: 600 }}>
                {auctionFilter === 'all' ? t('dashboard.auctions.empty.title.all') : 
                 auctionFilter === 'active' ? t('dashboard.auctions.empty.title.active') :
                 auctionFilter === 'ended' ? t('dashboard.auctions.empty.title.ended') :
                 t('dashboard.auctions.empty.title.cancelled')}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 4, maxWidth: 400, mx: 'auto' }}>
                {auctionFilter === 'all' ? t('dashboard.auctions.empty.subtitle.all') :
                 auctionFilter === 'active' ? t('dashboard.auctions.empty.subtitle.active') :
                 auctionFilter === 'ended' ? t('dashboard.auctions.empty.subtitle.ended') :
                 t('dashboard.auctions.empty.subtitle.cancelled')}
              </Typography>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {filteredAuctions.map((auction) => {
                const updatedAuction = updateAuctionStatus(auction);
                return (
                <Card key={updatedAuction.id} sx={{ 
                  borderRadius: 3, 
                  boxShadow: 2, 
                  overflow: 'hidden',
                  position: 'relative',
                  opacity: updatedAuction.status === 'ended' ? 0.85 : 1,
                  border: updatedAuction.status === 'ended' ? '2px solid #e0e0e0' : 'none',
                  background: updatedAuction.status === 'ended' ? 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)' : 'background.paper',
                  '&:hover': {
                    transform: updatedAuction.status === 'ended' ? 'none' : 'translateY(-2px)',
                    boxShadow: updatedAuction.status === 'ended' ? 2 : 4,
                  },
                  transition: 'all 0.3s ease',
                }}>
                  {/* Ended Auction Overlay */}
                  {updatedAuction.status === 'ended' && (
                    <Box sx={{
                      position: 'absolute',
                      top: 16,
                      right: 16,
                      zIndex: 2,
                      background: 'linear-gradient(135deg, #dc3545 0%, #c82333 100%)',
                      color: 'white',
                      px: 2,
                      py: 1,
                      borderRadius: 2,
                      fontWeight: 'bold',
                      fontSize: '0.875rem',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      boxShadow: '0 2px 8px rgba(220, 53, 69, 0.3)',
                    }}>
                      {t('dashboard.auctions.overlay.ended')}
                    </Box>
                  )}
                  
                  <CardContent sx={{ p: 3 }}>
                    <Box sx={{ display: 'flex', gap: 3, alignItems: 'flex-start' }}>
                      {/* Product Image */}
                      <Box sx={{ flexShrink: 0 }}>
                        <Box
                          sx={{
                            width: 120,
                            height: 120,
                            borderRadius: 2,
                            background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            overflow: 'hidden',
                          }}
                        >
                          {updatedAuction.product.images && updatedAuction.product.images.length > 0 ? (
                            <img
                              src={updatedAuction.product.images[0]}
                              alt={(autoTranslate && language !== 'en' && translatedAuctionNames[updatedAuction.id]) ? translatedAuctionNames[updatedAuction.id] : updatedAuction.product.name}
                              style={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover',
                              }}
                            />
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              {t('dashboard.auctions.noImage')}
                            </Typography>
                          )}
                        </Box>
                      </Box>

                      {/* Auction Details */}
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography variant="h6" fontWeight="bold" sx={{ mb: 1, color: 'text.primary' }}>
                              {(autoTranslate && language !== 'en' && translatedAuctionNames[updatedAuction.id]) ? translatedAuctionNames[updatedAuction.id] : updatedAuction.product.name}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                              {(autoTranslate && language !== 'en' && translatedAuctionDescriptions[updatedAuction.id])
                                ? translatedAuctionDescriptions[updatedAuction.id]
                                : updatedAuction.product.description}
                            </Typography>
                          </Box>
                          
                          {/* Status Chip - Hide for ended auctions since we have the overlay */}
                          {updatedAuction.status !== 'ended' && (
                            <Chip
                              label={
                                updatedAuction.status === 'active' ? t('dashboard.auctions.status.active') :
                                updatedAuction.status === 'cancelled' ? t('dashboard.auctions.status.cancelled') :
                                updatedAuction.status === 'paused' ? t('dashboard.auctions.status.paused') :
                                t('dashboard.auctions.status.draft')
                              }
                              sx={{
                                fontWeight: 600,
                                textTransform: 'capitalize',
                                background: updatedAuction.status === 'active' ? 'linear-gradient(135deg, #28a745 0%, #20c997 100%)' :
                                           updatedAuction.status === 'cancelled' ? 'linear-gradient(135deg, #dc3545 0%, #c82333 100%)' :
                                           updatedAuction.status === 'paused' ? 'linear-gradient(135deg, #ffc107 0%, #fd7e14 100%)' :
                                           'linear-gradient(135deg, #6f42c1 0%, #e83e8c 100%)',
                                color: 'white',
                                '& .MuiChip-label': {
                                  color: 'white',
                                  fontWeight: 700,
                                },
                              }}
                            />
                          )}
                        </Box>

                        {/* Auction Stats */}
                        <Box sx={{ display: 'flex', gap: 4, mb: 2 }}>
                          <Box>
                            <Typography variant="body2" color="text.secondary">
                              {t('dashboard.auctions.stats.startingPrice')}
                            </Typography>
                            <Typography variant="h6" fontWeight="bold" color="primary">
                              ${updatedAuction.starting_price.toFixed(2)}
                            </Typography>
                          </Box>
                          <Box>
                            <Typography variant="body2" color="text.secondary">
                              {updatedAuction.status === 'ended' ? t('dashboard.auctions.stats.finalBid') : t('dashboard.auctions.stats.currentBid')}
                            </Typography>
                            <Typography variant="h6" fontWeight="bold" color={updatedAuction.status === 'ended' ? 'text.secondary' : 'success.main'}>
                              ${updatedAuction.current_bid.toFixed(2)}
                            </Typography>
                          </Box>
                          {updatedAuction.reserve_price && (
                            <Box>
                              <Typography variant="body2" color="text.secondary">
                                {t('dashboard.auctions.stats.reservePrice')}
                              </Typography>
                              <Typography variant="h6" fontWeight="bold" color="warning.main">
                                ${updatedAuction.reserve_price.toFixed(2)}
                              </Typography>
                            </Box>
                          )}
                          {updatedAuction.status === 'ended' && (
                            <Box>
                              <Typography variant="body2" color="text.secondary">
                                {t('dashboard.auctions.stats.totalBids')}
                              </Typography>
                              <Typography variant="h6" fontWeight="bold" color="info.main">
                                {updatedAuction.bid_count || 0}
                              </Typography>
                            </Box>
                          )}
                        </Box>

                        {/* Time Information */}
                        <Box sx={{ display: 'flex', gap: 4, mb: 2 }}>
                          <Box>
                            <Typography variant="body2" color="text.secondary">
                              {t('dashboard.auctions.time.started')}
                            </Typography>
                            <Typography variant="body2" fontWeight="500">
                              {new Date(updatedAuction.start_time).toLocaleDateString()}
                            </Typography>
                          </Box>
                          <Box>
                            <Typography variant="body2" color="text.secondary">
                              {updatedAuction.status === 'ended' ? t('dashboard.auctions.time.ended') : t('dashboard.auctions.time.ends')}
                            </Typography>
                            <Typography variant="body2" fontWeight="500" color={updatedAuction.status === 'ended' ? 'text.secondary' : 'text.primary'}>
                              {new Date(updatedAuction.end_time).toLocaleDateString()}
                            </Typography>
                          </Box>
                          {updatedAuction.status === 'ended' && (
                            <Box>
                              <Typography variant="body2" color="text.secondary">
                                {t('dashboard.auctions.time.duration')}
                              </Typography>
                              <Typography variant="body2" fontWeight="500">
                                {(() => {
                                  const start = new Date(updatedAuction.start_time);
                                  const end = new Date(updatedAuction.end_time);
                                  const diffDays = Math.max(1, Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)));
                                  return `${diffDays} days`;
                                })()}
                              </Typography>
                            </Box>
                          )}
                        </Box>

                        {/* Actions */}
                        <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={() => navigate(`/product/${updatedAuction.product.id}`)}
                            sx={{
                              borderRadius: 2,
                              textTransform: 'none',
                              fontWeight: 600,
                            }}
                          >
                            {t('dashboard.auctions.button.viewProduct')}
                          </Button>
                          {updatedAuction.status === 'active' && (
                            <Button
                              variant="contained"
                              size="small"
                              onClick={() => navigate(`/auction/${updatedAuction.id}`)}
                              sx={{
                                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                borderRadius: 2,
                                textTransform: 'none',
                                fontWeight: 600,
                                '&:hover': {
                                  background: 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)',
                                }
                              }}
                            >
                              {t('dashboard.auctions.button.viewAuction')}
                            </Button>
                          )}
                          {updatedAuction.status === 'ended' && (
                            <Button
                              variant="contained"
                              size="small"
                              onClick={() => navigate(`/auction/${updatedAuction.id}`)}
                              sx={{
                                background: 'linear-gradient(135deg, #6c757d 0%, #495057 100%)',
                                borderRadius: 2,
                                textTransform: 'none',
                                fontWeight: 600,
                                '&:hover': {
                                  background: 'linear-gradient(135deg, #5a6268 0%, #343a40 100%)',
                                }
                              }}
                            >
                              View Results
                            </Button>
                          )}
                        </Box>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
                );
              })}
            </Box>
          )}
        </TabPanel>
        )}

        {/* Inquiries */}
        {is_job && !isCompanyDashboard && (
        <TabPanel value={tabValue} index={(window as any).__dashIdx.inquiriesIndex}>
          <Typography variant="h5" fontWeight="bold" gutterBottom>
            Profile Inquiries
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            Manage inquiries from users who want to view your private profile
          </Typography>

          <Box sx={{ display: 'flex', gap: 1.5, mb: 3 }}>
            <Button
              variant={inquiriesView === 'received' ? 'contained' : 'outlined'}
              onClick={() => setInquiriesView('received')}
              sx={{ 
                textTransform: 'none', 
                borderRadius: 999, 
                px: 4,
                py: 1.5,
                fontWeight: 600,
                fontSize: '0.875rem',
                background: inquiriesView === 'received' 
                  ? 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)'
                  : 'transparent',
                color: inquiriesView === 'received' ? 'white' : '#3b82f6',
                border: inquiriesView === 'received' 
                  ? 'none'
                  : '2px solid #3b82f6',
                boxShadow: inquiriesView === 'received' 
                  ? '0 4px 16px rgba(59, 130, 246, 0.3)'
                  : 'none',
                '&:hover': {
                  background: inquiriesView === 'received'
                    ? 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)'
                    : 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                  color: 'white',
                  boxShadow: '0 6px 20px rgba(59, 130, 246, 0.4)',
                  transform: 'translateY(-1px)'
                },
                transition: 'all 0.2s ease-in-out'
              }}
            >
              📥 Received Inquiries
            </Button>
            <Button
              variant={inquiriesView === 'sent' ? 'contained' : 'outlined'}
              onClick={() => setInquiriesView('sent')}
              sx={{ 
                textTransform: 'none', 
                borderRadius: 999, 
                px: 4,
                py: 1.5,
                fontWeight: 600,
                fontSize: '0.875rem',
                background: inquiriesView === 'sent' 
                  ? 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)'
                  : 'transparent',
                color: inquiriesView === 'sent' ? 'white' : '#3b82f6',
                border: inquiriesView === 'sent' 
                  ? 'none'
                  : '2px solid #3b82f6',
                boxShadow: inquiriesView === 'sent' 
                  ? '0 4px 16px rgba(59, 130, 246, 0.3)'
                  : 'none',
                '&:hover': {
                  background: inquiriesView === 'sent'
                    ? 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)'
                    : 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                  color: 'white',
                  boxShadow: '0 6px 20px rgba(59, 130, 246, 0.4)',
                  transform: 'translateY(-1px)'
                },
                transition: 'all 0.2s ease-in-out'
              }}
            >
              📤 Sent Inquiries
            </Button>
          </Box>

          <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 700 }}>
            {inquiriesView === 'received' ? 'Inquiries Received' : 'Inquiries Sent'}
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {loadingInquiries ? (
              <Card sx={{ borderRadius: 2, boxShadow: 1 }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Skeleton variant="rectangular" width={60} height={60} />
                    <Box sx={{ flex: 1 }}>
                      <Skeleton variant="text" height={32} width="40%" />
                      <Skeleton variant="text" height={24} width="25%" />
                      <Skeleton variant="text" height={20} width="80%" />
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            ) : inquiriesError ? (
              <Alert severity="error">{inquiriesError}</Alert>
            ) : (
              (() => {
                const filteredInquiries = inquiries.filter(inquiry => 
                  inquiriesView === 'received' ? inquiry.profile_user_id === user?.id : inquiry.requester_user_id === user?.id
                );
                
                if (filteredInquiries.length === 0) {
                  return (
                    <Card sx={{ borderRadius: 2, boxShadow: 1, textAlign: 'center', py: 6 }}>
                      <CardContent>
                        <MessageIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                        <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
                          {inquiriesView === 'received' ? 'No inquiries received yet' : 'No inquiries sent yet'}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                          {inquiriesView === 'received' 
                            ? 'When users request to view your private profile, they will appear here'
                            : 'Inquiries you send to view private profiles will appear here'
                          }
                        </Typography>
                      </CardContent>
                    </Card>
                  );
                }
                
                return filteredInquiries.map((inquiry, index) => {
                  const profilePicture = inquiryProfilePictures[inquiry.id];
                  const targetUserId = inquiriesView === 'received' ? inquiry.requester_user_id : inquiry.profile_user_id;
                  
                  console.log('Rendering inquiry:', inquiry.id, 'Profile picture:', profilePicture, 'Target user:', targetUserId);
                  
                  return (
                    <motion.div
                      key={inquiry.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.4, delay: index * 0.1 }}
                    >
                      <Card 
                        sx={{ 
                          borderRadius: 3, 
                          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
                          background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
                          border: '1px solid rgba(226, 232, 240, 0.8)',
                          '&:hover': { 
                            boxShadow: '0 12px 48px rgba(0, 0, 0, 0.15)',
                            transform: 'translateY(-2px)',
                            transition: 'all 0.3s ease-in-out'
                          }
                        }}
                      >
                        <CardContent sx={{ p: 3 }}>
                          <Box sx={{ display: 'flex', alignItems: 'stretch', gap: 3 }}>
                            {/* Profile Picture */}
                            <Box 
                              sx={{ 
                                position: 'relative',
                                cursor: 'pointer',
                                '&:hover': {
                                  transform: 'scale(1.05)',
                                  transition: 'transform 0.2s ease-in-out'
                                }
                              }}
                              onClick={() => handleProfilePreview(targetUserId)}
                            >
                              <Avatar 
                                sx={{ 
                                  width: 80, 
                                  height: 80, 
                                  border: '3px solid #ffffff',
                                  boxShadow: '0 4px 16px rgba(0, 0, 0, 0.1)',
                                  background: profilePicture 
                                    ? 'transparent'
                                    : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                  '&:hover': {
                                    boxShadow: '0 8px 24px rgba(0, 0, 0, 0.2)',
                                    transition: 'box-shadow 0.2s ease-in-out'
                                  }
                                }}
                                src={profilePicture}
                              >
                                {!profilePicture && <PersonIcon sx={{ fontSize: 40, color: 'white' }} />}
                              </Avatar>
                              
                              {/* Hover overlay */}
                              <Box
                                sx={{
                                  position: 'absolute',
                                  top: 0,
                                  left: 0,
                                  right: 0,
                                  bottom: 0,
                                  borderRadius: '50%',
                                  background: 'rgba(0, 0, 0, 0.1)',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  opacity: 0,
                                  transition: 'opacity 0.2s ease-in-out',
                                  '&:hover': {
                                    opacity: 1,
                                  }
                                }}
                              >
                                <PersonIcon sx={{ color: 'white', fontSize: 24 }} />
                              </Box>
                            </Box>
                            
                            <Box sx={{ flex: 1 }}>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                                <Box>
                                  <Typography variant="h6" fontWeight="bold" sx={{ mb: 0.5, color: '#1e293b' }}>
                                    {inquiriesView === 'received' ? 'Profile Access Request' : 'Profile Access Request'}
                                  </Typography>
                                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                                    {new Date(inquiry.created_at).toLocaleDateString('en-US', {
                                      year: 'numeric',
                                      month: 'short',
                                      day: 'numeric'
                                    })}
                                  </Typography>
                                </Box>
                                <Box sx={{ textAlign: 'right' }}>
                                  <Chip
                                    label={inquiry.status}
                                    sx={{
                                      textTransform: 'capitalize',
                                      fontWeight: 600,
                                      fontSize: '0.75rem',
                                      height: 28,
                                      borderRadius: 14,
                                      background: inquiry.status === 'approved' 
                                        ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)'
                                        : inquiry.status === 'rejected'
                                        ? 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)'
                                        : inquiry.status === 'pending'
                                        ? 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)'
                                        : 'linear-gradient(135deg, #6b7280 0%, #4b5563 100%)',
                                      color: 'white',
                                      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                                      '& .MuiChip-label': {
                                        px: 2
                                      }
                                    }}
                                  />
                                </Box>
                              </Box>
                              
                              {inquiry.message && (
                                <Box
                                  sx={{
                                    p: 2,
                                    mb: 2,
                                    borderRadius: 2,
                                    background: 'linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%)',
                                    border: '1px solid rgba(148, 163, 184, 0.2)'
                                  }}
                                >
                                  <Typography 
                                    variant="body2" 
                                    sx={{ 
                                      color: '#475569',
                                      fontStyle: 'italic',
                                      lineHeight: 1.6
                                    }}
                                  >
                                    "{inquiry.message}"
                                  </Typography>
                                </Box>
                              )}
                              
                              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                                {inquiriesView === 'received' && (
                                  <>
                                    {/* Always show action buttons for received inquiries */}
                                    <Button 
                                      variant="contained"
                                      onClick={() => handleRespondToInquiry(inquiry.id, 'approved')}
                                      disabled={inquiry.status === 'approved'}
                                      sx={{ 
                                        textTransform: 'none',
                                        fontWeight: 600,
                                        px: 4,
                                        py: 1.5,
                                        borderRadius: 3,
                                        background: inquiry.status === 'approved'
                                          ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)'
                                          : 'linear-gradient(135deg, #84cc16 0%, #22c55e 50%, #16a34a 100%)',
                                        boxShadow: inquiry.status === 'approved'
                                          ? '0 4px 16px rgba(16, 185, 129, 0.3)'
                                          : '0 6px 20px rgba(34, 197, 94, 0.4)',
                                        '&:hover': {
                                          background: inquiry.status === 'approved'
                                            ? 'linear-gradient(135deg, #059669 0%, #047857 100%)'
                                            : 'linear-gradient(135deg, #22c55e 0%, #16a34a 50%, #15803d 100%)',
                                          boxShadow: inquiry.status === 'approved'
                                            ? '0 6px 20px rgba(16, 185, 129, 0.4)'
                                            : '0 8px 25px rgba(34, 197, 94, 0.5)',
                                          transform: 'translateY(-2px)'
                                        },
                                        '&:disabled': {
                                          background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                                          color: 'white',
                                          opacity: 0.8
                                        },
                                        transition: 'all 0.3s ease-in-out'
                                      }}
                                    >
                                      {inquiry.status === 'approved' ? '✓ Approved' : '✓ Approve'}
                                    </Button>
                                    
                                    <Button 
                                      variant="contained"
                                      onClick={() => handleRespondToInquiry(inquiry.id, 'rejected')}
                                      disabled={inquiry.status === 'rejected'}
                                      sx={{ 
                                        textTransform: 'none',
                                        fontWeight: 600,
                                        px: 4,
                                        py: 1.5,
                                        borderRadius: 3,
                                        background: inquiry.status === 'rejected'
                                          ? 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)'
                                          : 'linear-gradient(135deg, #f87171 0%, #ef4444 50%, #dc2626 100%)',
                                        boxShadow: inquiry.status === 'rejected'
                                          ? '0 4px 16px rgba(220, 38, 38, 0.3)'
                                          : '0 6px 20px rgba(239, 68, 68, 0.4)',
                                        '&:hover': {
                                          background: inquiry.status === 'rejected'
                                            ? 'linear-gradient(135deg, #b91c1c 0%, #991b1b 100%)'
                                            : 'linear-gradient(135deg, #ef4444 0%, #dc2626 50%, #b91c1c 100%)',
                                          boxShadow: inquiry.status === 'rejected'
                                            ? '0 6px 20px rgba(220, 38, 38, 0.4)'
                                            : '0 8px 25px rgba(239, 68, 68, 0.5)',
                                          transform: 'translateY(-2px)'
                                        },
                                        '&:disabled': {
                                          background: 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)',
                                          color: 'white',
                                          opacity: 0.8
                                        },
                                        transition: 'all 0.3s ease-in-out'
                                      }}
                                    >
                                      {inquiry.status === 'rejected' ? '✕ Rejected' : '✕ Reject'}
                                    </Button>

                                    {/* Show pending button if status is pending */}
                                    {inquiry.status === 'pending' && (
                                      <Button 
                                        variant="outlined"
                                        onClick={() => handleRespondToInquiry(inquiry.id, 'pending')}
                                        sx={{ 
                                          textTransform: 'none',
                                          fontWeight: 600,
                                          px: 4,
                                          py: 1.5,
                                          borderRadius: 3,
                                          border: '2px solid #f59e0b',
                                          color: '#f59e0b',
                                          background: 'linear-gradient(135deg, rgba(245, 158, 11, 0.1) 0%, rgba(217, 119, 6, 0.1) 100%)',
                                          '&:hover': {
                                            background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                                            color: 'white',
                                            border: '2px solid #f59e0b',
                                            transform: 'translateY(-1px)'
                                          },
                                          transition: 'all 0.3s ease-in-out'
                                        }}
                                      >
                                        ⏳ Keep Pending
                                      </Button>
                                    )}
                                  </>
                                )}
                                
                                {inquiriesView === 'sent' && inquiry.status === 'approved' && (
                                  <Button 
                                    variant="contained"
                                    onClick={() => navigate(`/user-profile/${inquiry.profile_user_id}`)}
                                    sx={{ 
                                      textTransform: 'none',
                                      fontWeight: 600,
                                      px: 4,
                                      py: 1.5,
                                      borderRadius: 3,
                                      background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                                      boxShadow: '0 6px 20px rgba(59, 130, 246, 0.4)',
                                      '&:hover': {
                                        background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
                                        boxShadow: '0 8px 25px rgba(59, 130, 246, 0.5)',
                                        transform: 'translateY(-2px)'
                                      },
                                      transition: 'all 0.3s ease-in-out'
                                    }}
                                  >
                                    👁️ View Profile
                                  </Button>
                                )}
                              </Box>
                            </Box>
                          </Box>
                        </CardContent>
                      </Card>
                    </motion.div>
                  );
                });
              })()
            )}
          </Box>
        </TabPanel>
        )}

        {/* Edit Offer Popup */}
        <OfferPopup
          open={!!editingOffer}
          onClose={() => setEditingOffer(null)}
          onSubmit={async ({ price, message }) => {
            if (!editingOffer) return;
            try {
              setSavingEdit(true);
              const updated = await OffersService.updateOffer(editingOffer.id, {
                offer_price: price,
                offer_message: message,
              });
              setOffersMade(prev => prev.map(o => o.id === updated.id ? updated : o));
              setEditingOffer(null);
              toast.success('Offer updated');
            } catch (e) {
              console.error('Failed to update offer', e);
              toast.error('Failed to update offer');
            } finally {
              setSavingEdit(false);
            }
          }}
          productTitle={editingOffer ? (productInfoById[editingOffer.product_id]?.name || 'Product') : ''}
          productPrice={editingOffer ? (productInfoById[editingOffer.product_id]?.price || 0) : 0}
          sellerName={editingOffer ? 'Seller' : ''}
          loading={savingEdit}
          mode="edit"
          initialPrice={editingOffer?.offer_price}
          initialMessage={editingOffer?.offer_message || ''}
        />
        
        {/* Favorites */}
        {!isCompanyDashboard && (
        <TabPanel value={tabValue} index={(window as any).__dashIdx.favoritesIndex}>
          <Typography variant="h5" fontWeight="bold" gutterBottom>
            {is_job ? t('dashboard.favorites.title.jobs') : t('dashboard.favorites.title.products')}
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            {is_job ? t('dashboard.favorites.subtitle.jobs') : t('dashboard.favorites.subtitle.products')}
          </Typography>

          {loadingFavorites ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : favoritesError ? (
            <Alert severity="error" sx={{ mb: 3 }}>
              {favoritesError}
            </Alert>
          ) : (!is_job && favoriteProducts.length === 0) || (is_job && favoriteJobs.length === 0) ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="h6" color="text.secondary" gutterBottom>
                {is_job ? t('dashboard.favorites.empty.title.jobs') : t('dashboard.favorites.empty.title.products')}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                {is_job ? t('dashboard.favorites.empty.subtitle.jobs') : t('dashboard.favorites.empty.subtitle.products')}
              </Typography>
              <Button
                variant="contained"
                onClick={() => navigate(is_job ? '/all-jobs' : '/all-products')}
                sx={{ textTransform: 'none' }}
              >
                {is_job ? t('dashboard.favorites.button.browse.jobs') : t('dashboard.favorites.button.browse.products')}
              </Button>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
              {(is_job ? favoriteJobs : favoriteProducts).map((product, index) => (
              <motion.div
                key={product.id}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
              >
                <Card sx={{ width: 320, borderRadius: 2.5, boxShadow: 1, '&:hover': { boxShadow: 3 } }}>
                  <Box sx={{ position: 'relative' }}>
                    {!is_job && (
                      <Box
                        component="img"
                        src={(product as any).images?.[0] || (product as any).image || '/placeholder-product.jpg'}
                        alt={(autoTranslate && language !== 'en' && translatedFavoriteNames[product.id]) ? translatedFavoriteNames[product.id] : product.name}
                        sx={{
                          width: '100%',
                          height: 200,
                          objectFit: 'cover',
                          borderRadius: '8px 8px 0 0',
                        }}
                      />
                    )}
                    {!is_job && isOwnProduct(product) && (
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 8,
                          left: 8,
                          background: is_job ? 'linear-gradient(135deg, rgba(25,118,210,0.95), rgba(21,101,192,0.95))' : 'linear-gradient(135deg, rgba(33,150,243,0.95), rgba(25,118,210,0.95))',
                          color: 'white',
                          px: 1.5,
                          py: 0.5,
                          borderRadius: 1.5,
                          boxShadow: '0 6px 16px rgba(25,118,210,0.35)',
                          fontWeight: 'bold',
                          letterSpacing: 0.6,
                          textTransform: 'uppercase',
                          fontSize: '0.72rem',
                          border: '1px solid rgba(255,255,255,0.35)',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 0.5,
                          backdropFilter: 'blur(2px)'
                        }}
                      >
                        <CheckIcon sx={{ fontSize: 14, mr: 0.2 }} /> {is_job ? t('dashboard.favorites.badge.yourJob') : t('dashboard.favorites.badge.yourProduct')}
                      </Box>
                    )}
                    {!is_job && (
                      <IconButton
                        onClick={() => handleRemoveFavorite(product.id)}
                        sx={{
                          position: 'absolute',
                          top: 8,
                          right: 8,
                          bgcolor: 'white',
                          boxShadow: 1,
                          '&:hover': { bgcolor: 'grey.100' },
                        }}
                      >
                        <FavoritesIcon color="error" />
                      </IconButton>
                    )}
                  </Box>
                  <CardContent>
                    {is_job ? (
                      <>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                          {isOwnProduct(product) && (
                            <Chip label={t('dashboard.favorites.badge.yourJob')} color="primary" size="small" sx={{ fontWeight: 700, letterSpacing: 0.6 }} />
                          )}
                          <IconButton
                            onClick={() => handleRemoveFavorite(product.id)}
                            size="small"
                            sx={{ bgcolor: 'white', boxShadow: 1, '&:hover': { bgcolor: 'grey.100' } }}
                          >
                            <FavoritesIcon color="error" />
                          </IconButton>
                        </Box>
                        <Typography variant="h6" fontWeight="bold" sx={{ mb: 0.5 }}>
                          {(autoTranslate && language !== 'en' && translatedFavoriteNames[product.id]) ? translatedFavoriteNames[product.id] : product.name}
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1.5 }}>
                          {product.location && (
                            <Chip label={product.location} size="small" />
                          )}
                          {product.employmentType && (
                            <Chip label={String(product.employmentType).toUpperCase()} size="small" color="primary" />
                          )}
                          {product.experienceLevel && (
                            <Chip label={String(product.experienceLevel).toUpperCase()} size="small" color="secondary" />
                          )}
                        </Box>
                        <Typography variant="h6" color="primary.main" fontWeight="bold" sx={{ mb: 2 }}>
                          {(() => {
                            const rawType = (product as any).salary_type ?? (product as any).salaryType ?? (product as any).sku;
                            const st = typeof rawType === 'string' ? rawType.toLowerCase() : '';
                            const p = Number((product as any).price ?? 0);
                            const notSpecified = !p || p <= 0 || st === 'negotiable';
                            if (notSpecified) return 'Gehalt nicht angegeben';
                            const suffix = st === 'hourly' || (!st && product.employmentType) ? '/hr' : '';
                            return `$${p.toFixed(2)}${suffix}`;
                          })()}
                        </Typography>
                        <Button
                          variant="contained"
                          fullWidth
                          onClick={() => navigate(`/product/${product.id}`)}
                        >
                          {t('dashboard.favorites.button.viewJob')}
                        </Button>
                      </>
                    ) : (
                      <>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                          <Typography variant="h6" fontWeight="bold">
                            {(autoTranslate && language !== 'en' && translatedFavoriteNames[product.id]) ? translatedFavoriteNames[product.id] : product.name}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                          {(() => {
                            // Calculate price with fees (only for products, not jobs)
                            const basePrice = product.price || 0;
                            const isJob = product.isJob || false;
                            const displayPrice = (!isJob && platformFeeSettings)
                              ? calculatePriceWithFeesSync(basePrice, platformFeeSettings)
                              : basePrice;
                            const baseOriginalPrice = product.originalPrice;
                            const displayOriginalPrice = (!isJob && baseOriginalPrice && platformFeeSettings)
                              ? calculatePriceWithFeesSync(baseOriginalPrice, platformFeeSettings)
                              : baseOriginalPrice;
                            
                            return (
                              <>
                                <Typography variant="h6" color="primary.main" fontWeight="bold">
                                  ${typeof displayPrice === 'number' ? displayPrice.toFixed(2) : displayPrice}
                                </Typography>
                                {displayOriginalPrice && (
                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    sx={{ textDecoration: 'line-through' }}
                                  >
                                    ${typeof displayOriginalPrice === 'number' ? displayOriginalPrice.toFixed(2) : displayOriginalPrice}
                                  </Typography>
                                )}
                              </>
                            );
                          })()}
                        </Box>
                        <Button
                          variant="contained"
                          fullWidth
                          onClick={() => navigate(`/product/${product.id}`)}
                        >
                          {t('dashboard.favorites.button.viewProduct')}
                        </Button>
                      </>
                    )}
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </Box>
          )}
        </TabPanel>
        )}

        {is_job && !isCompanyDashboard && (
        <TabPanel value={tabValue} index={(window as any).__dashIdx.companiesIndex}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: { xs: 'flex-start', md: 'center' }, gap: 2, mb: 3, flexWrap: 'wrap' }}>
            <Box>
              <Typography variant="h5" fontWeight="bold" gutterBottom>
                Unternehmen
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Verwalte die Unternehmen, die du erstellt hast oder bei denen du Mitglied bist.
              </Typography>
            </Box>
            <Button
              variant="contained"
              startIcon={<UploadIcon />}
              size="large"
              onClick={() => navigate('/companies/create')}
              sx={{
                borderRadius: 2,
                px: 3,
                py: 1.25,
                textTransform: 'none',
                fontWeight: 800,
                background: 'linear-gradient(135deg, #ff7a18 0%, #ff3d00 100%)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #ff6a00 0%, #e63900 100%)',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 10px 24px rgba(255, 61, 0, 0.25)',
                },
                transition: 'all 0.2s ease',
              }}
            >
              Unternehmen erstellen
            </Button>
          </Box>

          <Card sx={{ borderRadius: 3, boxShadow: 2, p: { xs: 1.5, md: 2 } }}>
            <Tabs
              value={companiesSubTab}
              onChange={(_, v) => setCompaniesSubTab(v)}
              variant={isMobile ? 'scrollable' : 'standard'}
              scrollButtons="auto"
              sx={{
                mb: 2,
                '& .MuiTab-root': {
                  textTransform: 'none',
                  fontWeight: 800,
                },
              }}
            >
              <Tab label="Erstellt" />
              <Tab label="Mitglied" />
            </Tabs>

            {dashboardCompaniesLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                <CircularProgress />
              </Box>
            ) : dashboardCompaniesError ? (
              <Alert severity="error">{dashboardCompaniesError}</Alert>
            ) : (() => {
              const created = dashboardCompanies.filter((c) => {
                const meta = companyMemberMetaById[c.id];
                if (!meta?.role) return false;
                if (meta.role === 'owner') return true;
                if (meta.role === 'admin' && (meta.job_title === 'Company Creator' || meta.department === 'Management')) return true;
                return false;
              });
              const member = dashboardCompanies.filter((c) => !created.some((x) => x.id === c.id));
              const list = companiesSubTab === 0 ? created : member;

              if (dashboardCompanies.length === 0) {
                return (
                  <Box sx={{ textAlign: 'center', py: 7 }}>
                    <BusinessIcon sx={{ fontSize: 56, color: 'text.secondary', mb: 1.5 }} />
                    <Typography variant="h6" fontWeight={800} gutterBottom>
                      Noch keine Unternehmen
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
                      Erstelle dein erstes Unternehmen oder tritt einem Unternehmen bei.
                    </Typography>
                    <Button
                      variant="contained"
                      startIcon={<UploadIcon />}
                      onClick={() => navigate('/companies/create')}
                      sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 800 }}
                    >
                      Unternehmen erstellen
                    </Button>
                  </Box>
                );
              }

              if (list.length === 0) {
                return (
                  <Box sx={{ textAlign: 'center', py: 6 }}>
                    <Typography variant="h6" fontWeight={800} gutterBottom>
                      {companiesSubTab === 0 ? 'Keine erstellten Unternehmen' : 'Keine Mitgliedschaften'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      {companiesSubTab === 0 ? 'Du hast noch kein Unternehmen erstellt.' : 'Du bist aktuell bei keinem Unternehmen Mitglied.'}
                    </Typography>
                    <Button
                      variant="outlined"
                      startIcon={<UploadIcon />}
                      onClick={() => navigate('/companies/create')}
                      sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 800 }}
                    >
                      Unternehmen erstellen
                    </Button>
                  </Box>
                );
              }

              return (
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' }, gap: 2.5 }}>
                  {list.map((company) => (
                    <Card
                      key={company.id}
                      onClick={() => navigate(`/companies/${company.slug}`)}
                      sx={{
                        cursor: 'pointer',
                        height: '100%',
                        transition: 'all 0.25s ease',
                        borderRadius: 3,
                        '&:hover': {
                          transform: 'translateY(-4px)',
                          boxShadow: 4,
                        },
                      }}
                    >
                      <CardContent sx={{ p: 2.5 }}>
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                          <Avatar
                            src={company.logoUrl}
                            sx={{
                              width: 56,
                              height: 56,
                              bgcolor: 'primary.main',
                              fontSize: '1.3rem',
                              fontWeight: 800,
                            }}
                          >
                            {company.logoUrl ? null : company.name.charAt(0).toUpperCase()}
                          </Avatar>
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexWrap: 'wrap' }}>
                              <Typography variant="h6" sx={{ fontWeight: 800, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                {company.name}
                              </Typography>
                              {company.isVerified && (
                                <Chip
                                  icon={<VerifiedIcon />}
                                  label="Verified"
                                  size="small"
                                  color="success"
                                  variant="outlined"
                                />
                              )}
                              {companyMemberMetaById[company.id]?.role && (
                                <Chip
                                  label={String(companyMemberMetaById[company.id].role).toUpperCase()}
                                  size="small"
                                  variant="outlined"
                                />
                              )}
                            </Box>

                            {company.tagline && (
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{
                                  mb: 1.25,
                                  display: '-webkit-box',
                                  WebkitLineClamp: 2,
                                  WebkitBoxOrient: 'vertical',
                                  overflow: 'hidden',
                                }}
                              >
                                {company.tagline}
                              </Typography>
                            )}

                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                              {company.industry && (
                                <Chip size="small" variant="outlined" label={company.industry} />
                              )}
                              {company.isHiring && (
                                <Chip size="small" variant="outlined" label="Hiring" color="primary" />
                              )}
                              {company.isRemoteFriendly && (
                                <Chip size="small" variant="outlined" label="Remote" color="success" />
                              )}
                            </Box>

                            <Box sx={{ mt: 2 }}>
                              <Button
                                variant="outlined"
                                size="small"
                                startIcon={<BusinessIcon />}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigate(`/dashboard?company=${company.id}&name=${encodeURIComponent(company.name)}`);
                                }}
                                sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 800 }}
                              >
                                Unternehmen-Dashboard
                              </Button>
                            </Box>
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  ))}
                </Box>
              );
            })()}
          </Card>
        </TabPanel>
        )}

        <TabPanel value={tabValue} index={(window as any).__dashIdx.productsIndex}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Box>
              <Typography variant="h5" fontWeight="bold" gutterBottom>
                {isCompanyDashboard ? t('dashboard.products.title.company') : t('dashboard.products.title.user')}
              </Typography>
              <Typography variant="body1" color="text.secondary">
                {isCompanyDashboard 
                  ? t('dashboard.products.subtitle.company')
                  : t('dashboard.products.subtitle.user')
                }
              </Typography>
            </Box>
            {!isCompanyDashboard && (
              <FormControlLabel
                control={
                  <Switch
                    checked={showPricesWithFees}
                    onChange={(e) => setShowPricesWithFees(e.target.checked)}
                    color="primary"
                  />
                }
                label={
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {showPricesWithFees ? t('dashboard.products.switch.customerPrice') : t('dashboard.products.switch.basePrice')}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.7rem' }}>
                      {showPricesWithFees ? t('dashboard.products.switch.customerPrice.caption') : t('dashboard.products.switch.basePrice.caption')}
                    </Typography>
                  </Box>
                }
                sx={{ ml: 2 }}
              />
            )}
          </Box>

          {loading ? (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
              {[1, 2, 3].map((index) => (
                <Card key={index} sx={{ width: 320, borderRadius: 2, boxShadow: 1 }}>
                  <Skeleton variant="rectangular" height={200} />
                  <CardContent>
                    <Skeleton variant="text" height={32} />
                    <Skeleton variant="text" height={24} width="60%" />
                    <Skeleton variant="text" height={20} width="80%" />
                    <Box sx={{ display: 'flex', gap: 2, my: 2 }}>
                      {[1, 2, 3, 4].map((i) => (
                        <Box key={i} sx={{ textAlign: 'center', flex: 1 }}>
                          <Skeleton variant="text" height={24} />
                          <Skeleton variant="text" height={16} width="60%" />
                        </Box>
                      ))}
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Skeleton variant="rectangular" height={32} sx={{ flex: 1 }} />
                      <Skeleton variant="rectangular" height={32} sx={{ flex: 1 }} />
                      <Skeleton variant="rectangular" height={32} width={32} />
                    </Box>
                  </CardContent>
                </Card>
              ))}
            </Box>
          ) : error ? (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          ) : userProducts.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <Typography variant="h6" color="text.secondary" gutterBottom>
                {isCompanyDashboard ? t('dashboard.products.empty.title.company') : t('dashboard.products.empty.title.user')}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                {isCompanyDashboard 
                  ? t('dashboard.products.empty.subtitle.company')
                  : t('dashboard.products.empty.subtitle.user')
                }
              </Typography>
              <Button
                variant="contained"
                startIcon={<UploadIcon />}
                onClick={() => navigate(isCompanyDashboard ? `/upload-job?company=${companyId}&name=${encodeURIComponent(companyName || '')}` : '/upload-product')}
                sx={{ px: 4 }}
              >
                {isCompanyDashboard ? t('dashboard.products.empty.button.company') : t('dashboard.products.empty.button.user')}
              </Button>
            </Box>
          ) : (
            <>
              {/* Cache Status Indicator */}
              {!isCompanyDashboard && (
                <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Chip
                    label={t('dashboard.products.cache.label').replace('{used}', String(productsCache.size)).replace('{max}', String(MAX_CACHE_SIZE))}
                    size="small"
                    color={productsCache.size > 0 ? 'success' : 'default'}
                    variant="outlined"
                    sx={{ fontSize: '0.75rem' }}
                  />
                  {productsCache.size > 0 && (
                    <Chip
                      label={t('dashboard.products.cache.clear')}
                      size="small"
                      color="warning"
                      variant="outlined"
                      onClick={clearCache}
                      sx={{ 
                        fontSize: '0.75rem', 
                        cursor: 'pointer',
                        '&:hover': { bgcolor: 'warning.light', color: 'white' }
                      }}
                    />
                  )}
                </Box>
              )}

              {/* Pagination Component */}
              <ProductPagination
                currentPage={productsCurrentPage}
                totalPages={productsTotalPages}
                itemsPerPage={productsPerPage}
                totalItems={productsTotalCount}
                onPageChange={handleProductsPageChange}
                onItemsPerPageChange={handleProductsPerPageChange}
                itemsPerPageOptions={[6, 12, 24, 48]}
                showItemsPerPageSelector={true}
              />

              {/* Products Grid */}
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
                {userProducts.filter(p => !hiddenProductIds.includes(p.id)).map((product, index) => (
              <motion.div
                key={product.id}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
              >
                <Card 
                  sx={{ 
                    width: 320, 
                    borderRadius: 3, 
                    boxShadow: isCompanyDashboard ? 2 : 1,
                    cursor: (product.is_active && !product.is_sold) ? 'pointer' : 'default',
                    '&:hover': { 
                      boxShadow: (product.is_active && !product.is_sold) ? (isCompanyDashboard ? 4 : 3) : 1,
                      transform: (product.is_active && !product.is_sold) ? 'translateY(-2px)' : 'none',
                      transition: 'all 0.3s ease'
                    },
                    border: isCompanyDashboard 
                      ? '1px solid #e0e0e0' 
                      : product.auction_id 
                        ? '2px solid #f44336' 
                        : 'none',
                    background: isCompanyDashboard 
                      ? 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)' 
                      : product.auction_id 
                        ? 'linear-gradient(135deg, #fff5f5 0%, #ffffff 100%)'
                        : 'background.paper'
                  }}
                  onClick={() => {
                    if (product.is_active && !product.is_sold) {
                      navigate(`/product/${product.id}`);
                    }
                  }}
                >
                  <Box sx={{ position: 'relative' }}>
                    {!product.is_job && (
                      <>
                        <Box
                          component="img"
                          src={product.images && product.images.length > 0 ? product.images[0] : '/placeholder-product.jpg'}
                          alt={product.name}
                          sx={{
                            width: '100%',
                            height: 200,
                            objectFit: 'cover',
                            borderRadius: '8px 8px 0 0',
                            filter: (!product.is_active || product.is_sold) ? 'grayscale(100%) brightness(0.6)' : 'none',
                            transition: 'filter 0.3s ease',
                          }}
                        />
                        {/* Grayed out overlay for sold/inactive products */}
                        {(!product.is_active || product.is_sold) && (
                          <Box
                            sx={{
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              right: 0,
                              bottom: 0,
                              bgcolor: 'rgba(0, 0, 0, 0.4)',
                              borderRadius: '8px 8px 0 0',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            {product.is_sold ? (
                              <Box
                                sx={{
                                  bgcolor: 'rgba(0, 0, 0, 0.7)',
                                  borderRadius: '50%',
                                  p: 2,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                }}
                              >
                                <CheckIcon sx={{ color: 'white', fontSize: 32 }} />
                              </Box>
                            ) : (
                              <Box
                                sx={{
                                  bgcolor: 'rgba(0, 0, 0, 0.7)',
                                  borderRadius: '50%',
                                  p: 2,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                }}
                              >
                                <CloseIcon sx={{ color: 'white', fontSize: 32 }} />
                              </Box>
                            )}
                          </Box>
                        )}
                      </>
                    )}

                    {/* Overlay chips + menu only for non-job products (jobs use an inline header below) */}
                    {!product.is_job && (
                      <>
                        <Box sx={{ position: 'absolute', top: 8, left: 8, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                          <Chip
                            label={product.is_sold ? t('dashboard.products.status.sold') : (product.is_active ? t('dashboard.products.status.active') : t('dashboard.products.status.inactive'))}
                            color={(product.is_sold ? 'secondary' : (product.is_active ? 'success' : 'warning')) as any}
                            size="small"
                            sx={{
                              fontWeight: 'bold',
                              bgcolor: product.is_sold ? '#9c27b0' : (product.is_active ? '#4caf50' : '#ff9800'),
                              color: 'white',
                              '& .MuiChip-label': {
                                color: 'white',
                              }
                            }}
                          />
                          {product.is_featured && (
                            <Chip
                              label={t('dashboard.products.badge.featured')}
                              color="warning"
                              size="small"
                              sx={{
                                fontWeight: 'bold',
                                bgcolor: '#ff9800',
                                color: 'white',
                                '& .MuiChip-label': {
                                  color: 'white',
                                }
                              }}
                            />
                          )}
                          {/* Auction Indicator */}
                          {product.auction_id && product.auction && (
                            <Chip
                              label={t('dashboard.products.badge.auction')}
                              color="error"
                              size="small"
                              sx={{
                                fontWeight: 'bold',
                                bgcolor: '#f44336',
                                color: 'white',
                                '& .MuiChip-label': {
                                  color: 'white',
                                }
                              }}
                            />
                          )}
                        </Box>
                        <IconButton
                          onClick={(e) => {
                            e.stopPropagation();
                            handleProductMenuOpen(e, product);
                          }}
                          sx={{
                            position: 'absolute',
                            top: 8,
                            right: 8,
                            bgcolor: 'white',
                            boxShadow: 1,
                            '&:hover': { bgcolor: 'grey.100' },
                          }}
                        >
                          <MoreIcon />
                        </IconButton>
                      </>
                    )}
                  </Box>
                  
                  <CardContent>
                    {product.is_job && (
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1.5, mb: 1.25 }}>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, pr: 0.5 }}>
                          <Chip
                            label={product.is_sold ? t('dashboard.products.status.sold') : (product.is_active ? t('dashboard.products.status.active') : t('dashboard.products.status.inactive'))}
                            color={(product.is_sold ? 'secondary' : (product.is_active ? 'success' : 'warning')) as any}
                            size="small"
                            sx={{
                              fontWeight: 900,
                              bgcolor: product.is_sold ? '#9c27b0' : (product.is_active ? '#4caf50' : '#ff9800'),
                              color: 'white',
                              '& .MuiChip-label': { color: 'white' },
                            }}
                          />
                          {product.is_featured && (
                            <Chip
                              label={t('dashboard.products.badge.featured')}
                              color="warning"
                              size="small"
                              sx={{
                                fontWeight: 900,
                                bgcolor: '#ff9800',
                                color: 'white',
                                '& .MuiChip-label': { color: 'white' },
                              }}
                            />
                          )}
                          {product.auction_id && product.auction && (
                            <Chip
                              label={t('dashboard.products.badge.auction')}
                              color="error"
                              size="small"
                              sx={{
                                fontWeight: 900,
                                bgcolor: '#f44336',
                                color: 'white',
                                '& .MuiChip-label': { color: 'white' },
                              }}
                            />
                          )}
                        </Box>

                        <IconButton
                          onClick={(e) => {
                            e.stopPropagation();
                            handleProductMenuOpen(e, product);
                          }}
                          size="small"
                          sx={{
                            mt: -0.25,
                            bgcolor: 'grey.100',
                            '&:hover': { bgcolor: 'grey.200' },
                            flexShrink: 0,
                          }}
                        >
                          <MoreIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    )}

                    <Typography 
                      variant="h6" 
                      fontWeight="bold" 
                      gutterBottom
                      sx={{
                        fontSize: isCompanyDashboard ? '1.1rem' : '1rem',
                        color: isCompanyDashboard ? '#1976d2' : 'text.primary',
                        lineHeight: 1.3,
                        mb: 2
                      }}
                    >
                      {(autoTranslate && language !== 'en' && translatedProducts[product.id]?.name) ? translatedProducts[product.id].name : product.name}
                    </Typography>
                    
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      {(() => {
                        // Calculate price with fees (only for products, not jobs)
                        // In My Products tab, respect the toggle setting
                        const basePrice = product.price || 0;
                        const isJob = isCompanyDashboard;
                        const shouldApplyFees = !isJob && platformFeeSettings && showPricesWithFees;
                        const displayPrice = shouldApplyFees
                          ? calculatePriceWithFeesSync(basePrice, platformFeeSettings)
                          : basePrice;
                        const baseOriginalPrice = product.original_price;
                        const displayOriginalPrice = shouldApplyFees && baseOriginalPrice
                          ? calculatePriceWithFeesSync(baseOriginalPrice, platformFeeSettings)
                          : baseOriginalPrice;
                        
                        return (
                          <>
                            <Typography 
                              variant="h6" 
                              color="primary.main" 
                              fontWeight="bold"
                              sx={{
                                fontSize: isCompanyDashboard ? '1.2rem' : '1rem',
                                background: isCompanyDashboard ? 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)' : 'none',
                                backgroundClip: isCompanyDashboard ? 'text' : 'none',
                                WebkitBackgroundClip: isCompanyDashboard ? 'text' : 'none',
                                WebkitTextFillColor: isCompanyDashboard ? 'transparent' : 'inherit',
                                textShadow: isCompanyDashboard ? '0 1px 2px rgba(0,0,0,0.1)' : 'none'
                              }}
                            >
                              {(() => {
                                const rawType = (product as any).salary_type ?? (product as any).salaryType ?? (product as any).sku;
                                const st = typeof rawType === 'string' ? rawType.toLowerCase() : '';
                                const notSpecified = isCompanyDashboard && (!displayPrice || displayPrice <= 0 || st === 'negotiable');
                                if (notSpecified) return 'Gehalt nicht angegeben';
                                return isCompanyDashboard ? `$${displayPrice.toFixed(2)}/hr` : `$${displayPrice.toFixed(2)}`;
                              })()}
                            </Typography>
                            {displayOriginalPrice && !isCompanyDashboard && (
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{ textDecoration: 'line-through' }}
                              >
                                ${displayOriginalPrice.toFixed(2)}
                              </Typography>
                            )}
                          </>
                        );
                      })()}
                    </Box>

                    {/* Auction Countdown Timer */}
                    {product.auction_id && product.auction && (
                      <Box sx={{ mb: 2 }}>
                        <Box sx={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: 1,
                          p: 1.5,
                          borderRadius: 2,
                          bgcolor: getAuctionTimeRemaining(product.auction.end_time).expired 
                            ? 'rgba(244, 67, 54, 0.1)' 
                            : 'rgba(255, 152, 0, 0.1)',
                          border: `1px solid ${getAuctionTimeRemaining(product.auction.end_time).expired 
                            ? '#f44336' 
                            : '#ff9800'}`,
                        }}>
                          <AccessTimeIcon sx={{ 
                            color: getAuctionTimeRemaining(product.auction.end_time).expired 
                              ? '#f44336' 
                              : '#ff9800',
                            fontSize: 18 
                          }} />
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              fontWeight: 'bold',
                              color: getAuctionTimeRemaining(product.auction.end_time).expired 
                                ? '#f44336' 
                                : '#ff9800',
                              fontSize: '0.875rem'
                            }}
                          >
                            {getAuctionTimeRemaining(product.auction.end_time).text}
                          </Typography>
                        </Box>
                        {!getAuctionTimeRemaining(product.auction.end_time).expired && (
                          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                            {t('dashboard.products.label.currentBid')} ${product.auction.current_bid.toFixed(2)}
                          </Typography>
                        )}
                      </Box>
                    )}

                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 2 }}>
                      {(() => {
                        const paths: string[] = Array.isArray((product as any).categories) ? (product as any).categories : [];
                        // Collect unique segments across all selected paths (Main/Sub/Sub-sub)
                        const segments: string[] = [];
                        paths.forEach((p) => {
                          if (typeof p !== 'string') return;
                          p.split(' / ').map(s => s.trim()).filter(Boolean).forEach(seg => {
                            if (!segments.includes(seg)) segments.push(seg);
                          });
                        });
                        // Fallback to old fields if no categories array
                        if (segments.length === 0) {
                          if (product.category_name) segments.push(product.category_name);
                          if (product.subcategory_name) segments.push(product.subcategory_name);
                        }
                        // Render chips
                        return (
                          <>
                            {segments.slice(0, 6).map((seg, idx) => (
                              <Chip
                                key={`${product.id}-seg-${idx}-${seg}`}
                                label={seg}
                                size="small"
                                variant="outlined"
                                sx={{
                                  fontSize: '0.72rem',
                                  borderRadius: 1.5,
                                  borderColor: 'divider',
                                }}
                              />
                            ))}
                            <Chip
                              label={product.brand || t('dashboard.products.label.noBrand')}
                              size="small"
                              sx={{
                                fontSize: '0.72rem',
                                borderRadius: 1.5,
                                bgcolor: '#f0f4ff',
                                color: '#1e40af'
                              }}
                            />
                          </>
                        );
                      })()}
                    </Box>

                    {/* Job-specific Information */}
                    {isCompanyDashboard && (
                      <Box sx={{ mb: 2 }}>
                        {/* Job Type and Experience Level */}
                        <Box sx={{ display: 'flex', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                          {product.job_type && (
                            <Chip
                              label={product.job_type.replace('-', ' ').toUpperCase()}
                              size="small"
                              sx={{
                                bgcolor: '#e3f2fd',
                                color: '#1976d2',
                                fontWeight: 'bold',
                                fontSize: '0.75rem'
                              }}
                            />
                          )}
                          {product.experience_level && (
                            <Chip
                              label={product.experience_level.replace('-', ' ').toUpperCase()}
                              size="small"
                              sx={{
                                bgcolor: '#f3e5f5',
                                color: '#7b1fa2',
                                fontWeight: 'bold',
                                fontSize: '0.75rem'
                              }}
                            />
                          )}
                          {product.remote_work_allowed && (
                            <Chip
                              label={t('dashboard.products.label.remote')}
                              size="small"
                              sx={{
                                bgcolor: '#e8f5e8',
                                color: '#2e7d32',
                                fontWeight: 'bold',
                                fontSize: '0.75rem'
                              }}
                            />
                          )}
                        </Box>

                        {/* Location */}
                        {product.location && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                            <LocationIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                            <Typography variant="body2" color="text.secondary">
                              {product.location}
                            </Typography>
                          </Box>
                        )}

                        {/* Duration */}
                        {product.duration && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                            <ScheduleIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                            <Typography variant="body2" color="text.secondary">
                              {product.duration}
                            </Typography>
                          </Box>
                        )}

                        {/* Application Deadline */}
                        {product.application_deadline && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                            <EventIcon sx={{ fontSize: 16, color: 'warning.main' }} />
                            <Typography variant="body2" color="warning.main" fontWeight="bold">
                              {t('dashboard.products.label.applyBy')} {new Date(product.application_deadline).toLocaleDateString()}
                            </Typography>
                          </Box>
                        )}

                        {/* Expected Start Date */}
                        {product.expected_start_date && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                            <ScheduleIcon sx={{ fontSize: 16, color: 'success.main' }} />
                            <Typography variant="body2" color="success.main" fontWeight="bold">
                              {t('dashboard.products.label.start')} {new Date(product.expected_start_date).toLocaleDateString()}
                            </Typography>
                          </Box>
                        )}

                        {/* Contact Email */}
                        {product.contact_email && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                            <MessageIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                            <Typography variant="body2" color="text.secondary">
                              {t('dashboard.products.label.contact')} {product.contact_email}
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    )}

                    {/* Performance Stats - Only for regular products, not jobs */}
                    {!isCompanyDashboard && (
                      <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                        <Box sx={{ textAlign: 'center' }}>
                          <Typography variant="h6" fontWeight="bold" color="success.main">
                            {product.number_sold || 0}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {t('dashboard.products.stats.sold')}
                          </Typography>
                        </Box>
                        <Box sx={{ textAlign: 'center' }}>
                          <Typography variant="h6" fontWeight="bold" color="info.main">
                            {product.view_count || 0}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {t('dashboard.products.stats.views')}
                          </Typography>
                        </Box>
                        <Box sx={{ textAlign: 'center' }}>
                          <Typography variant="h6" fontWeight="bold" color="warning.main">
                            {product.stock || 0}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {t('dashboard.products.stats.stock')}
                          </Typography>
                        </Box>
                        <Box sx={{ textAlign: 'center' }}>
                          <Typography variant="h6" fontWeight="bold" color="primary.main">
                            {product.rating ? product.rating.toFixed(1) : 'N/A'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {t('dashboard.products.stats.rating')}
                          </Typography>
                        </Box>
                      </Box>
                    )}

                    {/* Job Applications and Views - Only for jobs */}
                    {isCompanyDashboard && (
                      <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                        <Box sx={{ textAlign: 'center' }}>
                          <Typography variant="h6" fontWeight="bold" color="success.main">
                            {product.number_sold || 0}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {t('dashboard.products.label.applications')}
                          </Typography>
                        </Box>
                        <Box sx={{ textAlign: 'center' }}>
                          <Typography variant="h6" fontWeight="bold" color="info.main">
                            {product.view_count || 0}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {t('dashboard.products.stats.views')}
                          </Typography>
                        </Box>
                      </Box>
                    )}

                    {/* Tags */}
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 2 }}>
                      {product.tags.slice(0, 3).map((tag, idx) => (
                        <Chip
                          key={idx}
                          label={tag}
                          size="small"
                          variant="outlined"
                          sx={{ fontSize: '0.7rem' }}
                        />
                      ))}
                    </Box>

                    {/* Benefits/Perks - Only for jobs */}
                    {isCompanyDashboard && product.benefits_perks && product.benefits_perks.length > 0 && (
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                          Benefits & Perks:
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {product.benefits_perks.slice(0, 3).map((benefit: string, idx: number) => (
                            <Chip
                              key={idx}
                              label={benefit}
                              size="small"
                              sx={{
                                bgcolor: '#e8f5e8',
                                color: '#2e7d32',
                                fontSize: '0.7rem',
                                fontWeight: 'bold'
                              }}
                            />
                          ))}
                          {product.benefits_perks.length > 3 && (
                            <Chip
                              label={`+${product.benefits_perks.length - 3} more`}
                              size="small"
                              variant="outlined"
                              sx={{ fontSize: '0.7rem' }}
                            />
                          )}
                        </Box>
                      </Box>
                    )}

                    {/* Action Buttons */}
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<ViewIcon />}
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/product/${product.id}`);
                        }}
                        sx={{ flex: 1 }}
                      >
                        {t('dashboard.products.button.view')}
                      </Button>
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<EditIcon />}
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(product.is_job ? `/edit-job/${product.id}` : `/edit-product/${product.id}`, {
                            state: { fromDashboard: true }
                          });
                        }}
                        sx={{
                          backgroundColor: '#ff9800',
                        }}
                      >
                        {t('dashboard.products.button.edit')}
                      </Button>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteProduct(product.id, product.name);
                        }}
                        sx={{ border: '1px solid', borderColor: 'error.main' }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>

                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                      {t('dashboard.products.label.lastUpdated')} {new Date(product.updated_at).toLocaleDateString()}
                    </Typography>
                  </CardContent>
                </Card>
              </motion.div>
                ))}
              </Box>
            </>
          )}

        </TabPanel>

        {(!is_job || isCompanyDashboard) && (
        <TabPanel value={tabValue} index={(window as any).__dashIdx.uploadIndex}>
          <Typography variant="h5" fontWeight="bold" gutterBottom>
            {isCompanyDashboard ? t('dashboard.upload.title.job') : t('dashboard.upload.title.product')}
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            {isCompanyDashboard 
              ? t('dashboard.upload.subtitle.job')
              : t('dashboard.upload.subtitle.product')
            }
          </Typography>

          <Card sx={{ borderRadius: 2, boxShadow: 1 }}>
            <CardContent sx={{ textAlign: 'center', py: 6 }}>
              <UploadIcon sx={{ fontSize: 80, color: 'primary.main', mb: 2 }} />
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                {isCompanyDashboard ? t('dashboard.upload.card.heading.job') : t('dashboard.upload.card.heading.product')}
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                {isCompanyDashboard 
                  ? t('dashboard.upload.card.description.job')
                  : t('dashboard.upload.card.description.product')
                }
              </Typography>
              <Button
                variant="contained"
                size="large"
                startIcon={<UploadIcon />}
                sx={{ px: 4, py: 1.5 }}
                onClick={() => navigate(isCompanyDashboard ? `/upload-job?company=${companyId}&name=${encodeURIComponent(companyName || '')}` : '/upload-product')}
              >
                {isCompanyDashboard ? t('dashboard.upload.button.job') : t('dashboard.upload.button.product')}
              </Button>
            </CardContent>
          </Card>
        </TabPanel>
        )}

        {/* Documents Tab */}
        <TabPanel value={tabValue} index={(window as any).__dashIdx.documentsIndex}>
          <Typography variant="h5" fontWeight="bold" gutterBottom>
            {t('dashboard.documents.title')}
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            {t('dashboard.documents.subtitle')}
          </Typography>

          <Card sx={{ borderRadius: 3, boxShadow: 2 }}>
            <CardContent sx={{ p: 4, textAlign: 'center' }}>
              <DocumentsIcon sx={{ fontSize: 64, color: 'primary.main', mb: 2 }} />
              <Typography variant="h6" gutterBottom>
                {t('dashboard.documents.card.title')}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                {t('dashboard.documents.card.description')}
              </Typography>
              <Button
                variant="contained"
                size="large"
                startIcon={<DocumentsIcon />}
                sx={{ px: 4, py: 1.5 }}
                onClick={() => setDocumentsModalOpen(true)}
              >
                {t('dashboard.documents.card.openManager')}
              </Button>
            </CardContent>
          </Card>
        </TabPanel>

        {/* Talent Pool Tab - Only for Company Dashboard */}
        {isCompanyDashboard && (
        <TabPanel value={tabValue} index={(window as any).__dashIdx.talentPoolIndex}>
          <TalentPoolSection />
        </TabPanel>
        )}
      </Container>
      {/* Accept Confirmation Dialog */}
      <Dialog open={!!confirmOffer} onClose={() => setConfirmOffer(null)}>
        <DialogTitle>Confirm Offer Acceptance</DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 1.5 }}>
            Are you sure you want to accept this offer for
            {' '}<strong>${confirmOffer?.offer_price}</strong> on
            {' '}<strong>{confirmOffer ? (productInfoById[confirmOffer.product_id]?.name || 'this product') : ''}</strong>?
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Accepting is final. You agree to sell this product to the buyer at the offered price. You will not be able to decline this offer afterwards.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOffer(null)} startIcon={<CloseIcon />}>Cancel</Button>
          <Button
            variant="contained"
            color="success"
            startIcon={<CheckIcon />}
            disabled={!confirmOffer || confirmLoading}
            onClick={async () => {
              if (!confirmOffer) return;
              try {
                setConfirmLoading(true);
                const updated = await OffersService.acceptOffer(confirmOffer.id);
                setOffersReceived(prev => prev.map(o => o.id === confirmOffer.id ? updated : o));
                // Refresh user products so status chips reflect is_active/is_sold changes
                try {
                  if (isCompanyDashboard) {
                    await loadProducts(productsCurrentPage, productsPerPage);
                  } else {
                    const refreshed = await UserProductService.getUserProducts();
                    setUserProducts(refreshed);
                  }
                } catch {}
                // Refresh purchases so the accepted offer appears in the orders tab
                await refreshPurchases();
                setConfirmOffer(null);
              } catch (error: any) {
                console.error('Error accepting offer:', error);
                // Show error to user
                alert(`Failed to accept offer: ${error?.message || 'Unknown error'}. Please try again.`);
              } finally {
                setConfirmLoading(false);
              }
            }}
          >
            Accept Offer
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Offer Confirmation Dialog */}
      <Dialog open={!!deleteOffer} onClose={() => setDeleteOffer(null)}>
        <DialogTitle>Withdraw/Delete Offer</DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 1.5 }}>
            Are you sure you want to withdraw/delete your offer of
            {' '}<strong>${deleteOffer?.offer_price}</strong> for
            {' '}<strong>{deleteOffer ? (productInfoById[deleteOffer.product_id]?.name || 'this product') : ''}</strong>?
          </Typography>
          <Typography variant="body2" color="text.secondary">
            This action cannot be undone. The offer will be permanently removed and the seller will no longer see it.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteOffer(null)} startIcon={<CloseIcon />}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            startIcon={<DeleteIcon />}
            disabled={!deleteOffer || deleteLoading}
            onClick={async () => {
              if (!deleteOffer) return;
              try {
                setDeleteLoading(true);
                setDebugError(null); // Clear any previous debug errors
                console.log('UserDashboard - Attempting to delete offer:', deleteOffer.id);
                
                // Debug: Check if offer exists and user permissions
                console.log('UserDashboard - Debug info:', {
                  offerId: deleteOffer.id,
                  offerStatus: deleteOffer.status,
                  buyerId: deleteOffer.buyer_id,
                  sellerId: deleteOffer.seller_id,
                  productId: deleteOffer.product_id,
                  currentUser: user?.id
                });
                
                await OffersService.deleteOffer(deleteOffer.id);
                setOffersMade(prev => prev.filter(o => o.id !== deleteOffer.id));
                setDeleteOffer(null);
                toast.success('Offer withdrawn successfully');
              } catch (error) {
                console.error('UserDashboard - Failed to delete offer:', error);
                console.error('UserDashboard - Error details:', {
                  message: error instanceof Error ? error.message : 'Unknown error',
                  stack: error instanceof Error ? error.stack : undefined,
                  offerId: deleteOffer.id,
                  offerStatus: deleteOffer.status
                });
                
                // Show more specific error message
                let errorMessage = 'Failed to withdraw offer. Please try again.';
                if (error instanceof Error) {
                  if (error.message.includes('not authenticated')) {
                    errorMessage = 'You must be logged in to withdraw offers.';
                  } else if (error.message.includes('not found') || error.message.includes('permission')) {
                    errorMessage = 'Offer not found or you do not have permission to delete it.';
                  } else if (error.message.includes('network') || error.message.includes('fetch')) {
                    errorMessage = 'Network error. Please check your connection and try again.';
                  } else {
                    errorMessage = `Failed to withdraw offer: ${error.message}`;
                  }
                }
                
                // Show detailed error in console AND in a longer toast
                console.error('=== DETAILED ERROR FOR OFFER WITHDRAWAL ===');
                console.error('Error object:', error);
                console.error('Error message:', error instanceof Error ? error.message : 'Unknown error');
                console.error('Error stack:', error instanceof Error ? error.stack : 'No stack trace');
                console.error('Offer details:', {
                  id: deleteOffer.id,
                  status: deleteOffer.status,
                  buyerId: deleteOffer.buyer_id,
                  sellerId: deleteOffer.seller_id
                });
                console.error('Current user:', user?.id);
                console.error('==========================================');
                
                // Store debug error for UI display
                const debugInfo = `Error: ${error instanceof Error ? error.message : 'Unknown error'}\n\nOffer ID: ${deleteOffer.id}\nStatus: ${deleteOffer.status}\nBuyer ID: ${deleteOffer.buyer_id}\nCurrent User: ${user?.id}\n\nCheck browser console (F12) for full details.`;
                setDebugError(debugInfo);
                
                // Show error in toast with more details
                toast.error(`${errorMessage}\n\nCheck browser console (F12) for detailed error information.`, {
                  duration: 8000,
                  style: {
                    whiteSpace: 'pre-line',
                    maxWidth: '400px'
                  }
                });
              } finally {
                setDeleteLoading(false);
              }
            }}
          >
            {deleteLoading ? 'Withdrawing...' : 'Withdraw Offer'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Debug Error Display */}
      {debugError && (
        <Dialog open={!!debugError} onClose={() => setDebugError(null)} maxWidth="md" fullWidth>
          <DialogTitle>Debug Information - Offer Withdrawal Error</DialogTitle>
          <DialogContent>
            <Typography variant="body2" component="pre" sx={{ 
              whiteSpace: 'pre-wrap', 
              fontFamily: 'monospace',
              backgroundColor: '#f5f5f5',
              padding: 2,
              borderRadius: 1,
              maxHeight: '400px',
              overflow: 'auto'
            }}>
              {debugError}
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDebugError(null)}>Close</Button>
            <Button 
              onClick={() => {
                navigator.clipboard.writeText(debugError);
                toast.success('Debug info copied to clipboard');
              }}
              variant="outlined"
            >
              Copy to Clipboard
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {/* Product Management Menu */}
      <Dialog
        open={!!productMenuAnchor}
        onClose={handleProductMenuClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Manage Product: {selectedProduct?.name}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            {/* Status Toggle */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography variant="body1" fontWeight="bold">
                Product Status
              </Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  variant={selectedProduct?.is_active ? 'contained' : 'outlined'}
                  color={selectedProduct?.is_active ? 'success' : 'primary'}
                  onClick={() => handleProductUpdate({ is_active: !selectedProduct?.is_active })}
                  disabled={updatingProduct}
                  size="small"
                >
                  {selectedProduct?.is_active ? 'Active' : 'Inactive'}
                </Button>
              </Box>
            </Box>

            {/* Featured Toggle */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography variant="body1" fontWeight="bold">
                Featured Product
              </Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  variant={selectedProduct?.is_featured ? 'contained' : 'outlined'}
                  color={selectedProduct?.is_featured ? 'warning' : 'primary'}
                  onClick={() => handleProductUpdate({ is_featured: !selectedProduct?.is_featured })}
                  disabled={updatingProduct}
                  size="small"
                >
                  {selectedProduct?.is_featured ? 'Featured' : 'Not Featured'}
                </Button>
              </Box>
            </Box>

            {/* Sold Status Toggle */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography variant="body1" fontWeight="bold">
                Sold Status
              </Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  variant={selectedProduct?.is_sold ? 'contained' : 'outlined'}
                  color={selectedProduct?.is_sold ? 'secondary' : 'primary'}
                  onClick={() => handleProductUpdate({ is_sold: !selectedProduct?.is_sold })}
                  disabled={updatingProduct}
                  size="small"
                >
                  {selectedProduct?.is_sold ? 'Sold' : 'Available'}
                </Button>
              </Box>
            </Box>

            {/* Current Status Display */}
            <Box sx={{ 
              bgcolor: 'grey.50', 
              p: 2, 
              borderRadius: 1, 
              border: '1px solid', 
              borderColor: 'grey.200' 
            }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Current Status:
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Chip
                  label={selectedProduct?.is_active ? 'Active' : 'Inactive'}
                  color={selectedProduct?.is_active ? 'success' : 'warning'}
                  size="small"
                  sx={{
                    fontWeight: 'bold',
                    bgcolor: selectedProduct?.is_active ? '#4caf50' : '#ff9800',
                    color: 'white',
                    '& .MuiChip-label': {
                      color: 'white',
                    }
                  }}
                />
                {selectedProduct?.is_featured && (
                  <Chip 
                    label="Featured" 
                    color="warning" 
                    size="small"
                    sx={{
                      fontWeight: 'bold',
                      bgcolor: '#ff9800',
                      color: 'white',
                      '& .MuiChip-label': {
                        color: 'white',
                      }
                    }}
                  />
                )}
                {selectedProduct?.is_sold && (
                  <Chip 
                    label="Sold" 
                    color="secondary" 
                    size="small"
                    sx={{
                      fontWeight: 'bold',
                      bgcolor: '#9c27b0',
                      color: 'white',
                      '& .MuiChip-label': {
                        color: 'white',
                      }
                    }}
                  />
                )}
              </Box>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleProductMenuClose}>
            Close
          </Button>
          <Button
            variant="outlined"
            color="error"
            onClick={() => {
              if (selectedProduct) {
                handleProductMenuClose();
                handleDeleteProduct(selectedProduct.id, selectedProduct.name);
              }
            }}
            disabled={updatingProduct}
          >
            Delete Product
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              if (selectedProduct) {
                handleProductMenuClose();
                navigate(selectedProduct.is_job ? `/edit-job/${selectedProduct.id}` : `/edit-product/${selectedProduct.id}`, {
                  state: { fromDashboard: true }
                });
              }
            }}
            disabled={updatingProduct}
          >
            Edit
          </Button>
        </DialogActions>
      </Dialog>

      {/* Beautiful Delete Confirmation Dialog */}
      <Dialog
        open={!!deleteProduct}
        onClose={() => setDeleteProduct(null)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            overflow: 'hidden',
            background: 'linear-gradient(135deg, #ff6b6b 0%, #ee5a52 100%)',
            boxShadow: '0 20px 40px rgba(255, 107, 107, 0.3)',
          }
        }}
      >
        <motion.div
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.3, ease: "easeOut" }}
        >
          <Box sx={{ 
            background: 'linear-gradient(135deg, #ff6b6b 0%, #ee5a52 100%)',
            p: 4,
            textAlign: 'center',
            color: 'white'
          }}>
            {/* Animated Warning Icon */}
            <motion.div
              initial={{ scale: 0, rotate: -180 }}
              animate={{ scale: 1, rotate: 0 }}
              transition={{ duration: 0.5, delay: 0.1, ease: "easeOut" }}
            >
              <Box
                sx={{
                  width: 80,
                  height: 80,
                  borderRadius: '50%',
                  background: 'rgba(255, 255, 255, 0.2)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mx: 'auto',
                  mb: 3,
                  backdropFilter: 'blur(10px)',
                  border: '2px solid rgba(255, 255, 255, 0.3)',
                }}
              >
                <DeleteIcon sx={{ fontSize: 40, color: 'white' }} />
              </Box>
            </motion.div>

            {/* Title */}
            <motion.div
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.4, delay: 0.2 }}
            >
              <Typography variant="h4" fontWeight="bold" gutterBottom>
                Delete Product?
              </Typography>
            </motion.div>

            {/* Product Name */}
            <motion.div
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.4, delay: 0.3 }}
            >
              <Typography variant="h6" sx={{ opacity: 0.9, mb: 2 }}>
                "{deleteProduct?.name}"
              </Typography>
            </motion.div>

            {/* Warning Message */}
            <motion.div
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.4, delay: 0.4 }}
            >
              <Typography variant="body1" sx={{ opacity: 0.8, mb: 3 }}>
                This action cannot be undone. The product and all its data will be permanently removed.
              </Typography>
            </motion.div>

            {/* Action Buttons */}
            <motion.div
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.4, delay: 0.5 }}
            >
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
                <Button
                  variant="outlined"
                  onClick={() => setDeleteProduct(null)}
                  disabled={deletingProduct}
                  sx={{
                    color: 'white',
                    borderColor: 'rgba(255, 255, 255, 0.5)',
                    px: 4,
                    py: 1.5,
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: 'bold',
                    '&:hover': {
                      borderColor: 'white',
                      backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    }
                  }}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  onClick={confirmDeleteProduct}
                  disabled={deletingProduct}
                  sx={{
                    backgroundColor: 'rgba(255, 255, 255, 0.2)',
                    color: 'white',
                    px: 4,
                    py: 1.5,
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: 'bold',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(255, 255, 255, 0.3)',
                    '&:hover': {
                      backgroundColor: 'rgba(255, 255, 255, 0.3)',
                    },
                    '&:disabled': {
                      backgroundColor: 'rgba(255, 255, 255, 0.1)',
                      color: 'rgba(255, 255, 255, 0.5)',
                    }
                  }}
                >
                  {deletingProduct ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CircularProgress size={16} sx={{ color: 'white' }} />
                      Deleting...
                    </Box>
                  ) : (
                    'Delete Forever'
                  )}
                </Button>
              </Box>
            </motion.div>
          </Box>
        </motion.div>
      </Dialog>

      {/* Settings Modal */}
      <SettingsModal
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
      />
      {/* Verification Modal */}
      <VerificationModal
        open={verificationOpen}
        onClose={() => setVerificationOpen(false)}
        userId={(user as any)?.id}
      />
      <VerificationInfoModal
        open={verificationInfoOpen}
        onClose={() => setVerificationInfoOpen(false)}
        userId={(user as any)?.id}
      />

      {/* Job Shuffle Modal - job mode in user profile only */}
      {is_job && !isCompanyDashboard && (
        <JobShuffleModal open={jobShuffleOpen} onClose={() => setJobShuffleOpen(false)} />
      )}


      {/* User Info Modal */}
      <UserInfoModal
        open={userInfoOpen}
        onClose={() => setUserInfoOpen(false)}
        user={user}
        userAlias={userAlias}
        profileImages={profileImages}
      />

      {/* Complete Transaction Popup */}
      <CompleteTransactionPopup
        open={paymentModalOpen}
        onClose={() => {
          setPaymentModalOpen(false);
          setSelectedOrderForPayment(null);
        }}
        order={selectedOrderForPayment ? {
          id: selectedOrderForPayment.id,
          product_id: selectedOrderForPayment.product_id,
          seller_id: selectedOrderForPayment.seller_id,
          purchase_price: selectedOrderForPayment.purchase_price,
        } : null}
        onPaymentSuccess={handlePaymentSuccess}
      />

      {/* Transaction Details Popup */}
      {selectedOrderForDetails && (
        <TransactionDetailsPopup
          open={transactionDetailsOpen}
          onClose={() => {
            setTransactionDetailsOpen(false);
            setSelectedOrderForDetails(null);
          }}
          orderId={selectedOrderForDetails}
        />
      )}

      {/* Delivery Manager Popup (Seller) */}
      {deliveryManagerTarget && (
        <DeliveryManagerPopup
          open={deliveryManagerOpen}
          onClose={() => {
            setDeliveryManagerOpen(false);
            setDeliveryManagerTarget(null);
          }}
          purchaseId={deliveryManagerTarget.id}
          productId={deliveryManagerTarget.product_id}
          buyerId={deliveryManagerTarget.buyer_id}
          sellerId={deliveryManagerTarget.seller_id}
          productName={deliveryManagerTarget.product_name}
          currentStatus={deliveryManagerTarget.delivery_status}
          currentTrackingNumber={deliveryManagerTarget.tracking_number}
          onSaved={() => {
            refreshPurchases();
          }}
        />
      )}

  {/* Leave Review Modal */}
  <ReviewModal
    open={reviewOpen}
    productName={reviewTarget?.product_name}
    productImage={(reviewTarget?.product_images && reviewTarget.product_images[0]) || undefined}
    price={reviewTarget?.purchase_price as any}
    date={reviewTarget?.created_at}
    productId={reviewTarget?.product_id}
    loading={reviewSubmitting}
    rating={reviewRating}
    title={reviewTitle}
    comment={reviewComment}
    onChangeRating={setReviewRating}
    onChangeTitle={setReviewTitle}
    onChangeComment={setReviewComment}
    userShowAlias={reviewUserShowAlias}
    onChangeUserShowAlias={setReviewUserShowAlias}
    priceRating={reviewPriceRating}
    accuracyRating={reviewAccuracyRating}
    deliveryRating={reviewDeliveryRating}
    packagingRating={reviewPackagingRating}
    communicationRating={reviewCommunicationRating}
    qualityRating={reviewQualityRating}
    onChangePriceRating={setReviewPriceRating}
    onChangeAccuracyRating={setReviewAccuracyRating}
    onChangeDeliveryRating={setReviewDeliveryRating}
    onChangePackagingRating={setReviewPackagingRating}
    onChangeCommunicationRating={setReviewCommunicationRating}
    onChangeQualityRating={setReviewQualityRating}
    onClose={() => setReviewOpen(false)}
    onSubmit={handleSubmitReview}
  />

      {/* User Documents Modal */}
      <UserDocumentsModal
        open={documentsModalOpen}
        onClose={() => setDocumentsModalOpen(false)}
        companyId={companyId}
        companyName={companyName}
      />

      {/* Profile Preview Modal */}
      <ProfilePreviewModal
        open={profilePreviewOpen}
        onClose={() => setProfilePreviewOpen(false)}
        userData={previewUserData}
      />
    </Box>
  );
};

export default UserDashboard;
